import { unreachable } from '../shared/exhaustive';
import {
  formatCoordPair as formatCoordPairIn,
  formatDimension,
} from './dimFormat';
import {
  textBoxCentre,
  textBoxLocal,
  textBoxToWorld,
} from './textBox';
import {
  drawGrip,
  drawGrips,
  GRIP_SELECTED,
} from './grip';
import type {
  Arc,
  CeilingPlanView,
  Connection,
  ConnectionType,
  DimensionLine,
  DimKind,
  DimStyle,
  Discipline,
  Drawing,
  Flooring,
  FloorKind,
  GeomEntity,
  Geometry,
  GlassSegment,
  InnerWall,
  Lang,
  Leader,
  Polygon,
  ProductInstance,
  RevisionCloud,
  SectionLine,
  ShapeRelation,
  TextComment,
  Vec2,
  Wall,
  WallFeature,
} from '../shared/types';
import { tr } from './i18n';
import { wallThicknessOf } from './dent';
import { curveTessellation } from './curves';
import {
  segCross,
  strokeWithHumps,
} from './lineHops';
import { benchSlatSpans } from './benches';
import {
  polylineIsClosed,
  wallBand,
  wallSegPin,
  wallSegPins,
} from './wallTool';
import {
  wallDoorSwing,
  wallFeatureQuad,
  type WallFeatureSpan,
  wallFeatureSpans,
  wallPaneQuad,
  wallPocketQuad,
  wallPockets,
  wallSegFrame,
  wallSolidRuns,
} from './wallFeatures';
import {
  type InWallPart,
  inWallParts,
} from './wallCuts';
import {
  partRisers,
  type Riser,
  RISER_COLORS,
  riserRing,
} from './risers';
import {
  doorParts,
  roomDoors,
} from './door';
import {
  doorGlassSpans,
  GLASS_STROKE_WIDTH,
  glassFill,
  glassPolysLocal,
  glassStroke,
  openingBandLocal,
} from './glass';
import { drawGlazingsPlan } from './glazingPlan';
import { glazingOfPane } from './roomGlazing';
import { darkenHex } from './hexTone';
import {
  arcPoints,
  BAND_MITRE,
  cloudScallops,
  dimensionGeometry,
  distance,
  ellipseFromRing,
  gridStepMm,
  HEATER_BODY_COLOR,
  instanceToWorld,
  offsetPolyline,
  ribbonJoint,
  rotatePoint,
} from './geometry';
import { wallBoundaryEdges } from './wall';
import {
  cornerRadiusLeaderLocal,
  heaterWallBands,
  innerWallDimSplits,
  innerWallGaps,
  isPolyRoom,
  openingHoleBand,
  partitionBenchJointHighlight,
  polyBenchJointHighlight,
  polyEdges,
  polyInnerCarved,
  polyInnerWallBand,
  polyInnerWallFill,
  polyWall,
  polyWallBand,
  resolvedOutline,
  type RoomCorner,
  roomCorners,
  roomWallCuts,
  segToWall,
  wallChain,
  wallLengthPin,
  wallView,
} from './polyRoom';
import {
  connDiscipline,
  connectionShown,
  type ConnGeom,
  connGeom,
  pipeTubes,
} from './connections';
import type { PipePlan } from './pipes';
import {
  FALL_ARROW_PX,
  fallArrows,
  floorIsSloping,
} from './flooring';
import {
  drawFloorSwatch,
  FLOOR_HATCH_MM,
  FLOOR_STYLE,
  hatchConcrete,
  hatchGrout,
} from './floorHatch';
import {
  connectionRows,
  connectionSchedule,
  flooringRows,
  lightingRows,
  pipeRowCells,
  pipeRows,
  preparationRows,
  preparationSchedule,
  type ScheduleGroup,
  scheduleNumbers,
  scheduleStrings,
} from './schedule';
import { Viewport } from './viewport';
import { type RingPoly } from './polybool';
import {
  drawSection,
  type SecSrc,
  sectionLabel,
} from './section';
import {
  drawCeilingPlan,
  rcpDrawnHeight,
} from './rcp';
import { numberedNames } from './naming';
import { productName } from '../shared/names';
import { hotTubLayout } from './hotTub';
import { STEAM_CUBE } from './builtModels';
import {
  objectHeight,
  partHeight,
} from './heights';
import {
  apply as applyMat3,
  type Body,
  bodyOf,
  bodyPoint,
  isTilted,
  planFaces,
  planOutline,
} from './tilt';
import {
  type ModelOutline,
  modelPlan,
  uprightModelPlan,
} from './partModel';
import {
  type LocalBox,
  type PartAttachment,
  partLook,
  TAKES_DRAWING,
} from './partLook';
import {
  BENCH_END_LINE,
  type BenchSeat,
  closedEndLines,
  LIP_SHADE,
  nodeBenchJoints,
  roomBenchSeats,
  saunaBenchPlan,
  type SaunaBenchSegment,
  saunaBenchSegment,
  structureColor,
  TRESTLE_LINE_SHADE,
  type TrestleMark,
} from './benchSolids';
import {
  backrestColor,
  backrestPlan,
  backrestsOf,
} from './backrest';
import {
  activeCageOf,
  cagePlan,
  framePoint,
  sculptColor,
  sculptedBenchesOf,
  sculptLayout,
} from './sculptBench';
import {
  sculptTrestleFootprint,
  sculptTrestles,
} from './sculptTrestles';
import {
  CAGE_COLOR,
  cageEdges,
} from './lattice';
import {
  channelFigures,
  channelsOf,
  coveGeometry,
  coveOf,
  drawnStripPts,
  FIBRE_DIAMETER,
  fibreKitsOf,
  kitArcs,
  kitFibres,
  LIGHT_AMBER,
  lightKeysInOrder,
  type LightSymbol,
  lightTagAnchors,
  lightTags,
  pathSides,
  projectorMiddle,
  skiesOf,
  SKY_FIBRE_DIAMETER,
  skyFibres,
  skyZone,
  SPOT_DIAMETER,
  type SpotGuideKind,
  spotsOf,
  STRIP_APART_PX,
  stripRuns,
  tagTextAt,
  updatedTagOrder,
} from './lights';
import {
  type CeilingMarks,
  ceilingMarksProjected,
  ceilingMarksShown,
  isFlatCeiling,
} from './ceiling';
import {
  type LayerEnv,
  layerModel,
  type PaintItem,
} from './layers';
import { type TitleBox } from './viewTitle';
import {
  lightTagBox,
  paintLightSymbol,
  paintLightTag,
  type TagBox,
} from './tagPaint';
import { instanceHalfExtents } from './hitRules';
import {
  grilleColor,
  grilleLayout,
} from './floorGrille';
import {
  doorIcons,
  roomDoor,
} from './roomDoor';
import { strokeSwing } from './doorSwing';

/** Live preview of the repeatable-product tool: the baseline polyline (committed
 *  vertices plus the cursor) and the centre/rotation of each tiled copy. */
export interface RepeatPreview {
  pts: Vec2[];
  copies: { x: number; y: number; rotation: number }[];
  width: number;
  depth: number;
  color: string;
  geometry: Geometry | null;
}

export interface RenderOptions {
  showGrid: boolean;
  /** The drawing's paint order, layer by layer (renderer/layers.ts `layerModel`
   *  `.paint`): hidden layers are already left out. Absent = the default layers
   *  with no room nesting (print paths that have no layer model at hand). */
  paint?: PaintItem[];
  /** Further layers to leave out (a sheet's own hidden layers). */
  hiddenLayers?: ReadonlySet<string>;
  /** The Imported layer is hidden: no underlays, no DXF plan. */
  hideImported?: boolean;
  selectedId: string | null;
  /** All selected instance ids (multi-select); each is drawn highlighted. */
  selectedIds?: string[];
  selectedDimId?: string | null;
  /** The static backdrop canvas (page fill, imported underlays, grid). Set ONLY
   *  by the interactive model canvas: its presence enables adaptive dimension
   *  ink, which lifts the ink's luminance over the stretches that sit on a dark
   *  backdrop. Absent for sheet viewports and print, so those render the stored
   *  dimension colour exactly. */
  inkBackdrop?: HTMLCanvasElement | null;
  /** When the selected instance is resize-locked: draw no resize handles (the
   *  rotation knob still shows). */
  selectionResizeLocked?: boolean;
  /** Id of the selected bench (highlighted in its room). */
  selectedBenchId?: string | null;
  /** The selected bench support (its room or standalone bench, and its id). */
  selectedSupport?: { instId: string; id: string } | null;
  /** World-space handle points of the selected bench (drawn as square handles). */
  benchHandles?: Vec2[] | null;
  /** Bright-green highlight of the clickable add-bench area (world polygons). */
  /** Bench-tool hover preview: green when a click would add a bench, red when it
   *  would delete one, blue for the wall a ceiling's direction pick would take. */
  benchAddHover?: { polys: Vec2[][]; kind: 'add' | 'delete' | 'pick' } | null;
  /** Heater guard-cover hover: the clearance gap's outer edge a click would build
   *  a cover on (blue segment), or the cover side a click would clear (red band). */
  heaterCoverHover?: { pts: Vec2[]; kind: 'add' | 'remove' } | null;
  /** The wall band a corner drag is being refused by — its figures don't fit any
   *  other length. */
  blockedWall?: Vec2[] | null;
  /** Live preview of a dimension being drawn. */
  pendingDim?: { a: Vec2; b: Vec2; offset: number; kind?: DimKind } | null;
  selectedSectionId?: string | null;
  /** The plan's live view only: what every section view boxes for the selection
   *  (`sectionTargets`), and the section a click has drilled into. */
  sectionMarks?: { selected: SecSrc[]; drilledId: string | null } | null;
  /** Live preview of a section line being drawn. */
  pendingSection?: SectionLine | null;
  selectedRcpId?: string | null;
  /** The reflected ceiling plan tool's view following the cursor. */
  pendingRcp?: CeilingPlanView | null;
  /** The line a reflected ceiling plan placed or dragged is held in projection
   *  on (from its room's middle to its own), while it is. */
  rcpGuide?: [Vec2, Vec2] | null;
  /** The plan's view titles (a section's "A–A", a ceiling plan's) as hit
   *  targets (`RenderHits.viewTitles`), and the one selected. Sheets and prints
   *  pass none. */
  viewTitles?: { selected: { kind: 'section' | 'rcp'; id: string } | null };
  /** The drawing's revision (changeTracker.ts), when the caller keeps one: what
   *  render() derives from the drawing alone (`DrawingDerived`) is then computed
   *  once per revision, not per call. Leave it out for a drawing the caller does
   *  not track (a print's copy). */
  revision?: number;
  /** H/V/45 alignment guides for the section line's moving endpoint. */
  pendingSectionGuide?: { hover: Vec2; verts: Vec2[] } | null;
  /** Purple guides from a live draft vertex to the same-kind vertices it is
   *  orthogonally aligned with — other section lines while drawing a section, other
   *  dimension endpoints while drawing a dimension. */
  alignGuide?: { hover: Vec2; verts: Vec2[] } | null;
  /** User-drawn text-box comments. */
  texts?: TextComment[];
  selectedTextId?: string | null;
  /** All selected text/arc/leader/wall ids (multi-select highlight). */
  selectedTextIds?: string[];
  selectedArcIds?: string[];
  selectedLeaderIds?: string[];
  selectedWallIds?: string[];
  /** Combined world bounding box of a 2+ multi-selection, drawn as a dashed frame. */
  multiSelectionBounds?: { min: Vec2; max: Vec2 } | null;
  /** Comment currently being edited inline (its text is drawn by the textarea). */
  editingTextId?: string | null;
  /** Live preview of a text box being placed (two corners + optional leader). */
  pendingTextBox?: { x: number; y: number; w: number; h: number; leader?: Vec2 } | null;
  /** World point currently being snapped to (drawn as a marker). */
  snapIndicator?: Vec2 | null;
  /** Which object-snap kind the indicator is (chooses the marker glyph). */
  /** Which object-snap kind the indicator is (chooses the marker glyph). */
  snapIndicatorKind?: 'endpoint' | 'midpoint' | 'center' | 'intersection' | 'quadrant' | 'perpendicular' | 'nearest' | 'node';
  /** Vertex under the cursor a move drag would grab (drawn as an endpoint marker). */
  vertexGrabHover?: Vec2 | null;
  /** Dashed preview polyline for the offset tool (the copy about to be created). */
  modifyPreview?: Vec2[] | null;
  /** The currently-selected free-standing arc (drawn highlighted). */
  selectedArcId?: string | null;
  /** Live arc-tool preview: placed-point dots + the fitted arc (if resolvable). */
  pendingArc?: { dots: Vec2[]; fit: { center: Vec2; radius: number; startDeg: number; endDeg: number } | null } | null;
  /** The currently-selected leader (drawn highlighted). */
  selectedLeaderId?: string | null;
  /** Live leader-tool preview: placed vertices + current hover. */
  pendingLeader?: { pts: Vec2[]; hover: Vec2 | null } | null;
  /** Orthogonal distance guides shown while dragging an object near walls/objects. */
  dragGuides?: Array<{ a: Vec2; b: Vec2; dist: number }>;
  /** Thin red centroid-alignment guides (dragged object centroid ↔ another's). */
  centroidGuides?: Array<[Vec2, Vec2]>;
  /** Purple dashed text-box alignment guides (dragged box edge ↔ another box). */
  alignGuides?: Array<[Vec2, Vec2]>;
  /** Purple guides from a moved underlay's (image/PDF/DXF) aligned corner to the
   *  object vertex it lined up on, while dragging it. */
  moveAlignGuides?: Array<{ from: Vec2; to: Vec2 }>;
  /** Flooring areas, each with its resolved drain instance and selection state. */
  floorings?: Array<{ floor: Flooring; selected: boolean; drainInst: ProductInstance | null; drainSel: boolean }>;
  /** Live preview of the flooring polygon being drawn. */
  pendingFloor?: { kind: FloorKind; pts: Vec2[]; hover: Vec2 | null; snapVerts?: Vec2[] } | null;
  /** Temporary perpendicular dimensions from a dragging drain to each floor edge. */
  floorDrainDims?: Array<{ from: Vec2; to: Vec2; len: number }> | null;
  /** A spot's temporary figures (world) — about to be put, dragged or selected:
   *  from its centre to the walls and to the spots it lines up with (`both`
   *  arrowed), `lane` 1 a lane beside its line; and the guides (dashed) from the
   *  spots it is held in line with. */
  spotAid?: SpotAidDraw | null;
  /** The worked grip's figure (world): a sky's margin, from the wall to the zone. */
  gripFigure?: { a: Vec2; b: Vec2; len: number } | null;
  /** A bench support's temporary figures (world) — slid, selected or about to
   *  be placed: from its trestle to the nearest trestle, or the bench's end, on
   *  each side. Their labels are recorded for `supportFigureAt`. */
  supportSpacings?: Array<{ a: Vec2; b: Vec2; length: number }> | null;
  /** The room or bench whose own temporary dimensions yield to its supports'
   *  figures, which share their lanes. */
  quietDimsOf?: string | null;
  /** Categories hidden because their schedule-discipline toggle is off. */
  hiddenCategories?: Set<string>;
  /** Live preview of a glass segment being dragged. */
  pendingGlass?: { instId: string; seg: GlassSegment } | null;
  pendingOpening?: { instId: string; seg: GlassSegment } | null;
  pendingInnerWall?: { instId: string; iw: InnerWall } | null;
  /** Raster underlays (imported PDFs/images), drawn behind everything, in order.
   *  `sharp` is an optional crisp re-rasterisation of the visible page slice
   *  (PDFs, when zoomed in), drawn over the base image at its own world rect. */
  backgroundImages?: Array<{ img: CanvasImageSource; x: number; y: number; widthMm: number; heightMm: number; rotation?: number; opacity?: number; sharp?: { canvas: CanvasImageSource; x: number; y: number; widthMm: number; heightMm: number } }>;
  /** Loaded raster drawings for image-based product instances, keyed by data URL. */
  instanceImages?: Map<string, CanvasImageSource>;
  /** World outline to highlight with a light-blue hatch (the object whose row is
   *  hovered in the Parts tab — a placed part, an underlay, or the imported DXF). */
  hoverHighlightRing?: Vec2[] | null;
  /** What a Select click at the cursor would pick, outlined before the click
   *  (PowerPoint's hover): a closed outline, or the path of a line-like object. */
  hoverOutline?: { ring: Vec2[]; closed: boolean } | null;
  /** The selection's grips (world): a selected leader's / line's vertices, an
   *  arc's ends and middle — main.ts `selectionGrips`, which the press reads too. */
  grips?: Array<{ at: Vec2; fill?: string }>;
  /** The underlay currently being transformed; drawn with move/resize/rotate handles. */
  editBox?: PdfBox | null;
  /** World bounds of the imported DXF while it is selected for moving; drawn as a
   *  highlighted rectangle. */
  editBgBounds?: { min: Vec2; max: Vec2 } | null;
  /** User-drawn polygons. */
  polygons?: Polygon[];
  selectedPolyId?: string | null;
  /** All selected polygon ids (multi-select). */
  selectedPolyIds?: string[];
  /** A single selected polygon edge (segment from vertex `edge` to `edge`+1). */
  selectedPolyEdge?: { id: string; edge: number } | null;
  /** Live preview of the polygon being drawn. `snapVerts` are extra nearby-object
   *  vertices the cursor may be aligned to (for drawing the alignment guides). */
  pendingPolygon?: { pts: Vec2[]; hover: Vec2 | null; snapVerts?: Vec2[] } | null;
  /** All selected revision-cloud ids (multi-select). */
  selectedRevCloudIds?: string[];
  /** Live preview of the revision cloud being drawn. */
  pendingRevisionCloud?: { pts: Vec2[]; hover: Vec2 | null } | null;
  /** User-drawn walls. */
  walls?: Wall[];
  selectedWallId?: string | null;
  /** Id of the polygon/wall whose vertices are being edited (shows vertex handles). */
  editVerticesId?: string | null;
  /** Nearby-object snap vertices at the current hover, shared by the draw tools
   *  so the wall / line pending previews can draw the same alignment guides. */
  snapVerts?: Vec2[];
  /** Alignment guide while dragging an existing vertex (its snapped position and
   *  the vertices it lines up with) — same blue guides as the draw tools. */
  editGuide?: { at: Vec2; verts: Vec2[] } | null;
  /** Live preview of the wall being drawn. */
  pendingWall?: { pts: Vec2[]; hover: Vec2 | null } | null;
  /** Live preview of the line (polyline) being drawn. */
  pendingLine?: { pts: Vec2[]; hover: Vec2 | null } | null;
  /** Live preview of the repeatable-product tool (baseline + tiled copies). */
  pendingRepeat?: RepeatPreview | null;
  /** Live preview of the PDF-scale tool's two picked points. */
  pendingScale?: { pts: Vec2[]; hover: Vec2 | null } | null;
  pendingMeasure?: { pts: Vec2[]; hover: Vec2 | null } | null;
  /** The Constrain tool: the piece under the cursor and the first pick (one
   *  point = a vertex, two = an edge), world mm. */
  constraintPick?: { hover: Vec2[] | null; first: Vec2[] | null } | null;
  /** Constraint badges, laid out in screen px (`layoutBadges`); `on` = selected
   *  or hovered, `broken` = the drawing no longer satisfies it. */
  constraintBadges?: Array<{ x: number; y: number; w: number; h: number; text: string; anchor: Vec2; broken: boolean; on: boolean; line?: [Vec2, Vec2] }>;
  /** The selected / hovered constraint's pieces (world mm), lit up. */
  constraintHighlight?: Vec2[][];
  /** Mirror tool: the first point of the mirror line and the cursor. */
  pendingMirror?: { a: Vec2; b: Vec2 | null } | null;
  /** Move / Copy / Rotate: base point → cursor. */
  pendingRubber?: { a: Vec2; b: Vec2 | null } | null;
  arrayCenter?: Vec2 | null;
  /** Draw the connection lines between parts and the preparation tags (each
   *  filtered by `disciplineFilters`). */
  showSchedule?: boolean;
  /** Draw the in-drawing schedule tables. Separate from `showSchedule` so the
   *  connection lines can appear in sheet viewports/prints without the tables. */
  showScheduleTables?: boolean;
  /** Which in-drawing schedule table is selected (draws handles). */
  selectedScheduleTable?: SchedTableKind | null;
  /** Show coloured hatch fills on products (default true). */
  showHatches?: boolean;
  /** Show product name labels (default true). */
  showLabels?: boolean;
  /** Show the lights' beams in sections (default true). */
  showBeams?: boolean;
  /** The rooms' light levels (`lightLevels`, room local) painted over their floors,
   *  each room by its transform, with the scale's key under `title`; null: off. */
  lux?: { title: string; maps: Array<{ x: number; y: number; rotation: number; cell: number; cells: ReadonlyArray<{ x: number; y: number; lux: number }> }> } | null;
  /** Show every room's ceiling lines and arrows (absent = hidden). */
  showCeilings?: boolean;
  /** Every selected room with a ceiling: each shows its marks and crown figure. */
  ceilingSelectedIds?: ReadonlySet<string>;
  /** The single selected room, whose arrows drag and whose grips show. */
  ceilingRoomId?: string | null;
  /** Language for product name labels (default 'it'). */
  lang?: Lang;
  /** Live preview of a connection being dragged from one part to the cursor. */
  /** The connection being drawn: start, the bends clicked so far and the cursor,
   *  in the discipline it will be born with. */
  pendingConnection?: { pts: Vec2[]; type: ConnectionType } | null;
  /** The connection points of the object under the cursor (Connect tool). */
  connectionPorts?: Vec2[] | null;
  /** Light (print) theme: white page, dark ink. */
  lightTheme?: boolean;
  /** Extra device-pixel multiplier on top of the canvas dpr. Used when printing
   *  a sheet to a high-dpi canvas so the fixed-pixel line weights scale up with
   *  the resolution instead of looking hair-thin. Default 1. */
  deviceScale?: number;
  /** When true, this is the interactive front layer: keep it transparent and
   *  skip the backdrop (drawn on a separate background canvas). */
  layered?: boolean;
  /** In a `layered` pass, still draw the imported underlays (raster/PDF + DXF)
   *  after clearing, before the model — but no page fill or grid. Used by sheet
   *  viewports so the imported background shows inside the model window. */
  underlay?: boolean;
  /** Per-discipline visibility (connections/preparations drawing + schedule, and
   *  the flooring finishes). A discipline set to `false` is hidden. */
  disciplineFilters?: Partial<Record<Discipline, boolean>>;
  /** Rubber-band rectangle (world coords) while choosing a print window. */
  pendingRect?: { a: Vec2; b: Vec2 } | null;
  /** Rubber-band selection box: window (entirely-inside) vs crossing (touching). */
  marquee?: { a: Vec2; b: Vec2; window: boolean } | null;
  /** Live preview of the circle/ellipse being drawn. */
  pendingEllipse?: { cx: number; cy: number; rx: number; ry: number; center: Vec2; hover: Vec2 } | null;
  /** The part a dragged part would be related to on release (green outline). */
  dropTargetId?: string | null;
  /** Its label beside it ("Relate to Heater 2"), already translated. */
  dropTargetLabel?: string;
  /** Parts related to the selection, outlined dashed. */
  relatedIds?: string[];
  /** Live preview of a heatedbench / saltwall / saunabench polyline being drawn. */
  pendingBenchPoly?: { pts: Vec2[]; hover: Vec2 | null; depth: number; category: string; valid?: boolean; slats?: number[]; gaps?: number[]; color?: string } | null;
  /** Instance currently open in the drawing editor (other instances are dimmed). */
  drawingEditId?: string | null;
  /** Figures the drawing editor's session draws (a steam profile's depth and heights). */
  drawingEditDims?: Array<{ a: Vec2; b: Vec2 }> | null;
  /** Which local shape's vertex handles are shown in the drawing editor. */
  drawEditVerticesId?: string | null;
  /** Selected local polygon id (inside the drawing editor). */
  drawEditSelectedPolyId?: string | null;
  /** Selected local wall id (inside the drawing editor). */
  drawEditSelectedWallId?: string | null;
  /** Instance whose polygon vertices are shown as draggable handles. */
  polyRoomVertexEditId?: string | null;
  /** Selected wall segment of the poly-room being vertex-edited (highlighted blue). */
  polyRoomSelectedEdge?: number | null;
  /** Selected corner of the poly-room being vertex-edited (its handle drawn blue). */
  polyRoomSelectedVertex?: number | null;
  /** Id of the currently-selected door (shows its jamb/flip handles). */
  selectedDoorId?: string | null;
  /** The selected connection, drawn highlighted. */
  selectedConnId?: string | null;
  /** Glazed pane(s) drawn with a selection outline. */
  selectedGlassId?: string | null;
  selectedGlassIds?: string[];
  /** Opening(s) drawn with a selection outline. */
  selectedOpeningIds?: string[];
  /** The door / pane / opening of a drawn wall that is selected (`Wall.features`). */
  selectedWallFeatureId?: string | null;
  /** A wall feature being dragged out by the glass / opening tool. */
  wallFeaturePreview?: { wallId: string; feature: WallFeature } | null;
  /** Hidden heater wall drawn with a selection outline. */
  selectedHeaterWallId?: string | null;
  /** Interior partition wall drawn with a selection outline. */
  selectedInnerWallId?: string | null;
  /** The selected backrest or light of a room, drawn with a selection outline. */
  selectedFixtureId?: string | null;
  /** Several lights of the selected room picked together: each drawn selected. */
  selectedFixtureIds?: string[];
  /** How dimension values are formatted (auto / mm / cm / m / usa). */
  dimStyle?: DimStyle;
  /** A polygon/poly-room segment to highlight (hovering a constraint row). */
  hoverConstraintSeg?: { shapeId: string; edge: number } | null;
  /** The bench join under the cursor (pulsing green): a room corner (its vertex),
   *  where a partition's face meets its host wall's benches, or a standalone
   *  sauna bench's corner node; `tier` the join's tier there. */
  benchJoint?: { instId: string; corner: number | { iw: number; face: 'a' | 'b' } | { node: number }; tier: number } | null;
}

/** A resize handle on a selected instance, identified by its local direction. */
export interface Handle {
  sx: -1 | 0 | 1;
  sy: -1 | 0 | 1;
  world: Vec2;
  /** For polyline-bench instances: index into inst.nodes[]. */
  nodeIndex?: number;
}

/** The eight resize handles (corners + edge midpoints) of an instance. */
export function instanceHandles(inst: ProductInstance): Handle[] {
  if (inst.nodes && inst.nodes.length >= 2) {
    return inst.nodes.map((n, i) => ({ sx: 0, sy: 0, world: { x: n.x, y: n.y }, nodeIndex: i }));
  }
  const { hw, hd } = instanceHalfExtents(inst);
  const dirs: Array<[-1 | 0 | 1, -1 | 0 | 1]> = [
    [-1, -1], [1, -1], [1, 1], [-1, 1], // corners
    [0, -1], [1, 0], [0, 1], [-1, 0], // edges
  ];
  return dirs.map(([sx, sy]) => ({
    sx,
    sy,
    world: instanceToWorld(inst, { x: sx * hw, y: sy * hd }),
  }));
}

const ROTATE_GAP_PX = 26; // gap from the back edge to the rotation knob
const ROTATE_RADIUS_PX = 6;

/** Screen position of the rotation knob, a fixed gap off the instance's back edge. */
export function rotationHandleScreen(vp: Viewport, inst: ProductInstance): Vec2 {
  const center = vp.worldToScreen(instanceToWorld(inst, { x: 0, y: 0 }));
  const edge = vp.worldToScreen(instanceToWorld(inst, { x: 0, y: instanceHalfExtents(inst).hd }));
  const dx = edge.x - center.x;
  const dy = edge.y - center.y;
  const len = Math.hypot(dx, dy) || 1;
  return { x: edge.x + (dx / len) * ROTATE_GAP_PX, y: edge.y + (dy / len) * ROTATE_GAP_PX };
}

/**
 * Rotation knob for a free shape (polygon/wall) given its outline world points:
 * an anchor at the top-centre of the bounding box and the knob a fixed gap above
 * it (in screen space). Used both to draw the knob and to hit-test it.
 */
export function shapeRotationKnob(vp: Viewport, points: Vec2[]): { anchor: Vec2; knob: Vec2 } {
  let minx = Infinity;
  let maxx = -Infinity;
  let maxy = -Infinity;
  for (const p of points) {
    if (p.x < minx) minx = p.x;
    if (p.x > maxx) maxx = p.x;
    if (p.y > maxy) maxy = p.y;
  }
  const anchor = vp.worldToScreen({ x: (minx + maxx) / 2, y: maxy });
  return { anchor, knob: { x: anchor.x, y: anchor.y - ROTATE_GAP_PX } };
}

/** A text box's rotate knob: off the middle of its TOP edge, square to it, so it
 *  turns with the box. Used both to draw the knob and to hit-test it. */
export function textRotationKnob(vp: Viewport, t: TextComment): { anchor: Vec2; knob: Vec2 } {
  const anchor = vp.worldToScreen(textBoxToWorld(t, t.w / 2, 0));
  const c = vp.worldToScreen(textBoxCentre(t));
  const dx = anchor.x - c.x, dy = anchor.y - c.y, l = Math.hypot(dx, dy) || 1;
  return { anchor, knob: { x: anchor.x + (dx / l) * ROTATE_GAP_PX, y: anchor.y + (dy / l) * ROTATE_GAP_PX } };
}

/** The eight resize handles (corners + edge midpoints) of a shape's bounding box. */
export function shapeBBoxHandles(points: Vec2[]): Handle[] {
  let minx = Infinity, maxx = -Infinity, miny = Infinity, maxy = -Infinity;
  for (const p of points) {
    if (p.x < minx) minx = p.x;
    if (p.x > maxx) maxx = p.x;
    if (p.y < miny) miny = p.y;
    if (p.y > maxy) maxy = p.y;
  }
  const cx = (minx + maxx) / 2, cy = (miny + maxy) / 2;
  const xAt = (s: number): number => (s < 0 ? minx : s > 0 ? maxx : cx);
  const yAt = (s: number): number => (s < 0 ? miny : s > 0 ? maxy : cy);
  const dirs: Array<[-1 | 0 | 1, -1 | 0 | 1]> = [
    [-1, -1], [1, -1], [1, 1], [-1, 1], [0, -1], [1, 0], [0, 1], [-1, 0],
  ];
  return dirs.map(([sx, sy]) => ({ sx, sy, world: { x: xAt(sx), y: yAt(sy) } }));
}

/** Light-blue diagonal hatch over an object's footprint, shown while its row is
 *  hovered in the Parts tab. `ring` is the outline in world coordinates. */
function drawHoverHatch(ctx: CanvasRenderingContext2D, vp: Viewport, ring: Vec2[]): void {
  if (ring.length < 3) return;
  const pts = ring.map((c) => vp.worldToScreen(c));
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const p of pts) { minX = Math.min(minX, p.x); minY = Math.min(minY, p.y); maxX = Math.max(maxX, p.x); maxY = Math.max(maxY, p.y); }
  ctx.save();
  ctx.beginPath();
  pts.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
  ctx.closePath();
  ctx.clip();
  ctx.fillStyle = 'rgba(90, 180, 255, 0.14)';
  ctx.fill();
  ctx.strokeStyle = 'rgba(90, 180, 255, 0.6)';
  ctx.lineWidth = 1;
  const step = 9;
  ctx.beginPath();
  for (let d = minX - (maxY - minY); d <= maxX; d += step) {
    ctx.moveTo(d, minY);
    ctx.lineTo(d + (maxY - minY), maxY);
  }
  ctx.stroke();
  ctx.strokeStyle = 'rgba(90, 180, 255, 0.85)';
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  pts.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
  ctx.closePath();
  ctx.stroke();
  ctx.restore();
}

/** The hover outline: the same light blue as the Parts-tab hatch, outline only —
 *  it follows the cursor everywhere, so it must not paint over the plan. A line-
 *  like object gets a wide translucent stroke along its own path instead. */
export const HOVER_OUTLINE_COLOR = 'rgba(90, 180, 255, 0.95)';
/** Takes a projector, not a Viewport, so a sheet (paper, Y-down) draws the same
 *  outline as the model. */
export function drawHoverOutline(ctx: CanvasRenderingContext2D, project: (p: Vec2) => Vec2, ring: Vec2[], closed: boolean): void {
  const pts = ring.map(project);
  ctx.save();
  ctx.beginPath();
  pts.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
  if (closed) ctx.closePath();
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  if (!closed) {
    ctx.strokeStyle = 'rgba(90, 180, 255, 0.35)';
    ctx.lineWidth = 8;
    ctx.stroke();
  }
  ctx.strokeStyle = HOVER_OUTLINE_COLOR;
  ctx.lineWidth = 2;
  ctx.stroke();
  ctx.restore();
}

/** A rubber-band marquee between two SCREEN points: blue solid for a window,
 *  green dashed for a crossing — model and sheets alike. */
export function drawMarquee(ctx: CanvasRenderingContext2D, a: Vec2, b: Vec2, win: boolean): void {
  ctx.save();
  ctx.setLineDash(win ? [] : [6, 4]);
  ctx.strokeStyle = win ? '#4aa3ff' : '#3ddc84';
  ctx.fillStyle = win ? 'rgba(74, 163, 255, 0.10)' : 'rgba(61, 220, 132, 0.10)';
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.rect(Math.min(a.x, b.x), Math.min(a.y, b.y), Math.abs(b.x - a.x), Math.abs(b.y - a.y));
  ctx.fill();
  ctx.stroke();
  ctx.restore();
}

/** The dashed bounding box and eight square resize handles of a selected shape.
 *  `accent` colours the handles/box (green when the shape's face dimensions are
 *  double-click editable, else the default yellow). */
function drawShapeResizeHandles(ctx: CanvasRenderingContext2D, vp: Viewport, points: Vec2[], accent = '#ffd166'): void {
  if (points.length < 2) return;
  const handles = shapeBBoxHandles(points);
  const corners = handles.slice(0, 4).map((h) => vp.worldToScreen(h.world));
  ctx.save();
  ctx.setLineDash([5, 4]);
  ctx.strokeStyle = hexToRgba(accent, 0.55);
  ctx.lineWidth = 1;
  ctx.beginPath();
  corners.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
  ctx.closePath();
  ctx.stroke();
  ctx.restore();
  drawGrips(ctx, handles.map((h) => vp.worldToScreen(h.world)), accent);
}

function drawShapeRotateKnob(ctx: CanvasRenderingContext2D, vp: Viewport, points: Vec2[], accent = '#ffd166'): void {
  if (points.length < 2) return;
  const { anchor, knob } = shapeRotationKnob(vp, points);
  drawKnob(ctx, anchor, knob, accent);
}

/** A rotate knob: a stem from `anchor` to the round knob (screen px). */
function drawKnob(ctx: CanvasRenderingContext2D, anchor: Vec2, knob: Vec2, accent = '#ffd166'): void {
  ctx.save();
  ctx.beginPath();
  ctx.moveTo(anchor.x, anchor.y);
  ctx.lineTo(knob.x, knob.y);
  ctx.strokeStyle = accent;
  ctx.lineWidth = 1.5;
  ctx.setLineDash([]);
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(knob.x, knob.y, ROTATE_RADIUS_PX, 0, Math.PI * 2);
  ctx.fillStyle = accent;
  ctx.strokeStyle = '#1e1f24';
  ctx.lineWidth = 1;
  ctx.fill();
  ctx.stroke();
  ctx.restore();
}



/** The four footprint corners of an instance, in world coordinates. */
export function instanceCorners(inst: ProductInstance): Vec2[] {
  const { hw, hd } = instanceHalfExtents(inst);
  return [
    instanceToWorld(inst, { x: -hw, y: -hd }),
    instanceToWorld(inst, { x: hw, y: -hd }),
    instanceToWorld(inst, { x: hw, y: hd }),
    instanceToWorld(inst, { x: -hw, y: hd }),
  ];
}

/** A rotatable raster box (the PDF underlay), for transform handles. */
export interface PdfBox {
  x: number;
  y: number;
  widthMm: number;
  heightMm: number;
  rotation?: number;
}

/** World corners of the (possibly rotated) PDF underlay. */
export function pdfBoxCorners(bi: PdfBox): Vec2[] {
  const hw = bi.widthMm / 2;
  const hd = bi.heightMm / 2;
  const r = bi.rotation || 0;
  return ([[-hw, -hd], [hw, -hd], [hw, hd], [-hw, hd]] as const).map(([lx, ly]) => {
    const p = rotatePoint({ x: lx, y: ly }, r);
    return { x: bi.x + p.x, y: bi.y + p.y };
  });
}

/** The eight resize handles (corners + edge midpoints) of the PDF underlay. */
export function pdfBoxHandles(bi: PdfBox): Handle[] {
  const hw = bi.widthMm / 2;
  const hd = bi.heightMm / 2;
  const r = bi.rotation || 0;
  const dirs: Array<[-1 | 0 | 1, -1 | 0 | 1]> = [
    [-1, -1], [1, -1], [1, 1], [-1, 1], [0, -1], [1, 0], [0, 1], [-1, 0],
  ];
  return dirs.map(([sx, sy]) => {
    const p = rotatePoint({ x: sx * hw, y: sy * hd }, r);
    return { sx, sy, world: { x: bi.x + p.x, y: bi.y + p.y } };
  });
}

/** Screen position of the PDF underlay's rotation knob (off its top edge). */
export function pdfRotationKnobScreen(vp: Viewport, bi: PdfBox): Vec2 {
  const r = bi.rotation || 0;
  const mid = rotatePoint({ x: 0, y: bi.heightMm / 2 }, r);
  const edge = vp.worldToScreen({ x: bi.x + mid.x, y: bi.y + mid.y });
  const center = vp.worldToScreen({ x: bi.x, y: bi.y });
  const dx = edge.x - center.x;
  const dy = edge.y - center.y;
  const len = Math.hypot(dx, dy) || 1;
  return { x: edge.x + (dx / len) * ROTATE_GAP_PX, y: edge.y + (dy / len) * ROTATE_GAP_PX };
}

function drawPdfTransform(ctx: CanvasRenderingContext2D, vp: Viewport, bi: PdfBox): void {
  const corners = pdfBoxCorners(bi).map((c) => vp.worldToScreen(c));
  ctx.save();
  ctx.beginPath();
  corners.forEach((c, i) => (i === 0 ? ctx.moveTo(c.x, c.y) : ctx.lineTo(c.x, c.y)));
  ctx.closePath();
  ctx.lineWidth = 2;
  ctx.strokeStyle = '#ffd166';
  ctx.stroke();
  // Rotation stem + knob off the top edge.
  const topMid = { x: (corners[0].x + corners[1].x) / 2, y: (corners[0].y + corners[1].y) / 2 };
  const knob = pdfRotationKnobScreen(vp, bi);
  ctx.beginPath();
  ctx.moveTo(topMid.x, topMid.y);
  ctx.lineTo(knob.x, knob.y);
  ctx.lineWidth = 1.5;
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(knob.x, knob.y, ROTATE_RADIUS_PX, 0, Math.PI * 2);
  ctx.fillStyle = '#ffd166';
  ctx.strokeStyle = '#1e1f24';
  ctx.lineWidth = 1;
  ctx.fill();
  ctx.stroke();
  // Resize handles.
  drawGrips(ctx, pdfBoxHandles(bi).map((h) => vp.worldToScreen(h.world)));
  ctx.restore();
}

// ---- One render() call: its pass ----
// What the draw helpers read while one render() call runs: its options as they
// want them, what it derives from the drawing, and the hit targets it draws. Set
// at the call's start and put back at its end (`withPass`), so a render inside or
// after another (a sheet's viewports, a thumbnail, a print) never sees, or
// overwrites, another's — the thumbnails' renders used to leave their boxes for
// the plan's next press.

/** A light tag painted on the plan: its box (screen px), tag, room and light. */
export type LightTagHit = TagBox & { tag: string; instId: string; id: string };
/** A view title painted on the plan: a section's "A–A", a ceiling plan's. */
export type ViewTitleHit = TitleBox & { kind: 'section' | 'rcp'; id: string };
/** A figure's label box (screen px, centred), with its index in its list. */
type FigureBox = { cx: number; cy: number; hw: number; hh: number; i: number };

/** The hit targets one render() call drew, in its canvas's CSS px — what a press
 *  on that canvas finds. render() returns them; the caller keeps its canvas's. */
export interface RenderHits {
  /** Every temporary dimension's figure text: the ONE record of where it landed,
   *  so the double-click that edits a figure needs a hit on its text, not on the
   *  line running the edge's length. */
  readonly dimLabels: DimLabelBox[];
  /** Every connection's schedule tag where it LANDED after `separateTags`, so a
   *  press grabs a tag where it is drawn. */
  readonly connTags: Array<{ id: string; x: number; y: number; w: number; h: number }>;
  /** The spot figures' labels, each with its index in `opts.spotAid.figures`:
   *  where a click retypes one. */
  readonly spotFigures: FigureBox[];
  /** The support figures' labels, each with its index in `opts.supportSpacings`:
   *  where a double-click retypes one. */
  readonly supportFigures: FigureBox[];
  /** The light tags, each with its room and the light that keeps its offset. */
  readonly lightTags: LightTagHit[];
  /** The view titles. */
  readonly viewTitles: ViewTitleHit[];
}

/** No hit targets: a canvas not drawn yet. */
export function noHits(): RenderHits {
  return { dimLabels: [], connTags: [], spotFigures: [], supportFigures: [], lightTags: [], viewTitles: [] };
}

/** What render() derives from the drawing alone, kept per drawing revision
 *  (`opts.revision`): a frame that changed nothing (a hover) and a sheet's every
 *  viewport derive it once. */
interface DrawingDerived {
  /** Auto-numbered display names per part (id → "Sauna01"). */
  numberedNames: Map<string, string>;
  /** The parts set into walls (wallCuts.ts): their cuts are drawn in the walls
   *  they cut. */
  wallCutParts: InWallPart[];
  /** The water risers of the parts set into rooms' walls (risers.ts): drawn in
   *  their room's wall. */
  risers: Riser[];
  /** The parts by id: a fibre kit's arcs run from its projector part. */
  partsById: ReadonlyMap<string, ProductInstance>;
  /** The light tags by tag key (L1, L2 …). */
  lightTags: ReadonlyMap<string, string>;
  /** The connection and preparation schedules, whole, and their numbers —
   *  numbered from the whole so a discipline filter doesn't renumber. */
  connSchedule: ScheduleGroup[];
  prepSchedule: ScheduleGroup[];
  connNums: Map<string, string>;
  prepNums: Map<string, string>;
}

const NO_DERIVED: DrawingDerived = {
  numberedNames: new Map(), wallCutParts: [], risers: [], partsById: new Map(), lightTags: new Map(),
  connSchedule: [], prepSchedule: [], connNums: new Map(), prepNums: new Map(),
};

interface RenderPass extends DrawingDerived {
  /** Theme: dark on screen, light for printing (white page, dark ink). */
  light: boolean;
  /** The selected bench (highlighted), and support. */
  selectedBench: string | null;
  selectedSupport: { instId: string; id: string } | null;
  /** The room or bench whose own temporary dimensions yield to its supports'
   *  figures (they share the lanes inside the room). */
  quietDimsOf: string | null;
  /** The bench corner join hovered (pulsing green swap highlight). */
  hoverBenchJoint: RenderOptions['benchJoint'];
  /** More than one instance selected: no per-instance handles. */
  multiSelect: boolean;
  /** The selected instance is resize-locked: no resize handles. */
  selectionResizeLocked: boolean;
  /** Coloured hatch fills on products, and product name labels. */
  showHatches: boolean;
  showLabels: boolean;
  /** The language product labels are shown in. */
  lang: Lang;
  /** The ceiling marks shown: every room's (Show ceilings), the selected rooms',
   *  and the single selected room's. */
  showCeilings: boolean;
  ceilingSelectedIds: ReadonlySet<string>;
  ceilingRoomId: string | null;
  /** The poly room in vertex-edit mode, and its selected edge and vertex. */
  polyRoomVertexEditId: string | null;
  polyRoomSelectedEdge: number | null;
  polyRoomSelectedVertex: number | null;
  selectedDoorId: string | null;
  /** The selected connection (its lines are drawn highlighted). */
  selectedConnId: string | null;
  selectedGlassIds: ReadonlySet<string>;
  selectedOpeningIds: ReadonlySet<string>;
  selectedWallFeatureId: string | null;
  wallFeaturePreview: { wallId: string; feature: WallFeature } | null;
  heaterWallSelectedId: string | null;
  innerWallSelectedId: string | null;
  fixtureSelectedIds: ReadonlySet<string>;
  /** The lights shown (the Lighting discipline toggle). */
  showLights: boolean;
  /** The drawing's revision, when the caller keeps one (`RenderOptions.revision`). */
  revision: number | undefined;
  dimStyle: DimStyle;
  /** The light tags' boxes drawn so far: a tag steps clear of them. */
  lightTagBoxes: TagBox[];
  /** Face-dimension draws deferred to the end of the call (`drawLineDimensions`). */
  deferredDims: Array<() => void>;
  hits: RenderHits;
}

/** The options as the draw helpers read them. */
function basePass(opts: RenderOptions): Omit<RenderPass, keyof DrawingDerived> {
  const selIds = opts.selectedIds && opts.selectedIds.length ? opts.selectedIds : (opts.selectedId ? [opts.selectedId] : []);
  return {
    light: !!opts.lightTheme,
    selectedBench: opts.selectedBenchId ?? null,
    selectedSupport: opts.selectedSupport ?? null,
    quietDimsOf: opts.quietDimsOf ?? null,
    hoverBenchJoint: opts.benchJoint ?? null,
    multiSelect: selIds.length > 1,
    selectionResizeLocked: !!opts.selectionResizeLocked,
    showHatches: opts.showHatches !== false,
    showLabels: opts.showLabels !== false,
    lang: opts.lang ?? 'it',
    showCeilings: opts.showCeilings === true,
    ceilingSelectedIds: opts.ceilingSelectedIds ?? new Set(),
    ceilingRoomId: opts.ceilingRoomId ?? null,
    polyRoomVertexEditId: opts.polyRoomVertexEditId ?? null,
    polyRoomSelectedEdge: opts.polyRoomSelectedEdge ?? null,
    polyRoomSelectedVertex: opts.polyRoomSelectedVertex ?? null,
    selectedDoorId: opts.selectedDoorId ?? null,
    selectedConnId: opts.selectedConnId ?? null,
    selectedGlassIds: new Set(opts.selectedGlassIds ?? (opts.selectedGlassId ? [opts.selectedGlassId] : [])),
    selectedOpeningIds: new Set(opts.selectedOpeningIds ?? []),
    selectedWallFeatureId: opts.selectedWallFeatureId ?? null,
    wallFeaturePreview: opts.wallFeaturePreview ?? null,
    heaterWallSelectedId: opts.selectedHeaterWallId ?? null,
    innerWallSelectedId: opts.selectedInnerWallId ?? null,
    fixtureSelectedIds: new Set(opts.selectedFixtureIds ?? (opts.selectedFixtureId ? [opts.selectedFixtureId] : [])),
    showLights: opts.disciplineFilters?.lighting !== false,
    revision: opts.revision,
    dimStyle: opts.dimStyle ?? 'mm',
    lightTagBoxes: [],
    deferredDims: [],
    hits: noHits(),
  };
}

let derivedKept: { drawing: Drawing; key: string; value: DrawingDerived } | null = null;

/** What the drawing derives, from the one kept for its revision and language
 *  when the caller passed a revision. */
function derivedOf(drawing: Drawing, opts: RenderOptions): DrawingDerived {
  const lang = opts.lang ?? 'it';
  const key = opts.revision == null ? null : `${opts.revision}|${lang}`;
  if (key !== null && derivedKept?.drawing === drawing && derivedKept.key === key) return derivedKept.value;
  const connSchedule = connectionSchedule(drawing), prepSchedule = preparationSchedule(drawing);
  const value: DrawingDerived = {
    numberedNames: numberedNames(drawing.instances, (i) => productName(i, lang)),
    wallCutParts: inWallParts(drawing.instances),
    risers: partRisers(drawing.instances),
    partsById: new Map(drawing.instances.map((i) => [i.id, i])),
    lightTags: lightTags(updatedTagOrder(drawing.lightTagOrder, lightKeysInOrder(drawing.instances))),
    connSchedule, prepSchedule, connNums: scheduleNumbers(connSchedule), prepNums: scheduleNumbers(prepSchedule),
  };
  if (key !== null) derivedKept = { drawing, key, value };
  return value;
}

/** The pass of the render() call running; between calls, a quiet one. */
let pass: RenderPass = { ...basePass({} as RenderOptions), ...NO_DERIVED };

/** Run `draw` with `p` as the pass, and put the one before back after it. */
function withPass<T>(p: RenderPass, draw: () => T): T {
  const outer = pass;
  pass = p;
  try { return draw(); } finally { pass = outer; }
}
/** A product instance's name in the active drawing language. */
function instName(inst: ProductInstance): string {
  return productName(inst, pass.lang);
}
/** The auto-numbered label a part shows on the drawing (falls back to its name). */
function instLabel(inst: ProductInstance): string {
  return pass.numberedNames.get(inst.id) ?? instName(inst);
}
/** Default colour for annotations: dimension lines and text-box outlines/arrows. */
export const ANNOTATION_COLOR = '#802723';
// Default fill colours for auto-bench rooms (used until the user overrides them).
const SAUNA_WALL_DEFAULT = '#e7b079';
const STEAM_WALL_DEFAULT = '#6b4e30';
const STEAM_BENCH_DEFAULT = '#987349';
// A sauna's default bench colour (its middle tier's).
const SAUNA_TIER_MID = '#f2d19f';

/** Default wall fill for an auto-bench room, by category. */
function wallDefault(inst: ProductInstance): string {
  return inst.category === 'steamroom' ? STEAM_WALL_DEFAULT : SAUNA_WALL_DEFAULT;
}
/** The default wall / bench colours shown in the properties panel (until overridden). */
export function defaultWallColor(inst: ProductInstance): string {
  return wallDefault(inst);
}
export function defaultBenchColor(inst: ProductInstance): string {
  return inst.category === 'steamroom' ? STEAM_BENCH_DEFAULT : SAUNA_TIER_MID;
}
/** A wall's fill colour, the category default until overridden. */
const wallColorOf = (inst: ProductInstance): string => inst.wallColor || wallDefault(inst);
/** A polygon room bench tier's fill: the room's colour override if set, else the
 *  per-level palette (matching the rectangular room's default tier colours). */
const PAGE_BG = (): string => (pass.light ? '#ffffff' : '#22242b');
/** The drawing editor's colour: its banner and the frame round the canvas. */
export const EDITOR_ACCENT = 'rgba(70,130,180,0.88)';
const EDITOR_FRAME = 6;
/** The page while the drawing editor is open: a lighter shade, tinted toward
 *  the accent, so the editor reads as a place of its own. */
const EDITOR_BG = (): string => (pass.light ? '#eaf1f8' : '#343a46');
const INK = (): string => (pass.light ? '#1b1d22' : '#e7ecf3');
const GRID_COLOR = (): string => (pass.light ? '#dfe3e9' : '#2c2f38');
const AXIS_COLOR = (): string => (pass.light ? '#c6ccd5' : '#3a3f4b');

// ---- Imported-geometry culling ----
interface EntBounds { minx: number; miny: number; maxx: number; maxy: number }

/** Axis-aligned bounds of one geometry entity (rough box for text). */
function entityBounds(e: GeomEntity): EntBounds {
  let minx = Infinity, miny = Infinity, maxx = -Infinity, maxy = -Infinity;
  const ext = (x: number, y: number): void => {
    if (x < minx) minx = x;
    if (x > maxx) maxx = x;
    if (y < miny) miny = y;
    if (y > maxy) maxy = y;
  };
  switch (e.kind) {
    case 'line': ext(e.a.x, e.a.y); ext(e.b.x, e.b.y); break;
    case 'polyline': for (const p of e.points) ext(p.x, p.y); break;
    case 'circle':
    case 'arc': ext(e.center.x - e.radius, e.center.y - e.radius); ext(e.center.x + e.radius, e.center.y + e.radius); break;
    case 'ellipse': {
      const ex = Math.hypot(e.major.x, e.major.y * e.ratio), ey = Math.hypot(e.major.y, e.major.x * e.ratio);
      ext(e.center.x - ex, e.center.y - ey); ext(e.center.x + ex, e.center.y + ey); break;
    }
    case 'spline': for (const p of e.control) ext(p.x, p.y); break;
    case 'text': {
      const w = e.value.length * e.height * 0.7;
      const x0 = e.pos.x - (e.align === 'center' ? w / 2 : e.align === 'right' ? w : 0);
      const y0 = e.pos.y - (e.baseline === 'middle' ? e.height / 2 : e.baseline === 'top' ? e.height : 0);
      ext(x0, y0); ext(x0 + w, y0 + e.height); break;
    }
    case 'hatch': for (const loop of e.loops) for (const p of loop) ext(p.x, p.y); break;
  }
  return { minx, miny, maxx, maxy };
}

// Per-entity bounds are cached against the background object identity so they're
// computed once per import, not every frame.
let bgBoundsSrc: Geometry | null = null;
let bgBoundsCache: EntBounds[] = [];
function backgroundBounds(bg: Geometry): EntBounds[] {
  if (bgBoundsSrc !== bg) {
    bgBoundsSrc = bg;
    bgBoundsCache = bg.entities.map(entityBounds);
  }
  return bgBoundsCache;
}

// Uniform-grid spatial index over the imported geometry. Collecting the visible
// entities by walking only the grid cells that overlap the viewport is
// sub-linear, so a small zoomed-in window over a huge drawing no longer pays an
// O(N) culling scan every frame. Built once per import (keyed on bg identity).
interface GridIndex {
  minx: number;
  miny: number;
  cellW: number;
  cellH: number;
  cols: number;
  rows: number;
  cells: number[][];
  /** Per-entity last-visited epoch, to dedup entities spanning several cells. */
  seen: Int32Array;
  epoch: number;
}
let bgGridSrc: Geometry | null = null;
let bgGrid: GridIndex | null = null;
function backgroundGrid(bg: Geometry, bounds: EntBounds[]): GridIndex {
  if (bgGridSrc === bg && bgGrid) return bgGrid;
  bgGridSrc = bg;
  const n = bounds.length;
  let minx = Infinity, miny = Infinity, maxx = -Infinity, maxy = -Infinity;
  for (const b of bounds) {
    if (!isFinite(b.minx) || !isFinite(b.miny)) continue;
    if (b.minx < minx) minx = b.minx;
    if (b.miny < miny) miny = b.miny;
    if (b.maxx > maxx) maxx = b.maxx;
    if (b.maxy > maxy) maxy = b.maxy;
  }
  if (!isFinite(minx)) { minx = 0; miny = 0; maxx = 1; maxy = 1; }
  // Aim for ~1 entity per cell on average; clamp so the grid stays small.
  const dim = Math.max(1, Math.min(256, Math.round(Math.sqrt(n))));
  const cols = dim, rows = dim;
  const cellW = (maxx - minx) / cols || 1;
  const cellH = (maxy - miny) / rows || 1;
  const cells: number[][] = new Array(cols * rows);
  for (let i = 0; i < cells.length; i++) cells[i] = [];
  for (let i = 0; i < n; i++) {
    const b = bounds[i];
    if (!isFinite(b.minx) || !isFinite(b.miny)) continue;
    let c0 = Math.floor((b.minx - minx) / cellW);
    let c1 = Math.floor((b.maxx - minx) / cellW);
    let r0 = Math.floor((b.miny - miny) / cellH);
    let r1 = Math.floor((b.maxy - miny) / cellH);
    if (c0 < 0) c0 = 0;
    if (r0 < 0) r0 = 0;
    if (c1 >= cols) c1 = cols - 1;
    if (r1 >= rows) r1 = rows - 1;
    for (let r = r0; r <= r1; r++)
      for (let c = c0; c <= c1; c++) cells[r * cols + c].push(i);
  }
  bgGrid = { minx, miny, cellW, cellH, cols, rows, cells, seen: new Int32Array(n), epoch: 0 };
  return bgGrid;
}

const idn = (p: Vec2): Vec2 => p;
const DEFAULT_BG_COLOR = '#9fb3c8';
// Reused across frames so the visible-entity pass allocates nothing per frame.
const visBuf: number[] = [];

/** Append an arc/circle to the current path with sample count scaled to its
 *  on-screen radius (LOD): tiny arcs get a few segments, large ones up to 64. */
function addArcPath(
  ctx: CanvasRenderingContext2D,
  sc: number, tx: number, ty: number,
  center: Vec2, radius: number, startDeg: number, endDeg: number
): void {
  let sweep = endDeg - startDeg;
  while (sweep <= 0) sweep += 360;
  const rPx = radius * sc;
  const segs = Math.max(6, Math.min(64, Math.ceil(((sweep / 360) * rPx) / 3)));
  const cx = center.x, cy = center.y;
  const step = (sweep * Math.PI) / 180 / segs;
  const a0 = (startDeg * Math.PI) / 180;
  for (let i = 0; i <= segs; i++) {
    const a = a0 + step * i;
    const x = (cx + radius * Math.cos(a)) * sc + tx;
    const y = -(cy + radius * Math.sin(a)) * sc + ty;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
}

/** Append a strokeable entity to the current path, computing screen coords
 *  inline (no per-point Vec2 allocation). */
function addEntityPath(
  ctx: CanvasRenderingContext2D,
  sc: number, tx: number, ty: number,
  e: GeomEntity
): void {
  switch (e.kind) {
    case 'line':
      ctx.moveTo(e.a.x * sc + tx, -e.a.y * sc + ty);
      ctx.lineTo(e.b.x * sc + tx, -e.b.y * sc + ty);
      break;
    case 'polyline': {
      const pts = e.points;
      if (pts.length === 0) break;
      ctx.moveTo(pts[0].x * sc + tx, -pts[0].y * sc + ty);
      for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x * sc + tx, -pts[i].y * sc + ty);
      if (e.closed) ctx.closePath();
      break;
    }
    case 'circle':
      addArcPath(ctx, sc, tx, ty, e.center, e.radius, 0, 360);
      break;
    case 'arc':
      addArcPath(ctx, sc, tx, ty, e.center, e.radius, e.startAngle, e.endAngle);
      break;
    case 'ellipse':
    case 'spline': {
      const pts = curveTessellation(e);
      if (pts && pts.length) {
        ctx.moveTo(pts[0].x * sc + tx, -pts[0].y * sc + ty);
        for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x * sc + tx, -pts[i].y * sc + ty);
        if (e.closed) ctx.closePath();
      }
      break;
    }
  }
}

/** Draw the imported DXF geometry. Collects the visible entities via a spatial
 *  grid, then strokes them batched by colour (one beginPath/stroke per colour)
 *  with inline coordinate math, so a heavy plan stays cheap to repaint. */
function drawBackgroundGeometry(ctx: CanvasRenderingContext2D, vp: Viewport, drawing: Drawing, w: number, h: number): void {
  const bg = drawing.background;
  if (!bg) return;
  const bounds = backgroundBounds(bg);
  const grid = backgroundGrid(bg, bounds);
  // The DXF can be moved as an object: fold its world offset into tx/ty so both
  // culling and drawing map each entity to its shifted position.
  const off = drawing.backgroundOffset;
  const sc = vp.scale, tx = vp.tx + (off ? off.x * vp.scale : 0), ty = vp.ty - (off ? off.y * vp.scale : 0);
  // Visible world rectangle (+ a small margin), for culling.
  const wx0 = (0 - tx) / sc, wx1 = (w - tx) / sc;
  const wyA = -(0 - ty) / sc, wyB = -(h - ty) / sc;
  const vminx = Math.min(wx0, wx1), vmaxx = Math.max(wx0, wx1);
  const vminy = Math.min(wyA, wyB), vmaxy = Math.max(wyA, wyB);
  const m = 4 / sc;

  // Walk only the grid cells overlapping the viewport, deduping via epoch.
  const vis = visBuf;
  vis.length = 0;
  const epoch = ++grid.epoch;
  const seen = grid.seen;
  let c0 = Math.floor((vminx - m - grid.minx) / grid.cellW);
  let c1 = Math.floor((vmaxx + m - grid.minx) / grid.cellW);
  let r0 = Math.floor((vminy - m - grid.miny) / grid.cellH);
  let r1 = Math.floor((vmaxy + m - grid.miny) / grid.cellH);
  if (c0 < 0) c0 = 0;
  if (r0 < 0) r0 = 0;
  if (c1 >= grid.cols) c1 = grid.cols - 1;
  if (r1 >= grid.rows) r1 = grid.rows - 1;
  const ents = bg.entities;
  for (let r = r0; r <= r1; r++) {
    for (let c = c0; c <= c1; c++) {
      const cell = grid.cells[r * grid.cols + c];
      for (let k = 0; k < cell.length; k++) {
        const i = cell[k];
        if (seen[i] === epoch) continue;
        seen[i] = epoch;
        const b = bounds[i];
        if (b.maxx < vminx - m || b.minx > vmaxx + m || b.maxy < vminy - m || b.miny > vmaxy + m) continue; // off-screen
        if ((b.maxx - b.minx) * sc < 0.4 && (b.maxy - b.miny) * sc < 0.4) continue; // sub-pixel
        vis.push(i);
      }
    }
  }
  if (vis.length === 0) return;

  // Bucket the stroked entities by colour; hatches (filled) and text are drawn
  // per-entity afterwards since they don't share the stroke batch.
  ctx.lineWidth = 1;
  const byColor = new Map<string, number[]>();
  let hatches: number[] | null = null;
  let texts: number[] | null = null;
  for (let j = 0; j < vis.length; j++) {
    const e = ents[vis[j]];
    if (e.kind === 'hatch') { (hatches ??= []).push(vis[j]); continue; }
    if (e.kind === 'text') { (texts ??= []).push(vis[j]); continue; }
    const key = e.color ?? DEFAULT_BG_COLOR;
    let bucket = byColor.get(key);
    if (!bucket) { bucket = []; byColor.set(key, bucket); }
    bucket.push(vis[j]);
  }
  for (const [color, idxs] of byColor) {
    ctx.strokeStyle = color;
    ctx.beginPath();
    for (let j = 0; j < idxs.length; j++) addEntityPath(ctx, sc, tx, ty, ents[idxs[j]]);
    ctx.stroke();
  }
  if (hatches || texts) {
    ctx.fillStyle = DEFAULT_BG_COLOR; // default for untinted text/hatch fills
    if (hatches && pass.showHatches) for (const i of hatches) drawEntity(ctx, vp, ents[i], idn);
    if (texts) for (const i of texts) drawEntity(ctx, vp, ents[i], idn);
  }
}

/** Static backdrop: page fill, PDF underlay, grid, axes and imported geometry. */
/** The imported raster/PDF underlays, back to front (no page fill, no grid). */
function drawUnderlayImages(ctx: CanvasRenderingContext2D, vp: Viewport, opts: RenderOptions): void {
  for (const bi of opts.backgroundImages ?? []) {
    const c = vp.worldToScreen({ x: bi.x, y: bi.y });
    const wpx = bi.widthMm * vp.scale;
    const hpx = bi.heightMm * vp.scale;
    ctx.save();
    ctx.translate(c.x, c.y);
    if (bi.rotation) ctx.rotate(-(bi.rotation * Math.PI) / 180);
    if (bi.opacity != null) ctx.globalAlpha = bi.opacity;
    ctx.drawImage(bi.img, -wpx / 2, -hpx / 2, wpx, hpx);
    ctx.restore();
    // Crisp overlay of the visible page slice (only present when zoomed in).
    if (bi.sharp && !bi.rotation) {
      const s = bi.sharp;
      const sc = vp.worldToScreen({ x: s.x, y: s.y });
      ctx.save();
      if (bi.opacity != null) ctx.globalAlpha = bi.opacity;
      ctx.drawImage(s.canvas, sc.x - (s.widthMm * vp.scale) / 2, sc.y - (s.heightMm * vp.scale) / 2, s.widthMm * vp.scale, s.heightMm * vp.scale);
      ctx.restore();
    }
  }
}

/** The imported drawing content only — raster/PDF underlays + imported DXF
 *  geometry — with no page fill and no grid. Used to show the imported background
 *  inside a sheet's model viewport. */
function drawImportedContent(ctx: CanvasRenderingContext2D, vp: Viewport, drawing: Drawing, opts: RenderOptions, w: number, h: number): void {
  if (opts.hideImported) return;
  drawUnderlayImages(ctx, vp, opts);
  drawBackgroundGeometry(ctx, vp, drawing, w, h);
}

function drawBackdrop(ctx: CanvasRenderingContext2D, vp: Viewport, drawing: Drawing, opts: RenderOptions, w: number, h: number): void {
  ctx.fillStyle = opts.drawingEditId ? EDITOR_BG() : PAGE_BG();
  ctx.fillRect(0, 0, w, h);
  if (!opts.hideImported) drawUnderlayImages(ctx, vp, opts);
  if (opts.showGrid) {
    drawGrid(ctx, vp, w, h);
    drawAxes(ctx, vp, w, h);
  }
  if (!opts.hideImported) drawBackgroundGeometry(ctx, vp, drawing, w, h);
}

/** Layers for a render with no layer model passed in: rooms are told apart, but
 *  nothing nests (the caller knows where parts stand; this doesn't). */
const PLAIN_LAYER_ENV: LayerEnv = { isRoom: (i) => !!i.autoBenches || isPolyRoom(i), hostRoom: () => null };

/** Render only the static backdrop (used for the dedicated background canvas). */
export function renderBackground(ctx: CanvasRenderingContext2D, vp: Viewport, drawing: Drawing, opts: RenderOptions): void {
  const canvas = ctx.canvas;
  const cssW = canvas.clientWidth || canvas.width;
  const cssH = canvas.clientHeight || canvas.height;
  const dpr = canvas.width / cssW || 1;
  withPass({ ...basePass(opts), ...NO_DERIVED }, () => {
    ctx.save();
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    drawBackdrop(ctx, vp, drawing, opts, cssW, cssH);
    ctx.restore();
  });
}

/** Draw the drawing on `ctx` through `vp`, and return the hit targets drawn
 *  (what a press on that canvas finds). */
export function render(ctx: CanvasRenderingContext2D, vp: Viewport, drawing: Drawing, opts: RenderOptions): RenderHits {
  return withPass({ ...basePass(opts), ...derivedOf(drawing, opts) }, () => {
    renderNow(ctx, vp, drawing, opts);
    return pass.hits;
  });
}

function renderNow(ctx: CanvasRenderingContext2D, vp: Viewport, drawing: Drawing, opts: RenderOptions): void {
  const canvas = ctx.canvas;
  // The backing store is sized in physical pixels (cssPx * dpr); draw in CSS
  // pixels and let a dpr transform scale up, so text and fixed-size UI stay
  // crisp and correctly sized on high-DPI displays.
  const cssW = canvas.clientWidth || canvas.width;
  const cssH = canvas.clientHeight || canvas.height;
  const dpr = (canvas.width / cssW || 1) * (opts.deviceScale ?? 1);
  const w = cssW;
  const h = cssH;

  ctx.save();
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  if (opts.layered) {
    // The static backdrop (page, grid, PDF, imported geometry) lives on a
    // separate canvas behind this one; keep this layer transparent — except a
    // sheet viewport, which draws the imported underlays here (on its white page,
    // no grid) so the model window shows the imported PDF/DXF/images.
    ctx.clearRect(0, 0, w, h);
    if (opts.underlay) drawImportedContent(ctx, vp, drawing, opts, w, h);
  } else {
    drawBackdrop(ctx, vp, drawing, opts, w, h);
  }

  // ---- The drawing, LAYER BY LAYER (renderer/layers.ts). `paint` is the ONE
  // stacking order — the picker reads the same list — and each item is drawn by
  // its kind. What used to be a whole-kind pass is computed once here and applied
  // per item: the connections' lanes and hops, the text leaders' hops, the
  // section labels, and the ink backdrop sample (taken before the first
  // annotation, so it reads the plan underneath rather than the annotations).
  const paint = (opts.paint ?? layerModel(drawing, PLAIN_LAYER_ENV).paint).filter((pi) => !opts.hiddenLayers?.has(pi.layer));
  const painted = new Set(paint.map((pi) => pi.id));
  // Numbers come from the full schedule so a discipline filter doesn't renumber.
  const filters = opts.disciplineFilters;
  const showType = (t: ConnectionType) => !filters || filters[t] !== false;
  const connGroups = pass.connSchedule.filter((g) => showType(g.type));
  const prepGroups = pass.prepSchedule.filter((g) => showType(g.type));
  const { connNums, prepNums } = pass;

  const selIds = opts.selectedIds && opts.selectedIds.length ? opts.selectedIds : (opts.selectedId ? [opts.selectedId] : []);
  const hidden = (inst: ProductInstance): boolean => !!opts.hiddenCategories && opts.hiddenCategories.has(inst.category);
  const selPolys = opts.selectedPolyIds && opts.selectedPolyIds.length ? opts.selectedPolyIds : (opts.selectedPolyId ? [opts.selectedPolyId] : []);
  const selWalls = opts.selectedWallIds && opts.selectedWallIds.length ? opts.selectedWallIds : (opts.selectedWallId ? [opts.selectedWallId] : []);
  const selLeaders = opts.selectedLeaderIds && opts.selectedLeaderIds.length ? opts.selectedLeaderIds : (opts.selectedLeaderId ? [opts.selectedLeaderId] : []);
  const selClouds = opts.selectedRevCloudIds || [];
  // A caller decides which of these kinds it shows at all (print previews pass
  // no texts, the print window no floorings).
  const polyIds = new Set((opts.polygons ?? []).map((q) => q.id));
  const wallIds = new Set((opts.walls ?? []).map((q) => q.id));
  const floorOf = new Map((opts.floorings ?? []).map((f) => [f.floor.id, f]));
  const textIds = new Set((opts.texts ?? []).map((q) => q.id));
  const paintRank = new Map(paint.map((pi, i) => [pi.id, i]));
  const byPaint = <T extends { id: string }>(arr: readonly T[]): T[] =>
    arr.filter((q) => painted.has(q.id)).sort((a, b) => paintRank.get(a.id)! - paintRank.get(b.id)!);
  // Leader segments (screen space) so crossing leaders can hop over each other:
  // a text's leader hops over those of the texts painted before it (a hidden
  // one is not there to hop over).
  const textHops = new Map<string, Vec2[]>();
  if (opts.texts) {
    const texts = byPaint(opts.texts);
    const leaders = texts.map((t) => {
      if (!t.leader) return null;
      const tl = vp.worldToScreen({ x: t.x, y: t.y });
      const target = vp.worldToScreen(t.leader);
      const start = nearestRectPoint(tl.x, tl.y, t.w * vp.scale, t.h * vp.scale, target);
      return { start, target };
    });
    texts.forEach((t, j) => {
      const me = leaders[j];
      const hops: Vec2[] = [];
      if (me) {
        for (let i = 0; i < j; i++) {
          const o = leaders[i];
          if (!o) continue;
          const x = segCross(me.start, me.target, o.start, o.target);
          if (x) hops.push(x);
        }
      }
      textHops.set(t.id, hops);
    });
  }
  // A reflected ceiling plan shows its room while the room is shown.
  const drawRcp = (v: CeilingPlanView, selected: boolean): void => {
    const room = drawing.instances.find((i) => i.id === v.roomId);
    if (!room || !painted.has(room.id)) return;
    const vt = opts.viewTitles;
    drawCeilingPlan(ctx, vp, v, room, {
      wall: wallColorOf(room), selected, title: rcpTitle(v, room), figure: formatDim, tags: pass.lightTags, lights: pass.showLights,
      source: { drawing, keepPart: (p) => painted.has(p.id) && !hidden(p), keepConn: (c) => painted.has(c.id) && (!c.type || showType(c.type)) },
      ...(pass.showLabels ? { partName: (id: string) => pass.numberedNames.get(id) ?? '' } : {}),
      ...(vt ? { titleSelected: vt.selected?.kind === 'rcp' && vt.selected.id === v.id, onTitle: (b: TitleBox) => { pass.hits.viewTitles.push({ ...b, kind: 'rcp', id: v.id }); } } : {}),
    });
  };
  // A section cuts what is shown: a hidden layer's objects leave it too.
  const secs = drawing.sections || [];
  const secIndex = new Map(secs.map((sec, i) => [sec.id, i]));
  const secInsts = drawing.instances.filter((i) => painted.has(i.id));
  const secWalls = (drawing.walls || []).filter((q) => painted.has(q.id));
  const secPolys = (drawing.polygons || []).filter((q) => painted.has(q.id));
  const secPipes = pipeTubes(drawing, (c) => painted.has(c.id) && showType(connDiscipline(c)));
  // The connections drawn, in paint order: a hidden one takes no lane and makes
  // no hop, and a line restacked over another is the one that hops.
  const paintedConns = byPaint(drawing.connections || []);

  // Model space only: ONE backdrop sample per frame, over the union of every
  // adaptive-ink annotation's extent (dimensions, and the leader arrows of text
  // boxes), so each stretch of ink can be lifted where — and only where — it runs
  // over a dark backdrop. Sheets and print pass no backdrop and therefore render
  // the stored colour exactly.
  const pendingDimLine = opts.pendingDim
    ? { id: '_pending', a: opts.pendingDim.a, b: opts.pendingDim.b, offset: opts.pendingDim.offset, kind: opts.pendingDim.kind }
    : null;
  let dimSample: LumaProbe | null = null;
  let sampled = false;
  const takeInkSample = (): void => {
    sampled = true;
    if (!opts.inkBackdrop) return;
    let box: { x0: number; y0: number; x1: number; y1: number } | null = null;
    const add = (b: { x0: number; y0: number; x1: number; y1: number } | null): void => {
      if (!b) return;
      box = box
        ? { x0: Math.min(box.x0, b.x0), y0: Math.min(box.y0, b.y0), x1: Math.max(box.x1, b.x1), y1: Math.max(box.y1, b.y1) }
        : b;
    };
    for (const dim of [...drawing.dimensions, ...(pendingDimLine ? [pendingDimLine] : [])]) add(dimScreenBox(vp, dim));
    for (const t of opts.texts ?? []) {
      if (!t.leader) continue;
      const tl = vp.worldToScreen({ x: t.x, y: t.y });
      const tg = vp.worldToScreen(t.leader);
      add({
        x0: Math.min(tl.x, tg.x) - 20, y0: Math.min(tl.y, tg.y) - 20,
        x1: Math.max(tl.x + t.w * vp.scale, tg.x) + 20, y1: Math.max(tl.y + t.h * vp.scale, tg.y) + 20,
      });
    }
    if (box) dimSample = backdropSampler(ctx, opts.inkBackdrop, canvas.width / cssW || 1, box);
  };
  // The sample is taken after the last physical item (so it reads the whole
  // plan, even under a section sent below it), or before the first ink-adaptive
  // one (a text or a dimension), whichever comes first.
  const ANNOTATION_KINDS = new Set<PaintItem['kind']>(['section', 'rcp', 'text', 'arc', 'leader', 'cloud', 'table', 'dim']);
  let lastPhys = -1;
  paint.forEach((pi, i) => { if (!ANNOTATION_KINDS.has(pi.kind)) lastPhys = i; });
  // Preparation tags go down once every part is drawn, so no later part covers one.
  let lastInst = -1;
  paint.forEach((pi, i) => { if (pi.kind === 'instance' || pi.kind === 'roomTop') lastInst = i; });
  let tagsDone = false;
  const drawTagsOf = (inst: ProductInstance): void => {
    if (!opts.showSchedule || hidden(inst)) return;
    const preps = (inst.preparations || []).filter((q) => showType(q.type));
    preps.forEach((q, i) => {
      const tl = vp.worldToScreen(instanceToWorld(inst, { x: -inst.width / 2, y: inst.depth / 2 }));
      const num = prepNums.get(q.id);
      if (num) drawTag(ctx, tl.x + 14 + i * 30, tl.y + 12, num, CONNECTION_COLORS[q.type]);
    });
  };
  const drawPrepTags = (): void => {
    tagsDone = true;
    // (The part open in the drawing editor is drawn last, its tags on it.)
    for (const pi of paint) if (pi.kind === 'instance' && pi.obj !== editInst) drawTagsOf(pi.obj as ProductInstance);
  };

  const drawOneInstance = (inst: ProductInstance): void => {
    if (hidden(inst)) return; // a discipline toggle hides this object
    const pendInner = opts.pendingInnerWall && opts.pendingInnerWall.instId === inst.id ? opts.pendingInnerWall.iw : null;
    const isEditInst = opts.drawingEditId === inst.id;
    const dimForEdit = !!opts.drawingEditId && !isEditInst;
    if (dimForEdit) ctx.globalAlpha = 0.2;
    const deCtx = isEditInst
      ? { selectedPolyId: opts.drawEditSelectedPolyId ?? null, selectedWallId: opts.drawEditSelectedWallId ?? null, verticesId: opts.drawEditVerticesId ?? null }
      : null;
    drawInstance(ctx, vp, inst, selIds.includes(inst.id), opts.instanceImages, pendInner, deCtx);
    if (dimForEdit) ctx.globalAlpha = 1;
  };
  const drawTable = (id: string): void => {
    if (!opts.showScheduleTables) return;
    if (id === 'table:connections' && connGroups.length && drawing.scheduleTable) {
      drawScheduleTable(ctx, vp, drawing.scheduleTable, drawing, 'connection', opts.selectedScheduleTable === 'connection');
    } else if (id === 'table:preparations' && prepGroups.length && drawing.preparationTable) {
      drawScheduleTable(ctx, vp, drawing.preparationTable, drawing, 'preparation', opts.selectedScheduleTable === 'preparation');
    } else if (id === 'table:floorings' && opts.disciplineFilters?.flooring !== false && (drawing.floorings || []).length && drawing.flooringTable) {
      drawScheduleTable(ctx, vp, drawing.flooringTable, drawing, 'flooring', opts.selectedScheduleTable === 'flooring');
    } else if (id === 'table:lighting' && opts.disciplineFilters?.lighting !== false && drawing.instances.some((i) => i.lights?.length) && drawing.lightingTable) {
      drawScheduleTable(ctx, vp, drawing.lightingTable, drawing, 'lighting', opts.selectedScheduleTable === 'lighting');
    } else if (id === 'table:pipes' && drawing.pipeTable && (drawing.connections || []).some((c) => c.pipe && showType(connDiscipline(c)))) {
      drawScheduleTable(ctx, vp, drawing.pipeTable, drawing, 'pipe', opts.selectedScheduleTable === 'pipe');
    }
  };
  const editInst = opts.drawingEditId ? drawing.instances.find((i) => i.id === opts.drawingEditId) ?? null : null;

  for (let k = 0; k < paint.length; k++) {
    const pi = paint[k];
    if (!tagsDone && k > lastInst) drawPrepTags();
    if (!sampled && (k > lastPhys || pi.kind === 'text' || pi.kind === 'dim')) takeInkSample();
    switch (pi.kind) {
      case 'polygon': {
        if (!polyIds.has(pi.id)) break;
        const selEdge = opts.selectedPolyEdge && opts.selectedPolyEdge.id === pi.id ? opts.selectedPolyEdge.edge : -1;
        drawPolygon(ctx, vp, pi.obj as Polygon, selPolys.includes(pi.id), selPolys.length === 1 && selPolys[0] === pi.id, selEdge);
        break;
      }
      case 'wall':
        if (wallIds.has(pi.id)) drawWall(ctx, vp, pi.obj as Wall, selWalls.includes(pi.id));
        break;
      case 'flooring': {
        // Hidden when the flooring discipline is toggled off.
        const f = floorOf.get(pi.id);
        if (f && filters?.flooring !== false) drawFlooring(ctx, vp, f.floor, f.selected, f.drainInst, f.drainSel);
        break;
      }
      case 'instance':
        if (pi.obj !== editInst) drawOneInstance(pi.obj as ProductInstance);
        break;
      case 'roomTop': {
        // A room's panes and openings, over what stands in it.
        const inst = pi.obj as ProductInstance;
        const pending = opts.pendingGlass && opts.pendingGlass.instId === inst.id ? opts.pendingGlass.seg : null;
        drawGlass(ctx, vp, inst, pending);
        drawGlazingsPlan(ctx, inst, (p) => vp.worldToScreen(instanceToWorld(inst, p)), { light: pass.light, selectedDoorId: pass.selectedDoorId, selectedPaneIds: pass.selectedGlassIds, woodFill: wallColorOf(inst) });
        const pendOpen = opts.pendingOpening && opts.pendingOpening.instId === inst.id ? opts.pendingOpening.seg : null;
        drawOpenings(ctx, vp, inst, pendOpen);
        drawRoomWallCuts(ctx, vp, inst);
        drawRoomRisers(ctx, vp, inst, (id) => {
          const part = pass.partsById.get(id);
          return !!part && painted.has(id) && !hidden(part);
        });
        if (!hidden(inst)) {
          const bg = opts.inkBackdrop;
          drawCeilingOverlay(ctx, vp, inst, bg ? (box) => backdropSampler(ctx, bg, canvas.width / cssW || 1, box) : null);
        }
        break;
      }
      case 'conn': {
        // Consecutive connections are one call, so their tags separate together.
        const run = new Set<string>();
        while (k < paint.length && paint[k].kind === 'conn') run.add(paint[k++].id);
        k--;
        // Connection lines between parts are only drawn with the schedule enabled,
        // each while its discipline is (`connectionShown`, as the DXF writes them).
        const show = { schedule: !!opts.showSchedule, filters };
        drawConnections(ctx, vp, drawing, opts.selectedId, connNums, (c) => connectionShown(c, show), run, paintedConns);
        break;
      }
      case 'section':
        drawSection(ctx, vp, pi.obj as SectionLine, secInsts, pi.id === opts.selectedSectionId, sectionLabel(secIndex.get(pi.id) ?? 0), secWalls, secPolys,
          {
            selected: opts.sectionMarks?.selected ?? [], drilled: !!opts.sectionMarks && opts.sectionMarks.drilledId === pi.id, beams: opts.showBeams !== false, lights: pass.showLights, cones: drawing.lightBeams,
            ...(opts.viewTitles ? { titleSelected: opts.viewTitles.selected?.kind === 'section' && opts.viewTitles.selected.id === pi.id, onTitle: (b: TitleBox) => { pass.hits.viewTitles.push({ ...b, kind: 'section', id: pi.id }); } } : {}),
          }, secPipes);
        break;
      case 'text': {
        if (!textIds.has(pi.id)) break;
        const selText = opts.selectedTextIds && opts.selectedTextIds.length ? opts.selectedTextIds.includes(pi.id) : pi.id === opts.selectedTextId;
        drawTextComment(ctx, vp, pi.obj as TextComment, selText, pi.id === opts.editingTextId, textHops.get(pi.id) ?? [], dimSample);
        break;
      }
      case 'arc': {
        const arc = pi.obj as Arc;
        const c = vp.worldToScreen(arc.center);
        const r = arc.radius * vp.scale;
        const sel = opts.selectedArcIds && opts.selectedArcIds.length ? opts.selectedArcIds.includes(arc.id) : arc.id === opts.selectedArcId;
        // World is Y-up, screen Y-down: negate angles and swap start/end so a CCW
        // world arc draws correctly on the flipped canvas.
        ctx.save();
        ctx.strokeStyle = sel ? '#ffd166' : arc.stroke;
        ctx.lineWidth = Math.max(sel ? 2 : 1, arc.strokeWidth * vp.scale);
        ctx.beginPath();
        ctx.arc(c.x, c.y, r, -(arc.startDeg * Math.PI) / 180, -(arc.endDeg * Math.PI) / 180, true);
        ctx.stroke();
        ctx.restore();
        break;
      }
      case 'leader':
        drawLeader(ctx, vp, pi.obj as Leader, selLeaders.includes(pi.id), drawing.language ?? 'it');
        break;
      case 'cloud':
        drawRevisionCloud(ctx, vp, pi.obj as RevisionCloud, selClouds.includes(pi.id), selClouds.length === 1 && selClouds[0] === pi.id);
        break;
      case 'table':
        drawTable(pi.id);
        break;
      case 'dim':
        drawDimension(ctx, vp, pi.obj as DimensionLine, pi.id === opts.selectedDimId, dimSample);
        break;
      case 'rcp':
        drawRcp(pi.obj as CeilingPlanView, pi.id === opts.selectedRcpId);
        break;
      default:
        unreachable(pi.kind, 'paint item kind');
    }
  }
  if (!tagsDone) drawPrepTags();
  if (!sampled) takeInkSample();
  // The part being drawn in the product drawing editor sits above everything.
  if (editInst) { drawOneInstance(editInst); drawTagsOf(editInst); }

  // Related parts of the selection: a dashed green outline, so a relation can be
  // read at any time, not only while it is made.
  for (const id of opts.relatedIds ?? []) {
    const t = drawing.instances.find((i) => i.id === id);
    if (!t || !painted.has(id)) continue;
    const pts = instanceCorners(t).map((c) => vp.worldToScreen(c));
    ctx.save();
    ctx.beginPath();
    pts.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
    ctx.closePath();
    ctx.lineWidth = 1.5;
    ctx.setLineDash([6, 4]);
    ctx.strokeStyle = 'rgba(34, 230, 80, 0.85)';
    ctx.stroke();
    ctx.restore();
  }

  // The part a dragged part would be related to on release: a pulsing green
  // outline (the bench tool's "add" green) and its label beside it.
  if (opts.dropTargetId) {
    const t = drawing.instances.find((i) => i.id === opts.dropTargetId);
    if (t) {
      const pulse = 0.55 + 0.4 * (1 + Math.sin(performance.now() / 140)) / 2;
      const pts = instanceCorners(t).map((c) => vp.worldToScreen(c));
      ctx.save();
      ctx.beginPath();
      pts.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
      ctx.closePath();
      ctx.fillStyle = `rgba(34, 230, 80, ${(pulse * 0.2).toFixed(3)})`;
      ctx.fill();
      ctx.lineWidth = 2.5;
      ctx.strokeStyle = `rgba(34, 230, 80, ${pulse.toFixed(3)})`;
      ctx.stroke();
      if (opts.dropTargetLabel) {
        const right = pts.reduce((m, p) => Math.max(m, p.x), -Infinity);
        const top = pts.reduce((m, p) => Math.min(m, p.y), Infinity);
        ctx.font = '11px system-ui, sans-serif';
        const bw = Math.ceil(ctx.measureText(opts.dropTargetLabel).width) + 12, bh = 17;
        ctx.fillStyle = 'rgba(18,20,24,0.9)';
        ctx.fillRect(right + 6, top - bh - 2, bw, bh);
        ctx.fillStyle = 'rgb(34, 230, 80)';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        ctx.fillText(opts.dropTargetLabel, right + 12, top - 2 - bh / 2);
      }
      ctx.restore();
    }
  }

  // Light-blue hatch over the object whose row is hovered in the Parts tab.
  if (opts.hoverHighlightRing && opts.hoverHighlightRing.length >= 3) {
    drawHoverHatch(ctx, vp, opts.hoverHighlightRing);
  }


  // Dents (inverse corners) need no fill: the sauna's floor tint and benches are
  // already clipped to exclude them, so the notch stays transparent and reveals
  // whatever lies beneath the sauna.

  // PDF transform handles (move/resize/rotate the underlay).
  if (opts.editBox) drawPdfTransform(ctx, vp, opts.editBox);

  // Selection outline for the imported DXF being moved.
  if (opts.editBgBounds) {
    const b = opts.editBgBounds;
    const p0 = vp.worldToScreen({ x: b.min.x, y: b.max.y });
    const p1 = vp.worldToScreen({ x: b.max.x, y: b.min.y });
    ctx.save();
    ctx.lineWidth = 2;
    ctx.strokeStyle = '#ffd166';
    ctx.setLineDash([6, 4]);
    ctx.strokeRect(p0.x, p0.y, p1.x - p0.x, p1.y - p0.y);
    ctx.restore();
  }

  // Vertex-edit handles for a polygon/wall being edited.
  if (opts.editVerticesId) {
    const shape =
      (opts.polygons || []).find((p) => p.id === opts.editVerticesId) ||
      (opts.walls || []).find((w) => w.id === opts.editVerticesId) ||
      (drawing.revisionClouds || []).find((c) => c.id === opts.editVerticesId);
    if (shape) {
      drawGrips(ctx, shape.points.map((p) => vp.worldToScreen(p)), VERTEX_EDIT_GREEN);
      // Hole rings get handles too, so their vertices can be edited.
      if ('holes' in shape && shape.holes) {
        for (const hole of shape.holes) drawGrips(ctx, hole.map((p) => vp.worldToScreen(p)), VERTEX_EDIT_GREEN);
      }
    }
  }
  // Highlight the segment a hovered constraint row refers to ("…to segment N").
  if (opts.hoverConstraintSeg) {
    const { shapeId, edge } = opts.hoverConstraintSeg;
    const poly = (opts.polygons || []).find((p) => p.id === shapeId);
    const inst = poly ? null : drawing.instances.find((i) => i.id === shapeId);
    const ring = poly ? poly.points : inst?.polygon?.map((p) => instanceToWorld(inst, p));
    if (ring && edge >= 0 && edge < ring.length) {
      const a = vp.worldToScreen(ring[edge]), b = vp.worldToScreen(ring[(edge + 1) % ring.length]);
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y);
      ctx.strokeStyle = '#ffb300';
      ctx.lineWidth = 5;
      ctx.lineCap = 'round';
      ctx.stroke();
      ctx.restore();
    }
  }

  if (opts.pendingSection) drawSection(ctx, vp, opts.pendingSection, secInsts, true, sectionLabel(secs.length), secWalls, secPolys, undefined, secPipes);
  if (opts.lux) drawLuxMaps(ctx, vp, opts.lux);
  if (opts.rcpGuide) drawOrthoGuides(ctx, vp, opts.rcpGuide[1], [opts.rcpGuide[0]]);
  if (opts.pendingRcp) {
    ctx.save(); ctx.globalAlpha = 0.7;
    drawRcp(opts.pendingRcp, true);
    ctx.restore();
  }
  if (opts.pendingTextBox) drawPendingTextBox(ctx, (q) => vp.worldToScreen(q), vp.scale, opts.pendingTextBox);

  if (opts.pendingLeader && opts.pendingLeader.pts.length) {
    const pts = opts.pendingLeader.hover ? [...opts.pendingLeader.pts, opts.pendingLeader.hover] : opts.pendingLeader.pts;
    ctx.save();
    ctx.strokeStyle = ANNOTATION_COLOR;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    pts.forEach((p, i) => { const s = vp.worldToScreen(p); i ? ctx.lineTo(s.x, s.y) : ctx.moveTo(s.x, s.y); });
    ctx.stroke();
    if (pts.length >= 2) drawArrow(ctx, vp.worldToScreen(pts[0]), vp.worldToScreen(pts[1]));
    ctx.restore();
  }
  if (opts.pendingArc && opts.pendingArc.dots.length) {
    ctx.save();
    ctx.fillStyle = '#4aa3ff';
    for (const p of opts.pendingArc.dots) { const s = vp.worldToScreen(p); ctx.beginPath(); ctx.arc(s.x, s.y, 3, 0, Math.PI * 2); ctx.fill(); }
    const fit = opts.pendingArc.fit;
    if (fit) {
      ctx.strokeStyle = '#4aa3ff';
      ctx.lineWidth = 1.5;
      ctx.setLineDash([6, 4]);
      const c = vp.worldToScreen(fit.center);
      ctx.beginPath();
      ctx.arc(c.x, c.y, fit.radius * vp.scale, -(fit.startDeg * Math.PI) / 180, -(fit.endDeg * Math.PI) / 180, true);
      ctx.stroke();
    }
    ctx.restore();
  }
  if (opts.pendingRevisionCloud) drawPendingRevisionCloud(ctx, vp, opts.pendingRevisionCloud.pts, opts.pendingRevisionCloud.hover);

  // The object under the cursor offers its connection points (PowerPoint): a dot
  // at each one the Connect tool can glue to. Not grips — nothing drags them.
  if (opts.connectionPorts && opts.connectionPorts.length) {
    ctx.save();
    ctx.fillStyle = '#4aa3ff';
    ctx.strokeStyle = '#1e1f24';
    ctx.lineWidth = 1;
    for (const p of opts.connectionPorts) {
      const sp = vp.worldToScreen(p);
      ctx.beginPath();
      ctx.arc(sp.x, sp.y, 3.5, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
    }
    ctx.restore();
  }
  // Live Connect preview: start, bends so far and the cursor, dashed in the
  // discipline the connection will be born with.
  if (opts.pendingConnection && opts.pendingConnection.pts.length >= 2) {
    const pts = opts.pendingConnection.pts.map((p) => vp.worldToScreen(p));
    ctx.save();
    ctx.setLineDash([9, 6]);
    ctx.lineWidth = 1.5;
    ctx.strokeStyle = CONNECTION_COLORS[opts.pendingConnection.type];
    ctx.beginPath();
    pts.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
    ctx.stroke();
    ctx.restore();
  }

  if (opts.dragGuides && opts.dragGuides.length) {
    drawDragGuides(ctx, vp, opts.dragGuides);
  }

  // Blue orthogonal guides while dragging an existing vertex (same as draw tools).
  if (opts.editGuide) drawOrthoGuides(ctx, vp, opts.editGuide.at, opts.editGuide.verts);

  // Thin red dashed lines connecting aligned object centroids.
  if (opts.centroidGuides && opts.centroidGuides.length) {
    ctx.save();
    ctx.setLineDash([5, 4]);
    ctx.strokeStyle = 'rgba(255, 70, 70, 0.9)';
    ctx.lineWidth = 1;
    for (const [a, b] of opts.centroidGuides) {
      const sa = vp.worldToScreen(a), sb = vp.worldToScreen(b);
      ctx.beginPath();
      ctx.moveTo(sa.x, sa.y);
      ctx.lineTo(sb.x, sb.y);
      ctx.stroke();
      // Small marker at the other object's centroid.
      ctx.setLineDash([]);
      ctx.beginPath();
      ctx.arc(sa.x, sa.y, 2.5, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(255, 70, 70, 0.9)';
      ctx.fill();
      ctx.setLineDash([5, 4]);
    }
    ctx.restore();
  }

  // Purple dashed lines linking a dragged text box's edge to the box it aligns to.
  if (opts.alignGuides && opts.alignGuides.length) {
    ctx.save();
    ctx.setLineDash([6, 4]);
    ctx.strokeStyle = 'rgba(168, 85, 247, 0.95)';
    ctx.lineWidth = 1;
    for (const [a, b] of opts.alignGuides) {
      const sa = vp.worldToScreen(a), sb = vp.worldToScreen(b);
      ctx.beginPath();
      ctx.moveTo(sa.x, sa.y);
      ctx.lineTo(sb.x, sb.y);
      ctx.stroke();
    }
    ctx.restore();
  }

  // Bench-tool hover: pulsing green over an area a click would add a bench, red
  // over a bench a click would delete.
  if (opts.blockedWall && opts.blockedWall.length >= 3) {
    const pulse = 0.4 + 0.35 * (1 + Math.sin(performance.now() / 180)) / 2;
    const pts = opts.blockedWall.map((p) => vp.worldToScreen(p));
    ctx.save();
    ctx.beginPath();
    pts.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
    ctx.closePath();
    ctx.fillStyle = `rgba(230, 60, 60, ${(pulse * 0.45).toFixed(3)})`;
    ctx.fill();
    ctx.lineWidth = 2;
    ctx.strokeStyle = `rgba(230, 60, 60, ${pulse.toFixed(3)})`;
    ctx.stroke();
    ctx.restore();
  }

  if (opts.benchAddHover && opts.benchAddHover.polys.length) {
    const pulse = 0.4 + 0.35 * (1 + Math.sin(performance.now() / 180)) / 2;
    const rgb = opts.benchAddHover.kind === 'delete' ? '230, 60, 60' : opts.benchAddHover.kind === 'pick' ? '74, 163, 255' : '34, 230, 80';
    ctx.save();
    ctx.lineJoin = 'round';
    for (const poly of opts.benchAddHover.polys) {
      const pts = poly.map((p) => vp.worldToScreen(p));
      if (pts.length < 3) continue;
      ctx.beginPath();
      pts.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
      ctx.closePath();
      ctx.fillStyle = `rgba(${rgb}, ${(pulse * 0.45).toFixed(3)})`;
      ctx.fill();
      ctx.lineWidth = 2;
      ctx.strokeStyle = `rgba(${rgb}, ${pulse.toFixed(3)})`;
      ctx.stroke();
    }
    ctx.restore();
  }

  // Heater guard-cover hover: blue along the gap edge a click would build a cover
  // on, red over the cover side a click would clear. Same pulsing highlight
  // language as the bench tool — a 2-point run is stroked, a band is filled.
  if (opts.heaterCoverHover && opts.heaterCoverHover.pts.length >= 2) {
    const pulse = 0.4 + 0.35 * (1 + Math.sin(performance.now() / 180)) / 2;
    const rgb = opts.heaterCoverHover.kind === 'remove' ? '230, 60, 60' : '74, 163, 255';
    const pts = opts.heaterCoverHover.pts.map((p) => vp.worldToScreen(p));
    ctx.save();
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.beginPath();
    pts.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
    if (pts.length > 2) {
      ctx.closePath();
      ctx.fillStyle = `rgba(${rgb}, ${(pulse * 0.45).toFixed(3)})`;
      ctx.fill();
    }
    ctx.lineWidth = pts.length > 2 ? 2 : 3;
    ctx.strokeStyle = `rgba(${rgb}, ${pulse.toFixed(3)})`;
    ctx.stroke();
    ctx.restore();
  }

  // Square edit handles on the selected bench (borders + vertices).
  if (opts.benchHandles && opts.benchHandles.length) {
    drawGrips(ctx, opts.benchHandles.map((p) => vp.worldToScreen(p)));
  }

  if (opts.modifyPreview && opts.modifyPreview.length >= 2) {
    ctx.save();
    ctx.strokeStyle = '#4aa3ff';
    ctx.lineWidth = 1.5;
    ctx.setLineDash([6, 4]);
    ctx.beginPath();
    opts.modifyPreview.forEach((p, i) => {
      const s = vp.worldToScreen(p);
      if (i === 0) ctx.moveTo(s.x, s.y); else ctx.lineTo(s.x, s.y);
    });
    ctx.stroke();
    ctx.restore();
  }
  // The dimension being placed, over the drawing (the placed ones paint on their
  // layer — Dimensions, on top of every other default layer). Everything below
  // this point is transient tool feedback (previews, the marquee, the snap
  // marker), which stays above them on purpose.
  if (pendingDimLine) {
    drawDimension(ctx, vp, pendingDimLine, true, dimSample);
  }
  // …and the selected object's own face dimensions, queued during its draw pass.
  // Last, so they sit above the placed ones: they are what you are editing.
  for (const draw of pass.deferredDims) draw();
  pass.deferredDims = [];

  // The OSNAP marker is cursor feedback, so it sits above the drawing content —
  // dimensions included, or a dimension already on the plan would paint over the
  // marker telling you where the next pick will land.
  if (opts.snapIndicator) {
    drawSnapMarker(ctx, vp.worldToScreen(opts.snapIndicator), opts.snapIndicatorKind, cssW, cssH);
  }
  // The vertex a move drag would grab: the same hollow endpoint glyph, but a passive
  // hover affordance, so no AutoSnap tooltip. Skipped while a live snap is showing,
  // so the two markers never stack on the same point.
  if (opts.vertexGrabHover && !opts.snapIndicator) {
    drawSnapMarker(ctx, vp.worldToScreen(opts.vertexGrabHover), 'endpoint', cssW, cssH, false);
  }

  if (opts.pendingRect) {
    const a = vp.worldToScreen(opts.pendingRect.a);
    const b = vp.worldToScreen(opts.pendingRect.b);
    ctx.save();
    ctx.setLineDash([8, 5]);
    ctx.strokeStyle = '#ffd166';
    ctx.fillStyle = 'rgba(255, 209, 102, 0.08)';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.rect(Math.min(a.x, b.x), Math.min(a.y, b.y), Math.abs(b.x - a.x), Math.abs(b.y - a.y));
    ctx.fill();
    ctx.stroke();
    ctx.restore();
  }

  for (const g of opts.grips ?? []) drawGrip(ctx, vp.worldToScreen(g.at), g.fill);
  // Hover outline: what a Select click would pick. Transient feedback, so it sits
  // over the geometry (a room paints its panes and doors late in the frame).
  if (opts.hoverOutline && opts.hoverOutline.ring.length >= 2) {
    drawHoverOutline(ctx, (p) => vp.worldToScreen(p), opts.hoverOutline.ring, opts.hoverOutline.closed);
  }

  // Rubber-band marquee: blue solid for a window, green dashed for a crossing.
  if (opts.marquee) drawMarquee(ctx, vp.worldToScreen(opts.marquee.a), vp.worldToScreen(opts.marquee.b), opts.marquee.window);

  // Combined bounding box around a 2+ multi-selection: a dashed blue frame with a
  // small inset margin so it doesn't sit exactly on the objects' edges.
  if (opts.multiSelectionBounds) {
    const mb = opts.multiSelectionBounds;
    const p0 = vp.worldToScreen({ x: mb.min.x, y: mb.max.y }); // world top-left → screen
    const p1 = vp.worldToScreen({ x: mb.max.x, y: mb.min.y });
    const pad = 6;
    const x = Math.min(p0.x, p1.x) - pad, y = Math.min(p0.y, p1.y) - pad;
    const w = Math.abs(p1.x - p0.x) + pad * 2, h = Math.abs(p1.y - p0.y) + pad * 2;
    ctx.save();
    ctx.setLineDash([6, 4]);
    ctx.strokeStyle = '#4aa3ff';
    ctx.lineWidth = 1.5;
    ctx.strokeRect(x, y, w, h);
    ctx.restore();
  }

  // Circle/ellipse tool: dashed preview + centre dot + cursor readout.
  if (opts.pendingEllipse) {
    const pe = opts.pendingEllipse;
    const c = vp.worldToScreen({ x: pe.cx, y: pe.cy });
    ctx.save();
    ctx.setLineDash([6, 4]);
    ctx.strokeStyle = '#ffd166';
    ctx.fillStyle = 'rgba(255, 255, 255, 0.10)';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.ellipse(c.x, c.y, pe.rx * vp.scale, pe.ry * vp.scale, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = '#ffd166';
    ctx.beginPath();
    ctx.arc(c.x, c.y, 3, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
    drawCursorReadout(ctx, (q) => vp.worldToScreen(q), pe.hover, pe.center);
  }

  // Polygon tool: the in-progress polygon (always shown closed).
  if (opts.pendingPolygon && (opts.pendingPolygon.pts.length || opts.pendingPolygon.hover)) {
    drawPendingPolygon(ctx, vp, opts.pendingPolygon.pts, opts.pendingPolygon.hover, opts.pendingPolygon.snapVerts);
  }
  // Flooring tool: same polyline preview as the polygon tool.
  if (opts.pendingFloor && (opts.pendingFloor.pts.length || opts.pendingFloor.hover)) {
    drawPendingPolygon(ctx, vp, opts.pendingFloor.pts, opts.pendingFloor.hover, opts.pendingFloor.snapVerts);
  }
  // Temporary drain dimensions: a light line to each floor edge with its length.
  for (const d of opts.floorDrainDims ?? []) drawTempFigure(ctx, vp.worldToScreen(d.from), vp.worldToScreen(d.to), d.len, false);
  if (opts.spotAid) pass.hits.spotFigures.push(...paintSpotAid(ctx, (p) => vp.worldToScreen(p), opts.spotAid, pass.dimStyle).figures);
  if (opts.gripFigure) drawTempFigure(ctx, vp.worldToScreen(opts.gripFigure.a), vp.worldToScreen(opts.gripFigure.b), opts.gripFigure.len, true);
  // A bench support's figures, to the trestle or bench end on either side.
  (opts.supportSpacings ?? []).forEach((d, i) => {
    pass.hits.supportFigures.push({ ...drawTempFigure(ctx, vp.worldToScreen(d.a), vp.worldToScreen(d.b), d.length, true), i });
  });

  // Wall tool: the in-progress wall band.
  if (opts.pendingWall && opts.pendingWall.pts.length) {
    drawPendingWall(ctx, vp, opts.pendingWall.pts, opts.pendingWall.hover);
    drawOrthoGuides(ctx, vp, opts.pendingWall.hover, [...opts.pendingWall.pts, ...(opts.snapVerts ?? [])]);
  }

  // Line tool: the in-progress polyline.
  if (opts.pendingLine && opts.pendingLine.pts.length) {
    drawPendingLine(ctx, (q) => vp.worldToScreen(q), opts.pendingLine.pts, opts.pendingLine.hover);
    drawOrthoGuides(ctx, vp, opts.pendingLine.hover, [...opts.pendingLine.pts, ...(opts.snapVerts ?? [])]);
  }

  // Section tool: H/V/45 alignment guides while placing the cut line's points.
  if (opts.pendingSectionGuide) {
    drawOrthoGuides(ctx, vp, opts.pendingSectionGuide.hover, opts.pendingSectionGuide.verts);
  }
  // Same-kind alignment (section-to-section, dimension-to-dimension): purple, so
  // lining a draft up with its siblings is never confused with the ordinary
  // geometry guides.
  if (opts.alignGuide) {
    drawOrthoGuides(ctx, vp, opts.alignGuide.hover, opts.alignGuide.verts, ALIGN_GUIDE_COLOR, true);
  }
  // A moved underlay (image/PDF/DXF) aligning a corner to another object's vertex —
  // the same purple, with a marker on the target vertex.
  if (opts.moveAlignGuides && opts.moveAlignGuides.length) {
    ctx.save();
    ctx.setLineDash([4, 4]);
    ctx.strokeStyle = ALIGN_GUIDE_COLOR;
    ctx.fillStyle = ALIGN_GUIDE_COLOR;
    ctx.lineWidth = 1;
    for (const { from, to } of opts.moveAlignGuides) {
      const a = vp.worldToScreen(from), b = vp.worldToScreen(to);
      ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();
      ctx.fillRect(b.x - 2.5, b.y - 2.5, 5, 5); // the vertex the guide locked onto
    }
    ctx.restore();
  }

  // Repeat tool: the baseline polyline plus the tiled product copies.
  if (opts.pendingRepeat && opts.pendingRepeat.pts.length) {
    drawPendingRepeat(ctx, vp, opts.pendingRepeat);
  }

  // Bench-poly tool: preview heatedbench/saltwall polyline being drawn.
  if (opts.pendingBenchPoly && opts.pendingBenchPoly.pts.length) {
    const { pts, hover, depth, category, valid, slats, gaps, color } = opts.pendingBenchPoly;
    const rejected = valid === false; // hover vertex too close to an existing one
    // A rejected hover isn't extruded into the bench body — only the committed
    // segments are drawn, plus a red rubber line + ✕ marking the invalid target.
    const allPts = hover && !rejected ? [...pts, hover] : pts;
    ctx.save();
    for (let i = 0; i < allPts.length - 1; i++) {
      const prev = i > 0 ? allPts[i - 1] : null, nxt = i + 2 < allPts.length ? allPts[i + 2] : null;
      if (category === 'saltwall') {
        drawSaltWall(ctx, vp, allPts[i], allPts[i + 1], false, i === 0, i === allPts.length - 2, prev, nxt);
      } else if (category === 'saunabench') {
        const seg = saunaBenchSegment(allPts[i], allPts[i + 1], depth, slats?.length ? benchSlatSpans({ slats, gaps: gaps ?? [] }) : [], prev, nxt);
        if (seg) drawSaunaBench(ctx, (p) => vp.worldToScreen(p), seg, color ?? '#c9a36a', false);
      } else {
        drawHeatedBench(ctx, vp, allPts[i], allPts[i + 1], depth, false, prev, nxt);
      }
    }
    // Draw placed vertex dots and rubber line to cursor. When the hovered vertex
    // is too close to an existing one, the rubber line + hover marker turn red.
    ctx.strokeStyle = '#ffd166';
    ctx.fillStyle = '#ffd166';
    for (const p of pts) {
      const sp = vp.worldToScreen(p);
      ctx.beginPath();
      ctx.arc(sp.x, sp.y, 4, 0, Math.PI * 2);
      ctx.fill();
    }
    if (pts.length >= 1 && hover) {
      const sp0 = vp.worldToScreen(pts[pts.length - 1]);
      const sp1 = vp.worldToScreen(hover);
      ctx.strokeStyle = rejected ? '#ff5a3a' : '#ffd166';
      ctx.setLineDash([5, 4]);
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(sp0.x, sp0.y);
      ctx.lineTo(sp1.x, sp1.y);
      ctx.stroke();
      ctx.setLineDash([]);
      // A red ✕ marks a rejected hover point.
      if (rejected) {
        ctx.strokeStyle = '#ff5a3a';
        ctx.lineWidth = 2;
        const r = 6;
        ctx.beginPath();
        ctx.moveTo(sp1.x - r, sp1.y - r); ctx.lineTo(sp1.x + r, sp1.y + r);
        ctx.moveTo(sp1.x + r, sp1.y - r); ctx.lineTo(sp1.x - r, sp1.y + r);
        ctx.stroke();
      }
    }
    ctx.restore();
  }


  // PDF-scale tool: the picked points and a rubber line between them.
  if (opts.pendingScale && opts.pendingScale.pts.length) {
    const sp = opts.pendingScale.pts.map((p) => vp.worldToScreen(p));
    const end = opts.pendingScale.pts.length >= 2 ? sp[1] : opts.pendingScale.hover ? vp.worldToScreen(opts.pendingScale.hover) : null;
    ctx.save();
    ctx.strokeStyle = '#ff7ad9';
    ctx.fillStyle = '#ff7ad9';
    ctx.lineWidth = 1.5;
    if (end) {
      ctx.setLineDash([6, 4]);
      ctx.beginPath();
      ctx.moveTo(sp[0].x, sp[0].y);
      ctx.lineTo(end.x, end.y);
      ctx.stroke();
      ctx.setLineDash([]);
    }
    for (const p of [sp[0], ...(opts.pendingScale.pts.length >= 2 ? [sp[1]] : [])]) {
      ctx.beginPath();
      ctx.arc(p.x, p.y, 4, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }

  // Measure tool: a dashed rubber-band line between the two picked points (or the
  // first point and the cursor), with a distance label at the midpoint.
  if (opts.pendingRubber?.b) {
    const a = vp.worldToScreen(opts.pendingRubber.a), b = vp.worldToScreen(opts.pendingRubber.b);
    ctx.save();
    ctx.strokeStyle = '#ffd166';
    ctx.lineWidth = 1.2;
    ctx.setLineDash([6, 4]);
    ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = '#ffd166';
    ctx.beginPath(); ctx.arc(a.x, a.y, 3, 0, Math.PI * 2); ctx.fill();
    ctx.restore();
  }
  // Mirror line: a dash-dot axis through both points, run out across the view
  // (the mirror is about the whole line, not just the stretch between the clicks).
  if (opts.pendingMirror?.b) {
    const a = vp.worldToScreen(opts.pendingMirror.a), b = vp.worldToScreen(opts.pendingMirror.b);
    const dx = b.x - a.x, dy = b.y - a.y, len = Math.hypot(dx, dy);
    if (len > 1) {
      const k = 4000 / len;
      ctx.save();
      ctx.strokeStyle = '#ffd166';
      ctx.lineWidth = 1.2;
      ctx.setLineDash([14, 4, 2, 4]);
      ctx.beginPath();
      ctx.moveTo(a.x - dx * k, a.y - dy * k);
      ctx.lineTo(b.x + dx * k, b.y + dy * k);
      ctx.stroke();
      ctx.restore();
    }
  }
  if (opts.constraintHighlight?.length) for (const pts of opts.constraintHighlight) drawConstraintPieceMark(ctx, vp, pts, CONSTRAINT_ON_COLOR);
  if (opts.constraintBadges?.length) drawObjConstraintBadges(ctx, opts.constraintBadges);
  if (opts.constraintPick) drawConstraintPick(ctx, vp, opts.constraintPick);
  if (opts.pendingMeasure && opts.pendingMeasure.pts.length) {
    const pm = opts.pendingMeasure;
    const a = vp.worldToScreen(pm.pts[0]);
    const bw = pm.pts.length >= 2 ? pm.pts[1] : pm.hover;
    const b = bw ? vp.worldToScreen(bw) : null;
    ctx.save();
    ctx.strokeStyle = '#ffd166';
    ctx.fillStyle = '#ffd166';
    ctx.lineWidth = 1.5;
    if (b) {
      ctx.setLineDash([6, 4]);
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      ctx.stroke();
      ctx.setLineDash([]);
      const wa = pm.pts[0], wb = bw!;
      const dist = Math.round(Math.hypot(wb.x - wa.x, wb.y - wa.y));
      const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2;
      ctx.font = 'bold 12px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'bottom';
      const label = formatDim(dist);
      const w = ctx.measureText(label).width;
      ctx.fillStyle = 'rgba(0,0,0,0.6)';
      ctx.fillRect(mx - w / 2 - 4, my - 20, w + 8, 16);
      ctx.fillStyle = '#ffd166';
      ctx.fillText(label, mx, my - 5);
    }
    for (const p of [a, ...(b && pm.pts.length >= 2 ? [b] : [])]) {
      ctx.beginPath();
      ctx.arc(p.x, p.y, 4, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }

  // Polar-array centre marker: a crosshair at the picked rotation pivot.
  if (opts.arrayCenter) {
    const c = vp.worldToScreen(opts.arrayCenter);
    ctx.save();
    ctx.strokeStyle = '#ffd166';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.arc(c.x, c.y, 7, 0, Math.PI * 2);
    ctx.moveTo(c.x - 11, c.y); ctx.lineTo(c.x + 11, c.y);
    ctx.moveTo(c.x, c.y - 11); ctx.lineTo(c.x, c.y + 11);
    ctx.stroke();
    ctx.restore();
  }

  // Drawing editor: the canvas framed in the banner's colour (the banner itself
  // is HTML over the canvas, #edit-banner), and the session's own figures.
  if (opts.drawingEditId) {
    const cw = ctx.canvas.clientWidth || ctx.canvas.width, ch = ctx.canvas.clientHeight || ctx.canvas.height;
    const dims = opts.drawingEditDims ?? [];
    if (dims.length) {
      const all = dims.flatMap((d) => [d.a, d.b]);
      const c = { x: all.reduce((t, p) => t + p.x, 0) / all.length, y: all.reduce((t, p) => t + p.y, 0) / all.length };
      drawDimSegments(ctx, vp, dims, c, true);
    }
    ctx.save();
    ctx.strokeStyle = EDITOR_ACCENT;
    ctx.lineWidth = EDITOR_FRAME;
    ctx.strokeRect(EDITOR_FRAME / 2, EDITOR_FRAME / 2, cw - EDITOR_FRAME, ch - EDITOR_FRAME);
    ctx.restore();
  }

  ctx.restore();
}


// ---- In-drawing schedule tables ---------------------------------------------
// Bordered grids placed in the drawing (world-anchored top-left, world width,
// fixed-pixel text like text comments). The connection table has columns
// No. / From / To / Description; the preparation table drops the destination:
// No. / Part / Description.

// Reference proportions only. The actual on-screen size is derived from the
// table's text height in world mm (SCHED_FONT_MM), so the whole table scales with
// the drawing and prints to scale — these ratios preserve its default look.
const STAB_FONT = 13;         // reference cell-text size the ratios below are against
const STAB_LINE = 18;         // line height
const STAB_PADX = 8;          // horizontal cell padding
const STAB_PADY = 6;          // vertical cell padding
const STAB_NO_W = 46;         // width of the No. column
const STAB_MIN_W = 220;       // minimum table width
/** Default text height (world mm) for annotation text — the model text box and the
 *  schedule tables share it, so they read as one size. 10 pt at 1:20 ≈ 70 mm. */
export const COMMENT_FONT_MM = 70;
const SCHED_FONT_MM = COMMENT_FONT_MM;

export type SchedTableKind =
  | 'connection'
  | 'flooring'
  | 'lighting'
  | 'pipe'
  | 'preparation';
/** A light's tag in the Lighting table: the light's amber, deep enough to read on white. */
const TAG_INK = '#a86b00';

/** What a schedule-table text cell edits when double-clicked. */
export type SchedCellEdit =
  | { kind: 'partName'; id: string }
  | { kind: 'connLabel'; id: string }
  | { kind: 'prepLabel'; id: string }
  | { kind: 'none' };

export interface SchedTableCell { text: string; lines: string[]; edit: SchedCellEdit; }
export interface SchedTableRow {
  id: string;                  // connection or preparation id
  num: string;
  type: ConnectionType;
  swatchKind?: FloorKind;      // flooring rows: draw a hatch swatch in the No. cell
  symbol?: LightSymbol;        // lighting rows: draw the light's plan symbol in the first text cell
  numColor?: string;           // the No. cell's colour when not its discipline's (a light's tag)
  cells: SchedTableCell[];     // the editable text columns (excludes No.)
  y: number;                   // px, top of the row relative to the table origin
  h: number;                   // px, row height
}
export interface SchedTableLayout {
  kind: SchedTableKind;
  title: string;
  headers: string[];           // column headers incl. No. (length = colX.length - 1)
  originX: number; originY: number; // screen px, top-left
  widthPx: number;
  /** Absolute screen x of every column boundary plus the right edge. */
  colX: number[];
  titleY: number; headerY: number; bodyY: number;
  rows: SchedTableRow[];
  totalH: number; // px
  /** Screen-px text metrics for this table, derived from its mm text height. */
  fontPx: number; line: number; padX: number; padY: number; header: number;
}

/** Lay out a schedule table at the current zoom, wrapping each cell's text.
 *  Returns null when there are no rows. `ctx` is only used to measure text (its
 *  font state is restored). */
/** Each kind's table layout as last made: made again when the drawing's revision,
 *  the table's width or text height, or the zoom changed (it measures every
 *  cell's text); a pan, or the table moved, only shifts it. */
const TABLE_LAYOUTS = new Map<SchedTableKind, { drawing: Drawing; key: string; layout: SchedTableLayout | null }>();

/** A schedule table's layout on screen — from the one kept for its kind when the
 *  caller passes the drawing's `revision` (changeTracker.ts). Shared: never
 *  mutate it. */
export function scheduleTableLayout(
  ctx: CanvasRenderingContext2D,
  vp: Viewport,
  table: { x: number; y: number; width: number; heightMm?: number },
  drawing: Drawing,
  kind: SchedTableKind,
  revision?: number,
): SchedTableLayout | null {
  const key = revision == null ? null : `${revision}|${table.width}|${table.heightMm ?? ''}|${vp.scale}`;
  const kept = TABLE_LAYOUTS.get(kind);
  if (key !== null && kept?.drawing === drawing && kept.key === key) {
    const L = kept.layout, o = vp.worldToScreen({ x: table.x, y: table.y });
    if (!L || (L.originX === o.x && L.originY === o.y)) return L;
    const dx = o.x - L.originX, dy = o.y - L.originY;
    return { ...L, originX: o.x, originY: o.y, colX: L.colX.map((x) => x + dx), titleY: L.titleY + dy, headerY: L.headerY + dy, bodyY: L.bodyY + dy };
  }
  const layout = tableLayoutOf(ctx, vp, table, drawing, kind);
  if (key !== null) TABLE_LAYOUTS.set(kind, { drawing, key, layout });
  return layout;
}

function tableLayoutOf(
  ctx: CanvasRenderingContext2D,
  vp: Viewport,
  table: { x: number; y: number; width: number; heightMm?: number },
  drawing: Drawing,
  kind: SchedTableKind
): SchedTableLayout | null {
  const lang = drawing.language ?? 'it';
  const s = scheduleStrings(lang);
  // Build each row's text columns + their edit targets, and the column fractions.
  let source: Array<{ id: string; num: string; type: ConnectionType; swatchKind?: FloorKind; symbol?: LightSymbol; numColor?: string; cols: Array<{ text: string; edit: SchedCellEdit }> }>;
  let fractions: number[];
  let headers: string[];
  let title: string;
  if (kind === 'connection') {
    source = connectionRows(drawing).map((r) => ({
      id: r.id, num: r.num, type: r.type,
      cols: [
        { text: r.startName, edit: { kind: 'partName', id: r.startId } as SchedCellEdit },
        { text: r.endName, edit: { kind: 'partName', id: r.endId } as SchedCellEdit },
        { text: r.lengthText, edit: { kind: 'none' } as SchedCellEdit },
        { text: r.installedByText, edit: { kind: 'none' } as SchedCellEdit },
        { text: r.description, edit: { kind: 'connLabel', id: r.id } as SchedCellEdit },
      ],
    }));
    fractions = [0.22, 0.22, 0.11, 0.14, 0.31];
    headers = [s.no, s.startPart, s.endPart, s.lengthCol, s.installedByCol, s.description];
    title = s.connectionTitle;
  } else if (kind === 'preparation') {
    source = preparationRows(drawing).map((r) => ({
      id: r.id, num: r.num, type: r.type,
      cols: [
        { text: r.partName, edit: { kind: 'partName', id: r.partId } as SchedCellEdit },
        { text: r.description, edit: { kind: 'prepLabel', id: r.id } as SchedCellEdit },
      ],
    }));
    fractions = [0.40, 0.60];
    headers = [s.no, s.partCol, s.description];
    title = s.preparationTitle;
  } else if (kind === 'lighting') {
    // One row per tag in each room, then the total power.
    const rows = lightingRows(drawing), cell = (text: string) => ({ text, edit: { kind: 'none' } as SchedCellEdit });
    source = rows.map((r) => ({ id: r.id, num: r.tag, type: 'electrical' as ConnectionType, numColor: TAG_INK, symbol: r.symbol, cols: [cell(''), cell(r.light), cell(r.room), cell(r.qty), cell(r.power)] }));
    if (rows.length) source.push({ id: 'total', num: '', type: 'electrical', cols: [cell(''), cell(s.total), cell(''), cell(''), cell(`${Math.round(rows.reduce((m, r) => m + r.watts, 0))} W`)] });
    fractions = [0.12, 0.46, 0.16, 0.13, 0.13];
    headers = [s.tagCol, s.symbolCol, s.lightCol, s.roomCol, s.qtyCol, s.powerCol];
    title = s.lightingTitle;
  } else if (kind === 'pipe') {
    // Per pipe type: its straight pipe's length, then its bends by angle.
    source = pipeRows(drawing).map((r) => ({ id: r.id, num: '', type: r.type, cols: pipeRowCells(r).map((text) => ({ text, edit: { kind: 'none' } as SchedCellEdit })) }));
    fractions = [0.5, 0.3, 0.2];
    headers = ['', s.pipeCol, s.itemCol, s.qtyCol];
    title = s.pipeTitle;
  } else {
    source = flooringRows(drawing).map((r) => ({
      id: r.id, num: '', type: 'drainage' as ConnectionType, swatchKind: r.kind,
      cols: [
        { text: r.typeLabel, edit: { kind: 'none' } as SchedCellEdit },
        { text: r.area, edit: { kind: 'none' } as SchedCellEdit },
        { text: r.drain, edit: { kind: 'none' } as SchedCellEdit },
      ],
    }));
    fractions = [0.44, 0.26, 0.30];
    headers = ['', s.typeCol, s.areaCol, s.drainCol]; // the first column is the hatch swatch
    title = s.flooringTitle;
  }
  if (source.length === 0) return null;

  // Every metric scales from the table's mm text height, so the table prints to
  // scale; the STAB_* ratios keep the default look.
  const fontPx = (table.heightMm ?? SCHED_FONT_MM) * vp.scale;
  const line = fontPx * (STAB_LINE / STAB_FONT);
  const padX = fontPx * (STAB_PADX / STAB_FONT);
  const padY = fontPx * (STAB_PADY / STAB_FONT);
  const header = line + padY * 2;
  const noW = fontPx * (STAB_NO_W / STAB_FONT);
  const minW = fontPx * (STAB_MIN_W / STAB_FONT);

  const origin = vp.worldToScreen({ x: table.x, y: table.y });
  const widthPx = Math.max(minW, table.width * vp.scale);
  const rest = widthPx - noW;
  const colW = [noW, ...fractions.map((f) => rest * f)];
  const colX: number[] = [origin.x];
  for (const w of colW) colX.push(colX[colX.length - 1] + w);
  colX[colX.length - 1] = origin.x + widthPx; // pin the right edge exactly

  const prevFont = ctx.font;
  ctx.font = `${fontPx}px sans-serif`;
  const titleY = origin.y;
  const headerY = titleY + header;
  const bodyY = headerY + header;
  let y = bodyY;
  const out: SchedTableRow[] = [];
  for (const r of source) {
    const cells: SchedTableCell[] = r.cols.map((c, i) => {
      const lines = wrapText(ctx, c.text, colW[i + 1] - padX * 2);
      return { text: c.text, lines: lines.length ? lines : [''], edit: c.edit };
    });
    const maxLines = Math.max(1, ...cells.map((c) => c.lines.length));
    const h = maxLines * line + padY * 2;
    out.push({ id: r.id, num: r.num, type: r.type, swatchKind: r.swatchKind, ...(r.symbol ? { symbol: r.symbol } : {}), ...(r.numColor ? { numColor: r.numColor } : {}), cells, y: y - origin.y, h });
    y += h;
  }
  ctx.font = prevFont;
  return { kind, title, headers, originX: origin.x, originY: origin.y, widthPx, colX, titleY, headerY, bodyY, rows: out, totalH: y - origin.y, fontPx, line, padX, padY, header };
}

/** Draw a schedule table (white grid, discipline-coloured numbers). */
function drawScheduleTable(
  ctx: CanvasRenderingContext2D,
  vp: Viewport,
  table: { x: number; y: number; width: number; heightMm?: number },
  drawing: Drawing,
  kind: SchedTableKind,
  selected: boolean
): void {
  const L = scheduleTableLayout(ctx, vp, table, drawing, kind, pass.revision);
  if (!L) return;
  const nCols = L.colX.length - 1; // total columns incl. No.
  const x0 = L.colX[0], x1 = L.colX[nCols];
  ctx.save();
  ctx.textBaseline = 'top';

  // Body background.
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(x0, L.originY, L.widthPx, L.totalH);

  // Title bar.
  ctx.fillStyle = '#2b303b';
  ctx.fillRect(x0, L.titleY, L.widthPx, L.header);
  ctx.fillStyle = '#ffffff';
  ctx.font = `bold ${L.fontPx}px sans-serif`;
  ctx.textAlign = 'center';
  ctx.fillText(L.title, (x0 + x1) / 2, L.titleY + L.padY);

  // Column-header row.
  ctx.fillStyle = '#eef1f5';
  ctx.fillRect(x0, L.headerY, L.widthPx, L.header);
  ctx.fillStyle = '#333a45';
  ctx.font = `bold ${L.fontPx}px sans-serif`;
  for (let i = 0; i < nCols; i++) {
    const cx = i === 0 ? (L.colX[0] + L.colX[1]) / 2 : L.colX[i] + L.padX;
    ctx.textAlign = i === 0 ? 'center' : 'left';
    ctx.fillText(L.headers[i], cx, L.headerY + L.padY);
  }

  // Data rows.
  for (const row of L.rows) {
    const ry = L.originY + row.y;
    // First cell: a hatch swatch (flooring — the type's own hatch, since a floor
    // carries no code in the drawing) or the discipline-coloured number.
    if (row.swatchKind) {
      drawFloorSwatch(ctx, L.colX[0], ry, L.colX[1] - L.colX[0], row.h, row.swatchKind);
    } else {
      ctx.fillStyle = row.numColor ?? CONNECTION_COLORS[row.type];
      ctx.font = `bold ${L.fontPx}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.fillText(row.num, (L.colX[0] + L.colX[1]) / 2, ry + L.padY);
    }
    // Text cells.
    ctx.fillStyle = '#1a1d24';
    ctx.font = `${L.fontPx}px sans-serif`;
    ctx.textAlign = 'left';
    row.cells.forEach((cell, i) => {
      let ly = ry + L.padY;
      for (const line of cell.lines) {
        ctx.fillText(line, L.colX[i + 1] + L.padX, ly);
        ly += L.line;
      }
    });
    if (row.symbol) {
      const w = L.colX[2] - L.colX[1];
      paintLightSymbol(ctx, row.symbol, { x: L.colX[1] + w / 2, y: ry + row.h / 2 }, w * 0.8, Math.min(row.h - L.padY, L.line * 1.3), TAG_INK);
    }
  }

  // Grid lines.
  ctx.strokeStyle = '#9aa3b0';
  ctx.lineWidth = 1;
  ctx.beginPath();
  const hys = [L.headerY, L.bodyY, ...L.rows.map((r) => L.originY + r.y + r.h)];
  for (const hy of hys) { ctx.moveTo(x0, Math.round(hy) + 0.5); ctx.lineTo(x1, Math.round(hy) + 0.5); }
  for (let i = 1; i < nCols; i++) { ctx.moveTo(Math.round(L.colX[i]) + 0.5, L.headerY); ctx.lineTo(Math.round(L.colX[i]) + 0.5, L.originY + L.totalH); }
  ctx.stroke();

  // Outer border.
  ctx.strokeStyle = selected ? '#ffd166' : '#5a6270';
  ctx.lineWidth = selected ? 2 : 1.25;
  ctx.strokeRect(x0 + 0.5, L.originY + 0.5, L.widthPx - 1, L.totalH - 1);
  ctx.restore();

  if (selected) {
    drawGrips(ctx, scheduleTableHandles(vp, table, L.totalH).map((h) => vp.worldToScreen(h.world)));
  }
}

/** The two width-resize handles (left & right edge midpoints) of the table, in
 *  world space. Height is content-derived, so there are no vertical handles. */
export function scheduleTableHandles(vp: Viewport, table: { x: number; y: number; width: number }, totalHpx: number): Handle[] {
  const hWorld = totalHpx / vp.scale;
  return [
    { sx: -1, sy: 0, world: { x: table.x, y: table.y - hWorld / 2 } },
    { sx: 1, sy: 0, world: { x: table.x + table.width, y: table.y - hWorld / 2 } },
  ];
}


function drawGrid(ctx: CanvasRenderingContext2D, vp: Viewport, w: number, h: number): void {
  // Draw at the current editing granularity (the same step dragging snaps to),
  // so the grid visualises how coarse/fine placement is at this zoom.
  const stepMm = gridStepMm(vp.scale);
  const stepPx = stepMm * vp.scale;
  if (stepPx < 6) return;

  const topLeft = vp.screenToWorld({ x: 0, y: 0 });
  const bottomRight = vp.screenToWorld({ x: w, y: h });
  const startX = Math.floor(topLeft.x / stepMm) * stepMm;
  const endX = Math.ceil(bottomRight.x / stepMm) * stepMm;
  const startY = Math.floor(bottomRight.y / stepMm) * stepMm;
  const endY = Math.ceil(topLeft.y / stepMm) * stepMm;

  ctx.strokeStyle = GRID_COLOR();
  ctx.lineWidth = 1;
  ctx.beginPath();
  for (let x = startX; x <= endX; x += stepMm) {
    const s = vp.worldToScreen({ x, y: 0 });
    ctx.moveTo(Math.round(s.x) + 0.5, 0);
    ctx.lineTo(Math.round(s.x) + 0.5, h);
  }
  for (let y = startY; y <= endY; y += stepMm) {
    const s = vp.worldToScreen({ x: 0, y });
    ctx.moveTo(0, Math.round(s.y) + 0.5);
    ctx.lineTo(w, Math.round(s.y) + 0.5);
  }
  ctx.stroke();
}

function drawAxes(ctx: CanvasRenderingContext2D, vp: Viewport, w: number, h: number): void {
  const origin = vp.worldToScreen({ x: 0, y: 0 });
  ctx.strokeStyle = AXIS_COLOR();
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(0, Math.round(origin.y) + 0.5);
  ctx.lineTo(w, Math.round(origin.y) + 0.5);
  ctx.moveTo(Math.round(origin.x) + 0.5, 0);
  ctx.lineTo(Math.round(origin.x) + 0.5, h);
  ctx.stroke();
}


/**
 * Render a sauna's interior in the filled style: the bench tiers as graduated
 * bands (darkest against the wall, lightening towards the centre) and the heater
 * as a solid dark box with its stones.
 */


/** What stands round a part's body (partLook.ts), drawn behind it in its local
 *  frame so it turns with it: its guard cover's bands filled as one union
 *  (seamless corners) with a boundary-only outline; its clearance gap an
 *  unfilled rectangle the size of the body plus the gap. */
function drawAttachment(ctx: CanvasRenderingContext2D, vp: Viewport, inst: ProductInstance, a: PartAttachment, selected: boolean): void {
  switch (a.kind) {
    case 'cover': {
      const rings = a.bands.map((b) => [{ x: b.x0, y: b.y0 }, { x: b.x1, y: b.y0 }, { x: b.x1, y: b.y1 }, { x: b.x0, y: b.y1 }]);
      ctx.beginPath();
      for (const ring of rings) {
        const pts = ring.map((p) => vp.worldToScreen(instanceToWorld(inst, p)));
        pts.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
        ctx.closePath();
      }
      ctx.fillStyle = a.color; // opaque wood; overlaps at corners union cleanly
      ctx.fill();
      ctx.beginPath();
      for (const [p, q] of wallBoundaryEdges(rings)) {
        const sa = vp.worldToScreen(instanceToWorld(inst, p));
        const sb = vp.worldToScreen(instanceToWorld(inst, q));
        ctx.moveTo(sa.x, sa.y);
        ctx.lineTo(sb.x, sb.y);
      }
      ctx.lineWidth = 1;
      ctx.lineCap = 'square';
      ctx.lineJoin = 'miter';
      ctx.strokeStyle = darken(a.color, 0.5);
      ctx.stroke();
      return;
    }
    case 'gap': strokeLocalBox(ctx, vp, inst, a.box, selected ? '#ffd166' : 'rgba(120,130,150,0.9)', 1); return;
    default: unreachable(a, 'attachment');
  }
}

/** A box in a part's local frame, stroked (and filled). */
function strokeLocalBox(ctx: CanvasRenderingContext2D, vp: Viewport, inst: ProductInstance, box: LocalBox, stroke: string, w: number, fill?: string): void {
  const ring = [{ x: box.x0, y: box.y0 }, { x: box.x1, y: box.y0 }, { x: box.x1, y: box.y1 }, { x: box.x0, y: box.y1 }];
  const pts = ring.map((p) => vp.worldToScreen(instanceToWorld(inst, p)));
  ctx.beginPath();
  pts.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
  ctx.closePath();
  if (fill) { ctx.fillStyle = fill; ctx.fill(); }
  ctx.lineWidth = w;
  ctx.lineJoin = 'miter';
  ctx.strokeStyle = stroke;
  ctx.stroke();
}

/** A heater block's body: its stored 3D model seen from above, or a solid fill
 *  in the product colour with a black outline. Its gap and cover are its
 *  attachments, drawn behind. */
function drawHeaterBody(ctx: CanvasRenderingContext2D, vp: Viewport, inst: ProductInstance, selected: boolean): void {
  const hw = inst.width / 2, hd = inst.depth / 2;
  const model = uprightModelPlan(inst);
  if (model.length) {
    ctx.save();
    ctx.lineJoin = 'miter';
    drawModelFromAbove(ctx, vp, model);
    ctx.restore();
    if (selected) strokeLocalBox(ctx, vp, inst, { x0: -hw, y0: -hd, x1: hw, y1: hd }, '#ffd166', 2.5);
    return;
  }
  strokeLocalBox(ctx, vp, inst, { x0: -hw, y0: -hd, x1: hw, y1: hd }, selected ? '#ffd166' : '#000000', selected ? 2.5 : 1.2, inst.color || HEATER_BODY_COLOR);
}





/**
 * Draw a standalone steam-cube product instance as a speckled dark stone block.
 * Unlike the legacy in-bench feature cube, this sits on top of whatever is under
 * it (benches, floor) and never carves the benches it overlaps.
 */
function drawCubeInstance(ctx: CanvasRenderingContext2D, vp: Viewport, inst: ProductInstance, selected: boolean): void {
  const S = (p: Vec2): Vec2 => vp.worldToScreen(instanceToWorld(inst, p));
  const hw = inst.width / 2, hd = inst.depth / 2;
  const poly = (pts: Vec2[]): void => {
    ctx.beginPath();
    pts.forEach((c, i) => (i === 0 ? ctx.moveTo(c.x, c.y) : ctx.lineTo(c.x, c.y)));
    ctx.closePath();
  };
  const rect = (x0: number, y0: number, x1: number, y1: number): Vec2[] =>
    [{ x: x0, y: y0 }, { x: x1, y: y0 }, { x: x1, y: y1 }, { x: x0, y: y1 }].map(S);

  ctx.save();
  // Stone body (its 50 mm walls read as the frame around the depression).
  poly(rect(-hw, -hd, hw, hd));
  ctx.fillStyle = STEAM_CUBE.stone;
  ctx.fill();

  // Central depression, lined with a steel grille.
  const w = STEAM_CUBE.wall;
  const ix0 = -hw + w, ix1 = hw - w, iy0 = -hd + w, iy1 = hd - w;
  if (ix1 - ix0 > 1 && iy1 - iy0 > 1) {
    poly(rect(ix0, iy0, ix1, iy1));
    ctx.save();
    ctx.clip();
    ctx.fillStyle = '#191a1d'; // recessed base, seen through the grille gaps
    ctx.fill();
    // Steel slats (parallel bars along local X, stacked in Y).
    if ((iy1 - iy0) * vp.scale >= 16) {
      const nBars = Math.max(3, Math.min(18, Math.round((iy1 - iy0) / 45)));
      const pitch = (iy1 - iy0) / nBars;
      for (let k = 0; k < nBars; k++) {
        const y = iy0 + k * pitch;
        poly(rect(ix0, y, ix1, y + pitch * 0.62));
        ctx.fillStyle = STEAM_CUBE.steel;
        ctx.fill();
        const a = S({ x: ix0, y }), b = S({ x: ix1, y });
        ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y);
        ctx.strokeStyle = 'rgba(255,255,255,0.28)'; ctx.lineWidth = 1; ctx.stroke();
      }
    }
    ctx.restore();
    // Recess edge.
    poly(rect(ix0, iy0, ix1, iy1));
    ctx.strokeStyle = '#2a2b2f'; ctx.lineWidth = 1; ctx.stroke();
  }

  // Outer outline.
  poly(rect(-hw, -hd, hw, hd));
  ctx.lineWidth = selected ? 2.5 : 1.2;
  ctx.strokeStyle = selected ? '#ffd166' : '#5a5c64';
  ctx.stroke();
  ctx.restore();
}

// Hot tub plan tones. The water is shaded by depth — lightest over the seats,
// darkest in the footwell — so the shell's contours read without a section.
const HOT_TUB_DECK = '#f2f4f5';
const HOT_TUB_COPING = '#9aa3a8';
// A moulded acrylic rim (the Lodge+ tubs) is the deck's own white, a shade down
// so the band still reads against it.
const HOT_TUB_ACRYLIC_RIM = '#e2e8ea';
const HOT_TUB_WATER = '#c3eaf3';
const HOT_TUB_SEAT = '#e0f4f8';
const HOT_TUB_DEEP = '#9fdcea';
const HOT_TUB_LINE = 'rgba(40, 120, 145, 0.7)';

/** A hot tub in plan, from `hotTubLayout` — the same layout the 3D preview
 *  extrudes, so plan and model cannot disagree. Painted back to front, each layer
 *  inside the last: deck, coping, water, seats and platforms, footwell. */
function drawHotTub(ctx: CanvasRenderingContext2D, vp: Viewport, inst: ProductInstance, selected: boolean): void {
  const L = hotTubLayout(inst.width, inst.depth, objectHeight(inst), inst.hotTubModel);
  const S = (p: Vec2): Vec2 => vp.worldToScreen(instanceToWorld(inst, p));
  const path = (ring: Vec2[]): void => {
    ctx.beginPath();
    ring.forEach((p, i) => { const q = S(p); if (i === 0) ctx.moveTo(q.x, q.y); else ctx.lineTo(q.x, q.y); });
    ctx.closePath();
  };
  const layer = (ring: Vec2[], fill: string, stroke: string, width = 1): void => {
    path(ring);
    ctx.fillStyle = fill; ctx.fill();
    ctx.strokeStyle = stroke; ctx.lineWidth = width; ctx.stroke();
  };

  ctx.save();
  layer(L.outline, HOT_TUB_DECK, selected ? '#ffd166' : inst.color, selected ? 2.5 : 1.5);
  layer(L.rim, L.coping === 'mosaic' ? HOT_TUB_COPING : HOT_TUB_ACRYLIC_RIM, 'rgba(60, 70, 78, 0.55)');
  layer(L.shell, HOT_TUB_WATER, HOT_TUB_LINE);
  for (const s of [...L.seats, ...L.platforms]) layer(s.poly, HOT_TUB_SEAT, HOT_TUB_LINE);
  if (L.lounger) {
    layer(L.lounger.outline, HOT_TUB_SEAT, HOT_TUB_LINE);
    // The lounger's recline: a faint line where each step meets the next.
    ctx.save();
    ctx.setLineDash([4, 3]);
    for (const st of L.lounger.steps) {
      path(st.poly);
      ctx.strokeStyle = 'rgba(40, 120, 145, 0.35)'; ctx.lineWidth = 1; ctx.stroke();
    }
    ctx.restore();
  }
  layer(L.footwell, HOT_TUB_DEEP, HOT_TUB_LINE);
  // The footwell drain.
  const c = S(L.drain.c), r = Math.max(1.5, L.drain.r * vp.scale);
  ctx.beginPath(); ctx.arc(c.x, c.y, r, 0, Math.PI * 2);
  ctx.fillStyle = '#e8eef0'; ctx.fill();
  ctx.strokeStyle = HOT_TUB_LINE; ctx.lineWidth = 1; ctx.stroke();
  ctx.restore();
}


function drawEntity(
  ctx: CanvasRenderingContext2D,
  vp: Viewport,
  e: GeomEntity,
  toWorld: (p: Vec2) => Vec2
): void {
  // Per-entity colour from the source DXF, when present. The caller sets a
  // default stroke/fill; we override it for this entity only.
  const tinted = !!e.color;
  if (tinted) {
    ctx.save();
    ctx.strokeStyle = e.color!;
    ctx.fillStyle = e.color!;
  }
  switch (e.kind) {
    case 'hatch': {
      ctx.beginPath();
      for (const loop of e.loops) {
        loop.forEach((p, i) => {
          const s = vp.worldToScreen(toWorld(p));
          if (i === 0) ctx.moveTo(s.x, s.y);
          else ctx.lineTo(s.x, s.y);
        });
        ctx.closePath();
      }
      // A colour carrying its own alpha (#rrggbbaa, e.g. a polygon symbol fill) is
      // used as-is; a plain DXF hatch colour gets the default translucent shading.
      ctx.fillStyle = !e.color
        ? hexToRgba('#9fb3c8', 0.25)
        : e.color.length === 9
          ? e.color
          : hexToRgba(e.color, 0.35);
      ctx.fill('evenodd');
      break;
    }
    case 'line': {
      const a = vp.worldToScreen(toWorld(e.a));
      const b = vp.worldToScreen(toWorld(e.b));
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      ctx.stroke();
      break;
    }
    case 'polyline': {
      ctx.beginPath();
      e.points.forEach((p, i) => {
        const s = vp.worldToScreen(toWorld(p));
        if (i === 0) ctx.moveTo(s.x, s.y);
        else ctx.lineTo(s.x, s.y);
      });
      if (e.closed) ctx.closePath();
      ctx.stroke();
      break;
    }
    case 'circle': {
      drawArcSampled(ctx, vp, toWorld, e.center, e.radius, 0, 360);
      break;
    }
    case 'arc': {
      drawArcSampled(ctx, vp, toWorld, e.center, e.radius, e.startAngle, e.endAngle);
      break;
    }
    case 'ellipse':
    case 'spline': {
      const pts = curveTessellation(e);
      if (pts && pts.length) {
        ctx.beginPath();
        pts.forEach((p, i) => { const s = vp.worldToScreen(toWorld(p)); if (i === 0) ctx.moveTo(s.x, s.y); else ctx.lineTo(s.x, s.y); });
        if (e.closed) ctx.closePath();
        ctx.stroke();
      }
      break;
    }
    case 'text': {
      const s = vp.worldToScreen(toWorld(e.pos));
      const px = e.height * vp.scale;
      if (px >= 5 && px < 4000) {
        ctx.save();
        ctx.font = `${px}px sans-serif`;
        ctx.textBaseline = e.baseline ?? 'bottom';
        if (e.align) ctx.textAlign = e.align;
        ctx.fillText(e.value, s.x, s.y);
        ctx.restore();
      }
      break;
    }
  }
  if (tinted) ctx.restore();
}

function drawArcSampled(
  ctx: CanvasRenderingContext2D,
  vp: Viewport,
  toWorld: (p: Vec2) => Vec2,
  center: Vec2,
  radius: number,
  startDeg: number,
  endDeg: number
): void {
  let sweep = endDeg - startDeg;
  while (sweep <= 0) sweep += 360;
  const segs = Math.max(8, Math.ceil((sweep / 360) * 64));
  ctx.beginPath();
  for (let i = 0; i <= segs; i++) {
    const a = ((startDeg + (sweep * i) / segs) * Math.PI) / 180;
    const wp = { x: center.x + radius * Math.cos(a), y: center.y + radius * Math.sin(a) };
    const s = vp.worldToScreen(toWorld(wp));
    if (i === 0) ctx.moveTo(s.x, s.y);
    else ctx.lineTo(s.x, s.y);
  }
  ctx.stroke();
}

/** The selection's box round a product drawn by its own drawing (an image or
 *  a catalogue geometry), which has none unless selected. */
function strokeFootprint(ctx: CanvasRenderingContext2D, corners: Vec2[]): void {
  ctx.beginPath();
  corners.forEach((c, i) => (i === 0 ? ctx.moveTo(c.x, c.y) : ctx.lineTo(c.x, c.y)));
  ctx.closePath();
  ctx.lineWidth = 2.5;
  ctx.strokeStyle = '#ffd166';
  ctx.stroke();
}

/** A part's 3D model seen from above (`modelPlan`): its solids, the lowest
 *  first, each filled over what lies under it, without the seams between its
 *  pieces. */
function drawModelFromAbove(ctx: CanvasRenderingContext2D, vp: Viewport, model: ModelOutline[]): void {
  const ring = (pts: Vec2[]): void => {
    ctx.beginPath();
    pts.forEach((p, i) => { const q = vp.worldToScreen(p); if (i === 0) ctx.moveTo(q.x, q.y); else ctx.lineTo(q.x, q.y); });
    ctx.closePath();
  };
  for (const s of model) {
    // On the dark canvas a near-black solid's edges are drawn lighter, or they vanish.
    const edge = pass.light ? darken(s.color, 0.45) : darkHex(s.color) ? lighten(s.color, 0.45) : s.color;
    for (const pc of s.pieces) {
      ring(pc.pts);
      ctx.fillStyle = s.water ? hexToRgba(s.color, 0.45) : over(s.color, PAGE_BG(), 0.55);
      ctx.fill();
      ctx.beginPath();
      pc.pts.forEach((p, i) => {
        if (pc.hidden[i]) return;
        const a = vp.worldToScreen(p), q = vp.worldToScreen(pc.pts[(i + 1) % pc.pts.length]);
        ctx.moveTo(a.x, a.y); ctx.lineTo(q.x, q.y);
      });
      ctx.lineWidth = 1;
      ctx.strokeStyle = edge;
      ctx.stroke();
    }
  }
}

/** A tilted part as seen from above — its true projection. A part with a 3D
 *  model (partModel.ts) is its model: its solids, the lowest first, each filled
 *  over what lies under it, without the seams between its pieces. Any other is
 *  its box's faces turned up, with its own drawing laid on whichever of its top
 *  and its underside faces up. */
function drawTiltedPart(ctx: CanvasRenderingContext2D, vp: Viewport, inst: ProductInstance, selected: boolean, images?: Map<string, CanvasImageSource>): void {
  const b = bodyOf(inst, 0, { w: inst.width, d: inst.depth, h: partHeight(inst) });
  const ring = (pts: Vec2[]): void => {
    ctx.beginPath();
    pts.forEach((p, i) => { const q = vp.worldToScreen(p); if (i === 0) ctx.moveTo(q.x, q.y); else ctx.lineTo(q.x, q.y); });
    ctx.closePath();
  };
  const model = modelPlan(inst, b);
  ctx.save();
  ctx.lineJoin = 'miter';
  if (model.length) {
    drawModelFromAbove(ctx, vp, model);
    if (selected) {
      ring(planOutline(b));
      ctx.lineWidth = 1.5;
      ctx.strokeStyle = '#ffd166';
      ctx.stroke();
    }
  } else {
    for (const f of planFaces(b)) {
      ring(f);
      if (pass.showHatches) { ctx.fillStyle = hexToRgba(inst.color, 0.22); ctx.fill(); }
      ctx.lineWidth = 1;
      ctx.strokeStyle = inst.color;
      ctx.stroke();
    }
    drawLaidDrawing(ctx, vp, inst, b, images);
    ring(planOutline(b));
    ctx.lineWidth = selected ? 2.5 : 1.5;
    ctx.strokeStyle = selected ? '#ffd166' : inst.color;
    ctx.stroke();
  }
  ctx.restore();
  if (pass.showLabels) {
    const c = vp.worldToScreen({ x: inst.x, y: inst.y });
    ctx.save();
    ctx.fillStyle = selected ? '#ffd166' : INK();
    ctx.font = '13px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(instLabel(inst), c.x, c.y);
    ctx.restore();
  }
  if (selected && !pass.multiSelect) {
    drawHandles(ctx, vp, inst);
    if (pass.quietDimsOf !== inst.id) drawLineDimensions(ctx, vp, instanceCorners(inst), true);
  }
}

/** A tilted part's own drawing (its DXF, its image, its kind's symbol) laid on
 *  the face of its body turned up — its top, or its underside if it is turned
 *  over —, as that face is seen from above: the upright drawing drawn through
 *  the map from its plan frame to the face's projection. */
function drawLaidDrawing(ctx: CanvasRenderingContext2D, vp: Viewport, inst: ProductInstance, b: Body, images?: Map<string, CanvasImageSource>): void {
  const z = applyMat3(b.m, { x: 0, y: 0, z: 1 }).z >= 0 ? b.size.h : 0;
  const o = bodyPoint(b, { x: 0, y: 0, z }), ex = bodyPoint(b, { x: 1, y: 0, z }), ey = bodyPoint(b, { x: 0, y: 1, z });
  const lx = { x: ex.x - o.x, y: ex.y - o.y }, ly = { x: ey.x - o.x, y: ey.y - o.y };
  const at = vp.worldToScreen({ x: inst.x, y: inst.y }), to = vp.worldToScreen(o);
  const upright: ProductInstance = { ...inst, rotation: 0 };
  delete upright.rotationX;
  delete upright.rotationY;
  const labels = pass.showLabels;
  ctx.save();
  ctx.translate(to.x, to.y);
  ctx.transform(lx.x, -lx.y, -ly.x, ly.y, 0, 0);
  ctx.translate(-at.x, -at.y);
  pass.showLabels = false;
  try { drawInstance(ctx, vp, upright, false, images); } finally { pass.showLabels = labels; ctx.restore(); }
}

/** A colour laid over another at `alpha`, opaque. */
function over(hex: string, under: string, alpha: number): string {
  const rgb = (h: string): number[] => {
    const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})/i.exec(h);
    return m ? [1, 2, 3].map((i) => parseInt(m[i], 16)) : [128, 128, 128];
  };
  const [a, u] = [rgb(hex), rgb(under)];
  return `rgb(${a.map((c, i) => Math.round(c * alpha + u[i] * (1 - alpha))).join(',')})`;
}

function drawInstance(
  ctx: CanvasRenderingContext2D,
  vp: Viewport,
  inst: ProductInstance,
  selected: boolean,
  images?: Map<string, CanvasImageSource>,
  pendingInner?: InnerWall | null,
  drawEdit?: { selectedPolyId: string | null; selectedWallId: string | null; verticesId: string | null } | null
): void {
  const corners = instanceCorners(inst).map((c) => vp.worldToScreen(c));

  if (inst.autoBenches) drawPolyRoom(ctx, vp, inst, selected, pendingInner);
  else if (!drawPart(ctx, vp, inst, selected, corners, images)) return;

  // Detailed (imported) geometry for non-auto-bench products: a part's own
  // drawing over its symbol (`drawPart` returned early for one that leaves it
  // out). Auto-bench benches are drawn earlier, beneath the walls.
  if (inst.geometry && !inst.autoBenches) {
    ctx.save();
    ctx.strokeStyle = inst.color;
    ctx.fillStyle = inst.color;
    ctx.lineWidth = 1;
    for (const e of inst.geometry.entities) {
      // Hatches baked into the product's own geometry (e.g. from drawn polygon
      // fills) are real content, so they ignore the Hatches toggle — only the
      // auto-added category footprint fill above is hidden by it.
      drawEntity(ctx, vp, e, (p) => instanceToWorld(inst, p));
    }
    ctx.restore();
  }
  drawInstanceRest(ctx, vp, inst, selected, drawEdit);
}

/** A placed part as its look has it (partLook.ts): what stands round its body,
 *  then — tilted — its body seen from above, else its plan symbol (or its image,
 *  once loaded). False when it is finished: its handles and dimensions drawn,
 *  its own drawing left out (`TAKES_DRAWING`). */
function drawPart(ctx: CanvasRenderingContext2D, vp: Viewport, inst: ProductInstance, selected: boolean, corners: Vec2[], images?: Map<string, CanvasImageSource>): boolean {
  const look = partLook(inst);
  // Behind the body: bands outside its footprint, so they never cross it.
  for (const a of look.attachments) drawAttachment(ctx, vp, inst, a, selected);

  // A tilted part is drawn as seen from above (its model, or its box's faces
  // with its drawing laid on them).
  if (isTilted(inst)) { drawTiltedPart(ctx, vp, inst, selected, images); return false; }

  // A raster image attached to a product (imported JPG / PNG / PDF) is drawn in
  // place of any category-specific graphic — the user explicitly chose an image
  // for it. Until the image finishes loading it is its symbol (the image's
  // onload re-renders once it's ready).
  if (look.plan.image) {
    const img = images?.get(look.plan.image);
    if (img) {
      const center = vp.worldToScreen({ x: inst.x, y: inst.y });
      ctx.save();
      ctx.translate(center.x, center.y);
      ctx.rotate((-inst.rotation * Math.PI) / 180);
      const wpx = inst.width * vp.scale, hpx = inst.depth * vp.scale;
      ctx.drawImage(img, -wpx / 2, -hpx / 2, wpx, hpx);
      ctx.restore();
      if (selected) strokeFootprint(ctx, corners);
      if (pass.showLabels) {
        const c = vp.worldToScreen({ x: inst.x, y: inst.y });
        ctx.save();
        ctx.fillStyle = selected ? '#ffd166' : INK();
        ctx.font = '13px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(instLabel(inst), c.x, c.y);
        ctx.restore();
      }
      if (selected && !pass.multiSelect) { drawHandles(ctx, vp, inst); drawLineDimensions(ctx, vp, instanceCorners(inst), true); }
      return false;
    }
  }

  const symbol = look.plan.symbol;
  switch (symbol) {
    // Salt wall: a parametric element — two fixed end frames with a stretchable
    // body, derived from the instance's top edge (length = width, height = depth).
    case 'saltWall': {
      if (inst.nodes && inst.nodes.length >= 2) {
        const n = inst.nodes.length;
        for (let i = 0; i < n - 1; i++) {
          drawSaltWall(ctx, vp, inst.nodes[i], inst.nodes[i + 1], selected, i === 0, i === n - 2, i > 0 ? inst.nodes[i - 1] : null, i + 2 < n ? inst.nodes[i + 2] : null);
        }
        if (selected && !pass.multiSelect) { drawHandles(ctx, vp, inst); drawLineDimensions(ctx, vp, inst.nodes, false); }
        break;
      }
      // The body extrudes from the (a,b) edge towards the opposite side; anchor it
      // on the far (+depth/2) edge so it fills the footprint rather than hanging
      // outside it — keeping the graphic aligned with the selection box/handles.
      const a = instanceToWorld(inst, { x: -inst.width / 2, y: inst.depth / 2 });
      const b = instanceToWorld(inst, { x: inst.width / 2, y: inst.depth / 2 });
      drawSaltWall(ctx, vp, a, b, selected);
      if (selected && !pass.multiSelect) { drawHandles(ctx, vp, inst); drawLineDimensions(ctx, vp, instanceCorners(inst), true); }
      break;
    }
    // Heated bench: a warm copper-coloured bench body with 3 wavy heating lines.
    case 'heatedBench': {
      if (inst.nodes && inst.nodes.length >= 2) {
        const n = inst.nodes.length;
        for (let i = 0; i < n - 1; i++) {
          drawHeatedBench(ctx, vp, inst.nodes[i], inst.nodes[i + 1], inst.depth, selected, i > 0 ? inst.nodes[i - 1] : null, i + 2 < n ? inst.nodes[i + 2] : null);
        }
        if (selected && !pass.multiSelect) { drawHandles(ctx, vp, inst); drawLineDimensions(ctx, vp, inst.nodes, false); }
        break;
      }
      const a = instanceToWorld(inst, { x: -inst.width / 2, y: inst.depth / 2 });
      const b = instanceToWorld(inst, { x: inst.width / 2, y: inst.depth / 2 });
      drawHeatedBench(ctx, vp, a, b, inst.depth, selected);
      if (selected && !pass.multiSelect) { drawHandles(ctx, vp, inst); drawLineDimensions(ctx, vp, instanceCorners(inst), true); }
      break;
    }
    // Sauna bench: a slatted bench body that follows a polyline path (like a heated
    // bench), but drawn as seat slats (doghe) — the same look as a sauna-room bench.
    case 'saunaBench': {
      // One plan with the DXF (`saunaBenchPlan`): its trestles show through the
      // slats, filled under and outlined over them, then its closed ends.
      const plan = saunaBenchPlan(inst);
      const toS = (p: Vec2): Vec2 => vp.worldToScreen(p);
      fillTrestles(ctx, toS, plan.trestles, inst.color);
      for (const seg of plan.segments) drawSaunaBench(ctx, toS, seg, inst.color, selected);
      outlineTrestles(ctx, toS, inst.id, plan.trestles.map((mark) => ({ mark, col: inst.color })));
      drawBenchEnds(ctx, toS, plan.ends);
      if (inst.nodes && inst.nodes.length >= 2) {
        const nj = pass.hoverBenchJoint?.instId === inst.id ? pass.hoverBenchJoint.corner : null;
        if (nj && typeof nj === 'object' && 'node' in nj) pulseJoint(ctx, nodeBenchJoints(inst).find((j) => j.node === nj.node)?.polys ?? [], toS);
        if (selected && !pass.multiSelect) { drawHandles(ctx, vp, inst); if (pass.quietDimsOf !== inst.id) drawLineDimensions(ctx, vp, inst.nodes, false); }
        break;
      }
      if (selected && !pass.multiSelect) { drawHandles(ctx, vp, inst); drawLineDimensions(ctx, vp, instanceCorners(inst), true); }
      break;
    }
    // Cable channel (canalina): a yellow channel with a green outer + inner outline.
    case 'canalina':
      drawCanalina(ctx, vp, inst, selected);
      if (selected && !pass.multiSelect) { drawHandles(ctx, vp, inst); drawLineDimensions(ctx, vp, instanceCorners(inst), true); }
      break;
    // Floor drain grate.
    case 'drain':
      drawDrain(ctx, vp, inst, selected);
      if (selected && !pass.multiSelect) { drawHandles(ctx, vp, inst); drawLineDimensions(ctx, vp, instanceCorners(inst), true); }
      break;
    // Infrared emitter: wall-mounted panel with reflectors and IR wave indicators.
    case 'infrared': drawInfraredEmitter(ctx, vp, inst, selected); break;
    case 'steamCube': drawCubeInstance(ctx, vp, inst, selected); break;
    case 'hotTub': drawHotTub(ctx, vp, inst, selected); break;
    case 'heaterBlock': drawHeaterBody(ctx, vp, inst, selected); break;
    case 'flex4': drawFlex4(ctx, vp, inst, selected); break;
    case 'lighting': drawLightingPart(ctx, inst, corners, selected); break;
    case 'model':
      ctx.save();
      ctx.lineJoin = 'miter';
      drawModelFromAbove(ctx, vp, uprightModelPlan(inst));
      ctx.restore();
      if (selected) strokeFootprint(ctx, corners);
      break;
    case 'drawing':
    case 'box':
      // Footprint fill + outline: a plain product's whole drawing, but only the
      // selection's box round one drawn by its own geometry (a catalogue
      // drawing). The coloured fill is a product "hatch", so it is hidden with
      // the Hatches toggle (leaving just the outline).
      if (symbol === 'drawing' && !selected) break;
      ctx.beginPath();
      corners.forEach((c, i) => (i === 0 ? ctx.moveTo(c.x, c.y) : ctx.lineTo(c.x, c.y)));
      ctx.closePath();
      if (pass.showHatches) {
        ctx.fillStyle = hexToRgba(inst.color, 0.22);
        ctx.fill();
      }
      ctx.lineWidth = selected ? 2.5 : 1.5;
      ctx.strokeStyle = selected ? '#ffd166' : inst.color;
      ctx.stroke();
      break;
    default: unreachable(symbol, 'plan symbol');
  }
  return TAKES_DRAWING[symbol];
}

/** What follows a part's or a room's own drawing: its label, its handles, a
 *  room's vertex-edit markers, its temporary dimensions and the shapes added to
 *  it in the drawing editor. */
function drawInstanceRest(
  ctx: CanvasRenderingContext2D,
  vp: Viewport,
  inst: ProductInstance,
  selected: boolean,
  drawEdit?: { selectedPolyId: string | null; selectedWallId: string | null; verticesId: string | null } | null
): void {
  // Label at the centre (toggled off via the bottom-bar Text button).
  if (pass.showLabels) {
    const center = vp.worldToScreen({ x: inst.x, y: inst.y });
    ctx.save();
    ctx.fillStyle = selected ? '#ffd166' : INK();
    ctx.font = '13px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(instLabel(inst), center.x, center.y);
    ctx.restore();
  }

  if (selected && !pass.multiSelect) drawHandles(ctx, vp, inst);
  // Poly-room vertex-edit markers go last, so the green/blue corner handles and
  // the selected wall band sit over the amber resize handles rather than under.
  drawPolyRoomVertexEdit(ctx, vp, inst);
  // Temporary outline dimensions for the selected object, spaced out to stay
  // readable: rooms split their door side into wall/door/wall, everything else
  // shows its footprint width and depth.
  if (selected && !pass.multiSelect && pass.quietDimsOf !== inst.id) {
    if (inst.autoBenches) drawRoomDimensions(ctx, vp, inst);
    else drawLineDimensions(ctx, vp, instanceCorners(inst), true);
  }

  // User-drawn shapes added in the drawing editor (stored in local frame).
  // Polygons and walls share one draw stack, ordered by each shape's `z` (higher
  // on top); shapes without a z fall back to polygons-then-walls insertion order.
  const toW = (p: Vec2): Vec2 => instanceToWorld(inst, p);
  const polyN = inst.drawPolygons?.length ?? 0;
  const stack: Array<{ poly?: Polygon; wall?: Wall; z: number }> = [];
  (inst.drawPolygons ?? []).forEach((lp, i) => stack.push({ poly: lp, z: lp.z ?? i }));
  (inst.drawWalls ?? []).forEach((lw, i) => stack.push({ wall: lw, z: lw.z ?? polyN + i }));
  stack.sort((a, b) => a.z - b.z);
  for (const s of stack) {
    if (s.poly) {
      const lp = s.poly;
      const wp: Polygon = { ...lp, points: lp.points.map(toW), holes: lp.holes?.map((h) => h.map(toW)) };
      const shSel = drawEdit ? drawEdit.selectedPolyId === lp.id : false;
      const showVHandles = drawEdit ? drawEdit.verticesId === lp.id : false;
      drawPolygon(ctx, vp, wp, shSel, false, showVHandles ? -1 : -1);
      if (showVHandles) {
        drawGrips(ctx, wp.points.map((p) => vp.worldToScreen(p)), VERTEX_EDIT_GREEN);
        for (const hole of wp.holes ?? []) drawGrips(ctx, hole.map((p) => vp.worldToScreen(p)), VERTEX_EDIT_GREEN);
      } else if (shSel && lp.points.length >= 2) {
        // Bounding-box resize handles (drag to resize; drag the body to move).
        // Computed in the local frame so they rotate with the instance.
        drawGrips(ctx, shapeBBoxHandles(lp.points).map((h) => vp.worldToScreen(toW(h.world))));
      }
    } else if (s.wall) {
      const lw = s.wall;
      const ww: Wall = { ...lw, points: lw.points.map(toW) };
      const shSel = drawEdit ? drawEdit.selectedWallId === lw.id : false;
      drawWall(ctx, vp, ww, shSel);
      if (drawEdit && drawEdit.verticesId === lw.id) {
        drawGrips(ctx, ww.points.map((p) => vp.worldToScreen(p)), VERTEX_EDIT_GREEN);
      } else if (shSel && lw.points.length >= 2) {
        drawGrips(ctx, shapeBBoxHandles(lw.points).map((h) => vp.worldToScreen(toW(h.world))));
      }
    }
  }
}

/** The world-space outline of a room: the polygon for a polygonal room, the
 *  rectangular footprint corners otherwise. */
function roomOutlineWorld(inst: ProductInstance): Vec2[] {
  // The SHARP polygon, deliberately: this drives the room's wall dimensions, and
  // a wall keeps measuring its full sharp-cornered length however its ends are
  // rounded (one dimension per wall, not one per arc segment).
  // One point per CORNER: a pane's jambs are not corners, so they never break a
  // wall's figure (the chain across the wall does that).
  if (isPolyRoom(inst)) return (wallView(inst).polygon || []).map((p) => instanceToWorld(inst, p));
  return instanceCorners(inst);
}

/** A placed support's trestles seen through the slats: filled in the bench's
 *  structure colour UNDER the slats (so it shows in their gaps)… */
function fillTrestles(ctx: CanvasRenderingContext2D, toS: (p: Vec2) => Vec2, marks: TrestleMark[], benchCol: string): void {
  if (!marks.length) return;
  ctx.beginPath();
  for (const m of marks) {
    m.poly.map(toS).forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
    ctx.closePath();
  }
  ctx.fillStyle = structureColor(benchCol);
  ctx.fill();
}

/** A bench's closed ends on the plan: a thin line across it at the boards'
 *  inside face — stepping where a board is thicker —, over its slats
 *  (`closedEndLines`, which the DXF writes too; `toS`: the marks' frame to the
 *  screen). */
function drawBenchEnds(ctx: CanvasRenderingContext2D, toS: (p: Vec2) => Vec2, lines: Vec2[][]): void {
  if (!lines.length) return;
  ctx.save();
  ctx.strokeStyle = BENCH_END_LINE;
  ctx.lineWidth = 1.2;
  ctx.beginPath();
  for (const line of lines) line.map(toS).forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
  ctx.stroke();
  ctx.restore();
}

/** …and outlined dashed OVER them; the selected support solid, in the
 *  selection yellow. */
function outlineTrestles(ctx: CanvasRenderingContext2D, toS: (p: Vec2) => Vec2, instId: string, marks: Array<{ mark: TrestleMark; col: string }>): void {
  ctx.save();
  ctx.lineJoin = 'miter';
  for (const { mark, col } of marks) {
    const sel = pass.selectedSupport?.instId === instId && pass.selectedSupport.id === mark.support;
    ctx.beginPath();
    mark.poly.map(toS).forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
    ctx.closePath();
    ctx.setLineDash(sel ? [] : [4, 3]);
    ctx.lineWidth = sel ? 2.5 : 1.2;
    ctx.strokeStyle = sel ? '#ffd166' : darken(col, TRESTLE_LINE_SHADE);
    ctx.stroke();
  }
  ctx.restore();
}

/**
 * Render a polygonal room: faint floor tint, solid wall ring (offset inward from
 * the drawn outline, mitred at the corners), the gapped wall outline, and the
 * door swing on the first segment. Glass and openings are drawn by the shared
 * drawGlass/drawOpenings passes (they read the edge-tagged segments).
 */
/** The strips of a room on the plan: each run dashed in the lighting amber, the
 *  selected strip under a wide halo, and a ▲ / ▼ beside each run's middle, on
 *  the room's side — moved along the run off another run's (C and D share a line). */
function drawStripLights(ctx: CanvasRenderingContext2D, inst: ProductInstance, toS: (p: Vec2) => Vec2): void {
  const runs = stripRuns(inst);
  if (!runs.length) return;
  ctx.save();
  ctx.lineCap = 'round';
  const glyphs: Vec2[] = [];
  // A backrest's up and down strips, one line in plan, are drawn a few px apart.
  const o = toS({ x: 0, y: 0 }), x1 = toS({ x: 1, y: 0 });
  const apart = STRIP_APART_PX / (Math.hypot(x1.x - o.x, x1.y - o.y) || 1);
  for (const run of runs) {
    for (const piece of run.pieces) {
      const pts = drawnStripPts({ ...piece, run }, apart).map(toS);
      const trace = (): void => { ctx.beginPath(); pts.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y))); };
      if (pass.fixtureSelectedIds.has(piece.id)) {
        trace(); ctx.setLineDash([]); ctx.strokeStyle = '#ffd166'; ctx.lineWidth = 7; ctx.stroke();
      }
      trace(); ctx.setLineDash([8, 5]); ctx.strokeStyle = LIGHT_AMBER; ctx.lineWidth = 2.2; ctx.stroke();
    }
    const mid = pathMiddle(run.pts);
    if (!mid) continue;
    const lean = { x: -mid.dir.y * run.side, y: mid.dir.x * run.side };
    const a = toS(mid.p), b = toS({ x: mid.p.x + lean.x, y: mid.p.y + lean.y });
    const L = Math.hypot(b.x - a.x, b.y - a.y) || 1;
    const g = { x: a.x + ((b.x - a.x) / L) * 11, y: a.y + ((b.y - a.y) / L) * 11 };
    const c = toS({ x: mid.p.x + mid.dir.x, y: mid.p.y + mid.dir.y });
    const along = { x: c.x - a.x, y: c.y - a.y }, M = Math.hypot(along.x, along.y) || 1;
    for (let k = 0; k < 8 && glyphs.some((q) => Math.hypot(q.x - g.x, q.y - g.y) < 10); k++) { g.x += (along.x / M) * 12; g.y += (along.y / M) * 12; }
    glyphs.push({ ...g });
    ctx.setLineDash([]);
    ctx.fillStyle = LIGHT_AMBER;
    ctx.beginPath();
    if (run.shine.up) { ctx.moveTo(g.x, g.y - 5); ctx.lineTo(g.x - 4.5, g.y + 3); ctx.lineTo(g.x + 4.5, g.y + 3); } else { ctx.moveTo(g.x, g.y + 5); ctx.lineTo(g.x - 4.5, g.y - 3); ctx.lineTo(g.x + 4.5, g.y - 3); }
    ctx.closePath();
    ctx.fill();
  }
  ctx.restore();
}

/** A room's ceiling lights on the plan, dashed as overhead: a spot a circle with
 *  a cross, a fibre end a small circle with a dot; the selected one under a halo. */
function drawCeilingLights(ctx: CanvasRenderingContext2D, vp: Viewport, inst: ProductInstance, toS: (p: Vec2) => Vec2): void {
  const spots = spotsOf(inst), channels = channelsOf(inst), cove = coveOf(inst), cg = cove ? coveGeometry(inst) : null, skies = skiesOf(inst);
  if (!spots.length && !channels.length && !cg && !skies.length) return;
  ctx.save();
  // A starry sky: its zone dashed, each fibre end a dot, the selected one's zone
  // under a halo; every fibre kit's arcs from its projector, dashed.
  for (const sky of skies) {
    const zone = skyZone(inst, sky).map(toS);
    const ring = (): void => { ctx.beginPath(); zone.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y))); ctx.closePath(); };
    if (zone.length >= 3 && pass.fixtureSelectedIds.has(sky.id)) { ring(); ctx.setLineDash([]); ctx.strokeStyle = '#ffd166'; ctx.lineWidth = 5; ctx.stroke(); }
    if (zone.length >= 3) { ring(); ctx.setLineDash([2, 5]); ctx.strokeStyle = LIGHT_AMBER; ctx.lineWidth = 1; ctx.stroke(); }
    ctx.fillStyle = LIGHT_AMBER;
    const r = Math.max((SKY_FIBRE_DIAMETER / 2) * vp.scale, 1.7);
    for (const f of skyFibres(inst, sky)) { const c = toS(f.at); ctx.beginPath(); ctx.arc(c.x, c.y, r, 0, Math.PI * 2); ctx.fill(); }
  }
  for (const kit of fibreKitsOf(inst)) {
    const part = kit.projectorId ? pass.partsById.get(kit.projectorId) : undefined;
    if (!part) continue;
    ctx.strokeStyle = LIGHT_AMBER; ctx.lineWidth = 1; ctx.setLineDash([5, 4]);
    for (const arc of kitArcs(inst, projectorMiddle(inst, part), kitFibres(inst, kit))) {
      ctx.beginPath(); arc.map(([x, y]) => toS({ x, y })).forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y))); ctx.stroke();
    }
  }
  // A cove: a flat ceiling's second layer, its ledges and valances (or a Coved
  // one's ledges) dashed in the walls' colour, overhead; its strip as a strip's.
  if (cove && cg) {
    ctx.strokeStyle = darken(wallColorOf(inst), 0.75); ctx.lineWidth = 1; ctx.setLineDash([6, 4]);
    for (const part of cg.parts) {
      ctx.beginPath(); part.poly.map(toS).forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y))); ctx.closePath(); ctx.stroke();
    }
    for (const run of cg.runs) {
      const pts = run.pts.map(toS);
      const trace = (): void => { ctx.beginPath(); pts.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y))); };
      if (pass.fixtureSelectedIds.has(cove.id)) { trace(); ctx.setLineDash([]); ctx.strokeStyle = '#ffd166'; ctx.lineWidth = 7; ctx.stroke(); }
      trace(); ctx.setLineDash([4, 4]); ctx.strokeStyle = LIGHT_AMBER; ctx.lineWidth = 1.8; ctx.stroke();
    }
  }
  // A channel: its groove's two sides, dashed.
  for (const ch of channels) {
    const sides = pathSides(ch.pts, Math.max(channelFigures(ch).width, 5 / vp.scale));
    if (!sides) continue;
    if (pass.fixtureSelectedIds.has(ch.id)) {
      ctx.setLineDash([]); ctx.strokeStyle = '#ffd166'; ctx.lineWidth = 8; ctx.lineJoin = 'round';
      ctx.beginPath(); ch.pts.map(toS).forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y))); ctx.stroke();
    }
    ctx.strokeStyle = LIGHT_AMBER; ctx.lineWidth = 1.2; ctx.setLineDash([7, 4]);
    for (const side of sides) {
      ctx.beginPath(); side.map(toS).forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y))); ctx.stroke();
    }
  }
  for (const sp of spots) {
    const c = toS(sp.at);
    const r = Math.max(((sp.fibre ? FIBRE_DIAMETER : SPOT_DIAMETER) / 2) * vp.scale, sp.fibre ? 4 : 7);
    if (pass.fixtureSelectedIds.has(sp.id)) {
      ctx.setLineDash([]); ctx.strokeStyle = '#ffd166'; ctx.lineWidth = 6;
      ctx.beginPath(); ctx.arc(c.x, c.y, r, 0, Math.PI * 2); ctx.stroke();
    }
    ctx.strokeStyle = LIGHT_AMBER; ctx.lineWidth = 1.6; ctx.setLineDash([3, 2.5]);
    ctx.beginPath(); ctx.arc(c.x, c.y, r, 0, Math.PI * 2); ctx.stroke();
    ctx.setLineDash([]);
    if (sp.fibre) {
      ctx.fillStyle = LIGHT_AMBER;
      ctx.beginPath(); ctx.arc(c.x, c.y, 1.8, 0, Math.PI * 2); ctx.fill();
      continue;
    }
    const q = r * 0.72;
    ctx.beginPath(); ctx.moveTo(c.x - q, c.y - q); ctx.lineTo(c.x + q, c.y + q); ctx.moveTo(c.x + q, c.y - q); ctx.lineTo(c.x - q, c.y + q); ctx.stroke();
  }
  ctx.restore();
}

/** A room's light tags (L1 …), the key to the Lighting table: each beside its
 *  light — a strip's or a channel's into the room —, moved along off another;
 *  one moved by hand where it was put (`tagTextAt`), with its leader. */
function drawLightTags(ctx: CanvasRenderingContext2D, inst: ProductInstance, toS: (p: Vec2) => Vec2): void {
  for (const a of lightTagAnchors(inst)) {
    const tag = pass.lightTags.get(a.key);
    if (!tag) continue;
    const c = toS(a.at);
    let box: TagBox;
    if (a.offset) box = lightTagBox(ctx, tag, toS(tagTextAt(a)));
    else {
      let dx = 14, dy = -14;
      if (a.side) {
        const s = toS({ x: a.at.x + a.side.x, y: a.at.y + a.side.y }), L = Math.hypot(s.x - c.x, s.y - c.y) || 1;
        dx = ((s.x - c.x) / L) * 22; dy = ((s.y - c.y) / L) * 22;
      }
      box = lightTagBox(ctx, tag, { x: c.x + dx, y: c.y + dy });
      for (let k = 0; k < 8 && pass.lightTagBoxes.some((b) => b.x < box.x + box.w && box.x < b.x + b.w && b.y < box.y + box.h && box.y < b.y + b.h); k++) box.y += box.h + 2;
    }
    pass.lightTagBoxes.push(box);
    pass.hits.lightTags.push({ ...box, tag, instId: inst.id, id: a.id });
    paintLightTag(ctx, tag, box, a.offset ? c : null);
  }
}

/** A lighting part — a fibre projector: its box in the light's amber, dashed when
 *  it stands overhead (above a ceiling), its fill a hatch. */
function drawLightingPart(ctx: CanvasRenderingContext2D, inst: ProductInstance, corners: Vec2[], selected: boolean): void {
  ctx.save();
  ctx.beginPath();
  corners.forEach((c, i) => (i === 0 ? ctx.moveTo(c.x, c.y) : ctx.lineTo(c.x, c.y)));
  ctx.closePath();
  if (pass.showHatches) { ctx.fillStyle = hexToRgba(LIGHT_AMBER, 0.12); ctx.fill(); }
  ctx.setLineDash((inst.heightFromGround ?? 0) > OVERHEAD_FROM ? [6, 4] : []);
  ctx.lineWidth = selected ? 2.5 : 1.5;
  ctx.strokeStyle = selected ? '#ffd166' : LIGHT_AMBER;
  ctx.stroke();
  ctx.restore();
}
/** A part standing this high (mm) or more is overhead: drawn dashed. */
const OVERHEAD_FROM = 1500;

/** The point halfway along a path, and the direction of the segment it lies on. */
function pathMiddle(pts: Vec2[]): { p: Vec2; dir: Vec2 } | null {
  const lens = pts.slice(1).map((p, i) => Math.hypot(p.x - pts[i].x, p.y - pts[i].y));
  let left = lens.reduce((s, l) => s + l, 0) / 2;
  for (let i = 0; i < lens.length; i++) {
    if (lens[i] > 1e-9 && left <= lens[i]) {
      const a = pts[i], b = pts[i + 1], t = left / lens[i];
      return { p: { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t }, dir: { x: (b.x - a.x) / lens[i], y: (b.y - a.y) / lens[i] } };
    }
    left -= lens[i];
  }
  return null;
}

function drawPolyRoom(ctx: CanvasRenderingContext2D, vp: Viewport, inst: ProductInstance, selected: boolean, pendingInner?: InnerWall | null): void {
  const toS = (p: Vec2): Vec2 => vp.worldToScreen(instanceToWorld(inst, p));
  const wallCol = wallColorOf(inst);

  // Floor tint, clipped to the inner polygon. Carved by any hidden heater walls,
  // so the air gap behind one reads as an empty void rather than room floor (and
  // benches, clipped to the same ring below, stop at the heater wall's face).
  const inner = polyInnerCarved(inst);
  if (inner.length >= 3) {
    ctx.save();
    ctx.beginPath();
    inner.map(toS).forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
    ctx.closePath();
    ctx.fillStyle = hexToRgba(wallCol, 0.1);
    ctx.fill();
    ctx.restore();
  }
  drawFloorGrille(ctx, inst, toS);

  // Benches (drawn before the walls so the wall bands sit on top), clipped to the
  // room interior so a mitre never spills under the wall. Each slatted bench's
  // trestles are filled under its slats and outlined over every bench.
  const seats = roomBenchSeats(inst, pendingInner);
  // Fill a set of outer+hole rings (room local frame) as one even-odd path.
  const strokeRings = (rings: RingPoly[], fill: string, sel: boolean): void => {
    if (!rings.length) return;
    ctx.beginPath();
    for (const r of rings) {
      for (const ring of [r.outer, ...r.holes]) {
        if (ring.length < 3) continue;
        ring.map(toS).forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
        ctx.closePath();
      }
    }
    ctx.fillStyle = fill;
    ctx.fill('evenodd');
    ctx.lineWidth = sel ? 2.5 : 1;
    ctx.strokeStyle = sel ? '#ffd166' : darken(fill, 0.5);
    ctx.stroke();
  };
  const trestleOutlines: Array<{ mark: TrestleMark; col: string }> = [];
  // Each seat as `roomBenchSeats` gives it (the DXF writes the same list): its
  // trestles filled under its slats, then the slats — a plain band with its lip
  // shadow so the seat reads as raised. A corner piece is derived: never selected.
  const drawSeat = (s: BenchSeat): void => {
    const col = s.fill;
    fillTrestles(ctx, toS, s.trestles, col);
    for (const mark of s.trestles) trestleOutlines.push({ mark, col });
    strokeRings(s.rings, col, s.kind === 'band' && s.id === pass.selectedBench);
    if (!s.lip) return;
    const la = toS(s.lip[0]), lb = toS(s.lip[1]);
    ctx.beginPath();
    ctx.moveTo(la.x, la.y);
    ctx.lineTo(lb.x, lb.y);
    ctx.strokeStyle = darken(col, LIP_SHADE);
    ctx.lineWidth = 1.6;
    ctx.stroke();
  };
  const wallSeats = seats.filter((s) => !s.partition);
  if (wallSeats.length && inner.length >= 3) {
    ctx.save();
    ctx.beginPath();
    inner.map(toS).forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
    ctx.closePath();
    ctx.clip();
    ctx.lineJoin = 'round';
    for (const s of wallSeats) drawSeat(s);
    // Pulsing green highlight on the hovered corner join (click to swap winner).
    if (pass.hoverBenchJoint && pass.hoverBenchJoint.instId === inst.id && typeof pass.hoverBenchJoint.corner === 'number') {
      pulseJoint(ctx, polyBenchJointHighlight(inst, pass.hoverBenchJoint.corner, pass.hoverBenchJoint.tier), toS);
    }
    ctx.restore();
  }

  // Interior partition-wall benches: bands stepping out from each inner wall
  // face, drawn under the wall bands (which fill on top) and clipped to the room.
  const partitionSeats = seats.filter((s) => s.partition);
  if (partitionSeats.length && inner.length >= 3) {
    ctx.save();
    ctx.beginPath();
    inner.map(toS).forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
    ctx.closePath();
    ctx.clip();
    ctx.lineJoin = 'round';
    for (const s of partitionSeats) drawSeat(s);
    // A partition / wall bench joint under the cursor, over both benches.
    const pj = pass.hoverBenchJoint?.instId === inst.id ? pass.hoverBenchJoint : null;
    if (pj && typeof pj.corner === 'object' && 'iw' in pj.corner) pulseJoint(ctx, partitionBenchJointHighlight(inst, pj.corner.iw, pj.corner.face, pj.tier), toS);
    ctx.restore();
  }
  if (trestleOutlines.length && inner.length >= 3) {
    ctx.save();
    ctx.beginPath();
    inner.map(toS).forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
    ctx.closePath();
    ctx.clip();
    outlineTrestles(ctx, toS, inst.id, trestleOutlines);
    ctx.restore();
  }
  drawBenchEnds(ctx, toS, closedEndLines(inst));

  // Sculpted benches: one solid each, its outline and the edges of its seats
  // (sculptBench.ts); the selected one shows the cage it edits.
  drawSculptedBenches(ctx, inst, toS, pass.fixtureSelectedIds);

  // Backrests: boards standing off a wall over the high tier, painted over the
  // benches they stand above (backrest.ts).
  for (const br of backrestsOf(inst)) {
    const col = backrestColor(inst, br);
    const sel = pass.fixtureSelectedIds.has(br.id);
    for (const band of backrestPlan(inst, br)) {
      ctx.beginPath();
      band.map(toS).forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
      ctx.closePath();
      ctx.fillStyle = col;
      ctx.fill();
      ctx.lineWidth = sel ? 2.5 : 1;
      ctx.strokeStyle = sel ? '#ffd166' : darken(col, 0.5);
      ctx.stroke();
    }
  }

  // Strip lights: dashed, as hidden under or behind what they light, each run
  // with ▼ or ▲ beside its middle for the way it shines (lights.ts).
  if (pass.showLights) {
    drawStripLights(ctx, inst, toS);
    drawCeilingLights(ctx, vp, inst, toS);
    drawLightTags(ctx, inst, toS);
  }

  const wall = polyWall(inst);
  // Interior partition walls: committed ones plus the in-progress preview. A
  // partition carrying a door is split into gapped pieces (the passage) and its
  // pieces are stroked all round (including the jamb faces); a plain partition
  // keeps the seamless 3-sided stroke that merges into the room wall.
  const innerFills: Vec2[][] = [];
  const innerStroke3: Vec2[][] = []; // seamless partitions (skip the room-face side)
  const innerStrokeFull: Vec2[][] = []; // door-gapped pieces (all four sides)
  // A partition with its own colour is filled separately (it is a different
  // material, so the seam into the room wall is wanted); one that inherits the
  // room's colour stays in the merged path and reads seamless.
  const innerColored: Array<{ quads: Vec2[][]; color: string }> = [];
  const innerSel: Vec2[][] = []; // the selected partition, outlined like a heater wall
  for (const iw of inst.innerWalls || []) {
    if (iw.edge == null) continue;
    const gapped = innerWallGaps(inst, iw).length > 0; // a door OR a glazed pane
    const quads = (gapped ? polyInnerWallFill(inst, iw) : [polyInnerWallBand(inst, iw)]).filter((q) => q.length >= 4);
    if (!quads.length) continue;
    if (iw.color && iw.color !== wallCol) innerColored.push({ quads, color: iw.color });
    else innerFills.push(...quads);
    (gapped ? innerStrokeFull : innerStroke3).push(...quads);
    if (iw.id && iw.id === pass.innerWallSelectedId) innerSel.push(...quads);
  }
  if (pendingInner && pendingInner.edge != null) {
    const band = polyInnerWallBand(inst, pendingInner);
    if (band.length >= 4) { innerFills.push(band); innerStroke3.push(band); }
  }

  // Wall fill: every band quad (perimeter + interior partitions) in one nonzero
  // path so the mitred corners merge without internal seams.
  ctx.beginPath();
  for (const quad of [...wall.fill, ...innerFills]) {
    quad.map(toS).forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
    ctx.closePath();
  }
  ctx.fillStyle = wallCol;
  ctx.fill();

  // Partitions given their own colour, filled over the merged wall path.
  for (const ic of innerColored) {
    ctx.beginPath();
    for (const quad of ic.quads) {
      quad.map(toS).forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
      ctx.closePath();
    }
    ctx.fillStyle = ic.color;
    ctx.fill();
  }

  // Hidden heater walls: false walls set back from their room wall by an air gap.
  // Each carries its own (lighter) colour, so they're filled separately from the
  // room walls, then outlined like a wall band.
  for (const hb of heaterWallBands(inst)) {
    if (hb.poly.length < 4) continue;
    const sel = pass.heaterWallSelectedId === hb.wall.id;
    ctx.beginPath();
    hb.poly.map(toS).forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
    ctx.closePath();
    ctx.fillStyle = hb.wall.color;
    ctx.fill();
    ctx.strokeStyle = sel ? '#ffd166' : darken(hb.wall.color, 0.45);
    ctx.lineWidth = sel ? 2 : 1;
    ctx.stroke();
  }

  // Outline: outer + inner loops (gapped) and the jamb faces at openings.
  ctx.beginPath();
  for (const [a, b] of [...wall.outer, ...wall.inner, ...wall.jambs]) {
    const sa = toS(a), sb = toS(b);
    ctx.moveTo(sa.x, sa.y);
    ctx.lineTo(sb.x, sb.y);
  }
  // Rounded corners are carried as true arcs (for the DXF export); expand them
  // here so the stroked outline closes round the curve.
  for (const arc of wall.cornerArcs) {
    const pts = arcPoints(arc.center, arc.from, arc.to, arc.radius).map(toS);
    pts.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
  }
  // Seamless partitions: stroke the three exposed sides (skip the outer edge that
  // butts the room wall's inner face, which the fill already merges).
  for (const band of innerStroke3) {
    const [oS, oE, iE, iS] = band.map(toS);
    ctx.moveTo(oS.x, oS.y); ctx.lineTo(iS.x, iS.y);
    ctx.lineTo(iE.x, iE.y);
    ctx.lineTo(oE.x, oE.y);
  }
  // Door-gapped partition pieces: stroke all four sides (jamb faces included).
  for (const quad of innerStrokeFull) {
    const pts = quad.map(toS);
    ctx.moveTo(pts[0].x, pts[0].y);
    for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x, pts[i].y);
    ctx.closePath();
  }
  ctx.lineWidth = 1;
  ctx.lineCap = 'square';
  ctx.lineJoin = 'miter';
  ctx.strokeStyle = selected ? '#ffd166' : darken(wallCol, 0.5);
  ctx.stroke();

  // The individually-selected partition, outlined all round like a heater wall.
  if (innerSel.length) {
    ctx.beginPath();
    for (const quad of innerSel) {
      quad.map(toS).forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
      ctx.closePath();
    }
    ctx.lineWidth = 2;
    ctx.strokeStyle = '#ffd166';
    ctx.stroke();
    ctx.lineWidth = 1;
  }

  if (selected) {
    ctx.beginPath();
    polyVerticesWorld(inst).map(toS).forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
    ctx.closePath();
    ctx.lineWidth = 2;
    ctx.strokeStyle = '#ffd166';
    ctx.stroke();
  }

  // Door swing — one per door (outer walls and inner partitions alike). Handles
  // (jamb squares + flip icon) show only for the currently-selected door.
  for (const door of roomDoors(inst)) {
    const rd = roomDoor(inst, door);
    if (!rd) continue;
    const doorSelected = selected && pass.selectedDoorId === door.id;
    // A glazing's door swings about its hinges' axis (glazingPlan.ts draws it).
    if (rd.kind === 'plain') {
      ctx.save();
      ctx.strokeStyle = doorSelected ? '#ffd166' : '#e7b15a';
      ctx.lineWidth = doorSelected ? 2.5 : 1.6;
      strokeSwing(ctx, rd.swing, toS);
      // Hinge marker.
      const h = toS(rd.swing.hinge);
      ctx.fillStyle = doorSelected ? '#ffd166' : '#e7b15a';
      ctx.beginPath();
      ctx.arc(h.x, h.y, 3, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }

    // Jamb handles + flip-hinge icon for the selected door (resize / move / flip).
    if (doorSelected) {
      drawGrips(ctx, [toS(rd.jambs.start), toS(rd.jambs.end)]);
      // Two flip icons: '⇄' swaps the hinge left/right, and on a partition (where
      // both sides are interior) '⇅' swaps the side the leaf swings to — push/pull.
      const badge = (at: Vec2, glyph: string): void => {
        const c = toS(at);
        ctx.fillStyle = 'rgba(30,31,36,0.85)';
        ctx.strokeStyle = '#ffd166';
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.arc(c.x, c.y, 8, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
        ctx.fillStyle = '#ffd166';
        ctx.font = '11px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(glyph, c.x, c.y);
      };
      const icons = doorIcons(inst, rd);
      badge(icons.flip, '⇄');
      if (icons.swing) badge(icons.swing, '⇅');
    }
  }

  // The selected-wall band and vertex handles are drawn later, on top of the
  // resize handles (see drawPolyRoomVertexEdit), so they're never masked.

  // Constraint badges on each constrained wall (h/v & length locks, edge-pair
  // relations), shown when the room is selected or being vertex-edited.
  if ((selected || pass.polyRoomVertexEditId === inst.id) && inst.polygon) {
    // One badge per WALL: its segments share one orientation lock, and a relation on
    // any of them is a relation on the wall.
    const view = wallView(inst);
    const ringW = view.polygon!.map((p) => instanceToWorld(inst, p));
    const toWall = (e: number): number => segToWall(inst, e)?.wall ?? e;
    const relations = view === inst ? inst.polygonRelations : inst.polygonRelations
      ?.flatMap((r) => ('edges' in r ? [{ ...r, edges: [toWall(r.edges[0]), toWall(r.edges[1])] as [number, number] }] : []));
    drawConstraintBadges(ctx, vp, ringW, { locks: view.polygonConstraints, relations });
  }
}

/** World-frame outer vertices of a polygonal room (local frame, transformed). */
function polyVerticesWorld(inst: ProductInstance): Vec2[] {
  // The resolved outline, so a selected room's highlight traces its rounded
  // corners instead of cutting across them.
  return resolvedOutline(inst, 0);
}

/** Draw small square handles at the given screen points (vertex/dent editing). */
/** Green marks the editable vertices while in vertex-edit mode. */
const VERTEX_EDIT_GREEN = '#2fdb6a';
/** Blue marks the one vertex or wall the user has selected. */
const VERTEX_EDIT_SELECTED = GRIP_SELECTED;
/** A pane's jamb in vertex edit: pale glass blue, never mistaken for the selected corner. */
const VERTEX_EDIT_JAMB = '#bfe6ff';

/** Vertex-edit overlay for a poly room — the selected wall band and the corner
 *  handles — drawn AFTER the resize handles so the green/blue markers always sit
 *  on top of the amber resize squares (which share the corners), and the selected
 *  corner reads blue against the unselected green ones. */
function drawPolyRoomVertexEdit(ctx: CanvasRenderingContext2D, vp: Viewport, inst: ProductInstance): void {
  if (pass.polyRoomVertexEditId !== inst.id || !inst.polygon) return;
  const ring = inst.polygon;
  const toS = (p: Vec2): Vec2 => vp.worldToScreen(instanceToWorld(inst, p));
  // Selected wall segment: the wall's own band FILLED blue, mitres included, and
  // stroked in the same blue so the band's amber outline is covered too. Stroking
  // a line down the edge instead leaves the mitre triangles and both outline
  // strokes amber, which is what reads as "a blue outline over an amber wall".
  const e = pass.polyRoomSelectedEdge;
  if (e != null && e >= 0 && e < ring.length) {
    const bands = polyWallBand(inst, e).filter((b) => b.length >= 3);
    if (bands.length) {
      ctx.save();
      ctx.beginPath();
      for (const b of bands) {
        b.map(toS).forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
        ctx.closePath();
      }
      ctx.fillStyle = VERTEX_EDIT_SELECTED;
      ctx.fill();
      ctx.strokeStyle = VERTEX_EDIT_SELECTED;
      // Wide enough to swallow the room's own 2px amber selection outline, which
      // rides the band's outer face.
      ctx.lineWidth = 3;
      ctx.lineJoin = 'round';
      ctx.stroke();
      ctx.restore();
    }
  }
  // Corner handles: all green, the selected one repainted blue on top. A pane's
  // jambs are not corners — they only slide along their wall — so they wear a pale
  // glass blue instead.
  const joint = (i: number): boolean => !!inst.polygonJoints?.[i];
  drawGrips(ctx, ring.filter((_, i) => !joint(i)).map(toS), VERTEX_EDIT_GREEN);
  const jambs = ring.filter((_, i) => joint(i)).map(toS);
  if (jambs.length) drawGrips(ctx, jambs, VERTEX_EDIT_JAMB);
  const v = pass.polyRoomSelectedVertex;
  if (v != null && v >= 0 && v < ring.length) drawGrips(ctx, [toS(ring[v])], VERTEX_EDIT_SELECTED);
}


function drawHandles(ctx: CanvasRenderingContext2D, vp: Viewport, inst: ProductInstance): void {
  ctx.save();
  ctx.fillStyle = '#ffd166';
  ctx.strokeStyle = '#1e1f24';
  ctx.lineWidth = 1;

  if (inst.nodes && inst.nodes.length >= 2) {
    // Polyline bench: a grip at each node, no rotation knob.
    drawGrips(ctx, instanceHandles(inst).map((h) => vp.worldToScreen(h.world)));
    ctx.restore();
    return;
  }

  // A resize-locked part shows no resize handles (only the rotation knob below).
  if (!pass.selectionResizeLocked) drawGrips(ctx, instanceHandles(inst).map((h) => vp.worldToScreen(h.world)));

  // Rotation knob: a stem off the back edge ending in a round handle.
  const edge = vp.worldToScreen(instanceToWorld(inst, { x: 0, y: inst.depth / 2 }));
  const knob = rotationHandleScreen(vp, inst);
  ctx.beginPath();
  ctx.moveTo(edge.x, edge.y);
  ctx.lineTo(knob.x, knob.y);
  ctx.strokeStyle = '#ffd166';
  ctx.lineWidth = 1.5;
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(knob.x, knob.y, ROTATE_RADIUS_PX, 0, Math.PI * 2);
  ctx.fillStyle = '#ffd166';
  ctx.strokeStyle = '#1e1f24';
  ctx.lineWidth = 1;
  ctx.fill();
  ctx.stroke();
  ctx.restore();
}

function drawGlass(
  ctx: CanvasRenderingContext2D,
  vp: Viewport,
  inst: ProductInstance,
  pending: GlassSegment | null
): void {
  // A glazing's panes and door are drawn as the glazing (glazingPlan.ts).
  const segs: GlassSegment[] = (inst.glass || []).filter((g) => !glazingOfPane(inst, g.id));
  segs.push(...doorGlassSpans(inst).filter((d) => roomDoor(inst, d.door)?.kind === 'plain').map((d) => d.seg)); // every outer-wall door pane is glass
  if (pending) segs.push(pending);
  if (segs.length === 0) return;

  ctx.save();
  ctx.lineWidth = GLASS_STROKE_WIDTH;
  for (const seg of segs) {
    ctx.fillStyle = glassFill(seg);
    ctx.strokeStyle = glassStroke(seg);
    // Fill the pane's pieces as one path (nonzero) so notch corners don't
    // double their alpha where connector and run rectangles overlap.
    ctx.beginPath();
    // The pane being dragged is measured along its whole WALL (it may cross jambs).
    for (const poly of glassPolysLocal(seg === pending && seg.edge != null ? wallView(inst) : inst, seg)) {
      const pts = poly.map((p) => vp.worldToScreen(instanceToWorld(inst, p)));
      pts.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
      ctx.closePath();
    }
    // The selected pane gets a halo UNDER its own fill: a pane is 10 mm thick, so
    // an outline drawn over it would cover the pane's colour entirely at any
    // normal zoom.
    if (seg.id && pass.selectedGlassIds.has(seg.id)) {
      ctx.save();
      ctx.strokeStyle = '#22e650'; // a green selection halo (matches the add-hover green)
      ctx.lineWidth = 5;
      ctx.stroke();
      ctx.restore();
    }
    ctx.fill();
    ctx.stroke();
  }
  ctx.restore();
}

/**
 * Draw wall openings (glass-less "windows"): the wall band over each opening is
 * knocked down to 50% opacity so you see through it, with a thin frame outline.
 */
/** The holes the parts set into a room's walls cut through them (`roomWallCuts`:
 *  a display in the sauna's wood): their outlines dashed in the wall, which
 *  stands above the plan's cut. */
function drawRoomWallCuts(ctx: CanvasRenderingContext2D, vp: Viewport, inst: ProductInstance): void {
  const cuts = roomWallCuts(inst, pass.wallCutParts);
  if (!cuts.length) return;
  ctx.save();
  ctx.setLineDash([4, 3]);
  ctx.lineWidth = 1;
  ctx.strokeStyle = pass.light ? '#333333' : '#d0d4da';
  for (const seg of cuts) {
    ctx.beginPath();
    openingHoleBand(inst, seg).map((p) => vp.worldToScreen(instanceToWorld(inst, p))).forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
    ctx.closePath();
    ctx.stroke();
  }
  ctx.restore();
}

/** The water risers in a room's wall (risers.ts): each a filled circle in its
 *  water's colour, of the parts `shown`. */
function drawRoomRisers(ctx: CanvasRenderingContext2D, vp: Viewport, inst: ProductInstance, shown: (partId: string) => boolean): void {
  const risers = pass.risers.filter((r) => r.room === inst.id && shown(r.src));
  if (!risers.length) return;
  ctx.save();
  ctx.lineWidth = 1;
  ctx.strokeStyle = pass.light ? '#000000' : '#ffffff';
  for (const r of risers) {
    ctx.beginPath();
    riserRing(r).map((p) => vp.worldToScreen(p)).forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
    ctx.closePath();
    ctx.fillStyle = RISER_COLORS[r.water];
    ctx.fill();
    ctx.stroke();
  }
  ctx.restore();
}

function drawOpenings(
  ctx: CanvasRenderingContext2D,
  vp: Viewport,
  inst: ProductInstance,
  pending: GlassSegment | null
): void {
  const segs: GlassSegment[] = [...(inst.openings || [])];
  if (pending) segs.push(pending);
  if (segs.length === 0) return;

  const screenBand = (seg: GlassSegment) =>
    openingBandLocal(seg === pending && seg.edge != null ? wallView(inst) : inst, seg).map((p) => vp.worldToScreen(instanceToWorld(inst, p)));

  // 1) Fade the wall to 50% under the openings (remove half the existing alpha).
  // All bands are filled as ONE path so overlapping openings (e.g. two meeting in
  // a corner) fade exactly once instead of compounding into a darker patch.
  ctx.save();
  ctx.globalCompositeOperation = 'destination-out';
  ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
  ctx.beginPath();
  for (const seg of segs) {
    const pts = screenBand(seg);
    pts.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
    ctx.closePath();
  }
  ctx.fill();
  ctx.restore();

  // 2) A thin frame so the opening reads as a window; a selected opening gets the
  // same green selection halo a selected pane does.
  for (const seg of segs) {
    const pts = screenBand(seg);
    ctx.save();
    const sel = !!seg.id && pass.selectedOpeningIds.has(seg.id);
    ctx.strokeStyle = sel ? '#22e650' : darken(wallColorOf(inst), 0.5);
    ctx.lineWidth = sel ? 3 : 1;
    ctx.beginPath();
    pts.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
    ctx.closePath();
    ctx.stroke();
    ctx.restore();
  }
}

/** Connection line colour by discipline. */
export const CONNECTION_COLORS: Record<NonNullable<Connection['type']>, string> = {
  electrical: '#ff8c1a', // orange (distinct from the yellow selection highlight)
  plumbing: '#4aa3ff',
  mechanical: '#9fb0c4',
  drainage: '#4db891',
};

/** Canvas dash pattern (screen px) for a connection line style. `dashed` keeps
 *  the historical [9,6] look so existing drawings render unchanged. */
function connectionDash(style: NonNullable<Connection['style']>): number[] {
  switch (style) {
    case 'solid': return [];
    case 'dotted': return [1.5, 5];
    case 'dash-dot': return [9, 5, 1.5, 5];
    case 'dashed':
    default: return [9, 6];
  }
}

function drawConnections(
  ctx: CanvasRenderingContext2D,
  vp: Viewport,
  drawing: Drawing,
  selectedId: string | null,
  numbers: Map<string, string> | null,
  shown: (c: Connection) => boolean,
  only?: ReadonlySet<string>,
  list: readonly Connection[] = drawing.connections || []
): void {
  if (!list.length) return;
  const SPACING = 5; // screen px between parallel lines

  // Build each visible connection's world centre-line route — through the ONE end
  // resolver (glued point, free point, or the object's centre). `list` is the
  // connections painted, bottom first.
  const routes = list
    .filter(shown)
    .map((c) => {
      const g = connGeom(drawing, c);
      return g ? { c, g, path: g.plan.pts } : null;
    })
    .filter((r): r is { c: Connection; g: ConnGeom; path: Vec2[] } => r !== null);

  // Straight connections (no waypoints) sharing an endpoint pair would draw on
  // top of each other; lay their parallel-line bundles side by side along the
  // perpendicular, each lane sized by that connection's own line count.
  const GAP = SPACING * 1.5;
  const baseOff = new Map<string, number>();
  const pairs = new Map<string, { c: Connection; path: Vec2[] }[]>();
  for (const r of routes) {
    if (r.path.length !== 2 || !r.c.from || !r.c.to) continue; // has waypoints or a free end → keep its own route
    const key = [r.c.from, r.c.to].sort().join('|');
    (pairs.get(key) ?? pairs.set(key, []).get(key)!).push(r);
  }
  for (const g of pairs.values()) {
    if (g.length < 2) continue;
    g.sort((a, b) => (a.c.id < b.c.id ? -1 : 1)); // stable lane order
    const widths = g.map((r) => (Math.max(1, r.c.lines || 1) - 1) * SPACING);
    const total = widths.reduce((s, w) => s + w, 0) + GAP * (g.length - 1);
    let cursor = -total / 2;
    g.forEach((r, idx) => {
      baseOff.set(r.c.id, cursor + widths[idx] / 2);
      cursor += widths[idx] + GAP;
    });
  }

  // Where two routes of the SAME discipline cross, the later one hops over the
  // earlier so equal-coloured lines that only cross read as not touching. The hop
  // is placed where the ACTUAL drawn lines meet — so an offset underlying line (a
  // lane offset, or one strand of a multi-line bundle) is hopped at the right
  // spot. Each route's drawn segments are accumulated; the next route's strands
  // test against them (only earlier routes; never a route against its own lines).
  const drawnSegs: Array<{ type: ConnectionType; a: Vec2; b: Vec2 }> = [];
  // Schedule-number tags are collected here and drawn after every line (a separate
  // layer) so lines never cover them, then nudged apart so they don't overlap.
  const tags: Array<{ id: string; x: number; y: number; num: string; color: string; w: number }> = [];

  ctx.save();
  for (const { c, g, path } of routes) {
    const n = Math.max(1, c.lines || 1);
    const sel = selectedId === c.from || selectedId === c.to || c.id === pass.selectedConnId;
    const color = CONNECTION_COLORS[g.discipline];
    if (g.pipe) {
      if (!only || only.has(c.id)) drawPipePlan(ctx, vp, g.pipe.plan, sel ? '#ffd166' : color);
      if (only && !only.has(c.id)) continue;
      if (c.waypoints && c.waypoints.length) drawGrips(ctx, c.waypoints.map((w) => vp.worldToScreen(w)), color);
      const num = numbers?.get(c.id);
      if (num) {
        const sm = vp.worldToScreen(g.tag);
        tags.push({ id: c.id, x: sm.x, y: sm.y, num, color, w: 0 });
      }
      continue;
    }
    const type = g.discipline;
    const base = baseOff.get(c.id) ?? 0; // lane offset for overlapping same-pair connections
    const style = c.style ?? 'dashed';
    // A connection painted in another run of the stacking: its strands still
    // count for the hops of the routes after it, but it is not drawn here.
    const skip = !!only && !only.has(c.id);
    ctx.strokeStyle = sel ? '#ffd166' : color;
    ctx.lineWidth = c.thickness ?? 1.5;
    ctx.lineCap = style === 'dotted' ? 'round' : 'butt';
    ctx.setLineDash(connectionDash(style));
    const mySegs: Array<{ a: Vec2; b: Vec2 }> = [];
    for (let i = 0; i < n; i++) {
      const offWorld = ((i - (n - 1) / 2) * SPACING + base) / vp.scale;
      // Offset the whole route as a continuous mitred polyline, so at a mid-route
      // vertex each strand's two segments meet instead of stepping apart.
      const off = offsetPolyline(path, offWorld, false, BAND_MITRE);
      for (let s = 0; s < off.length - 1; s++) {
        const a = off[s], b = off[s + 1];
        // Hop where this strand crosses an earlier route's actual drawn strand.
        const hopScreen: Vec2[] = [];
        for (const seg of drawnSegs) {
          if (seg.type !== type) continue;
          const x = segCross(a, b, seg.a, seg.b);
          if (x) hopScreen.push(vp.worldToScreen(x));
        }
        if (!skip) strokeWithHumps(ctx, vp.worldToScreen(a), vp.worldToScreen(b), hopScreen, 6);
        mySegs.push({ a, b });
      }
    }
    // Only later routes hop over this one, so add its strands after drawing it.
    for (const seg of mySegs) drawnSegs.push({ type, a: seg.a, b: seg.b });
    if (skip) continue;
    // Fixed mid-route vertices, as grips (right-click to delete). They show on
    // every route, not only a selected one, so they carry the route's colour.
    if (c.waypoints && c.waypoints.length) {
      drawGrips(ctx, c.waypoints.map((w) => vp.worldToScreen(w)), color);
    }
    // Schedule number tag at the route midpoint (half the total length along it),
    // collected to draw on top once all lines are down.
    const num = numbers?.get(c.id);
    if (num) {
      const mid = { ...g.tag };
      if (base) { // keep the tag on the offset lane
        const p0 = path[0], p1 = path[path.length - 1];
        const L = Math.hypot(p1.x - p0.x, p1.y - p0.y) || 1;
        mid.x += (-(p1.y - p0.y) / L) * base / vp.scale;
        mid.y += ((p1.x - p0.x) / L) * base / vp.scale;
      }
      const sm = vp.worldToScreen(mid);
      tags.push({ id: c.id, x: sm.x, y: sm.y, num, color, w: 0 });
    }
  }
  ctx.restore();

  // Tag layer: measure, separate overlapping tags, then draw them over the lines.
  ctx.font = 'bold 13px sans-serif';
  for (const t of tags) t.w = ctx.measureText(t.num).width + 10;
  separateTags(tags, 19);
  for (const t of tags) {
    drawTag(ctx, t.x, t.y, t.num, t.color);
    pass.hits.connTags.push({ id: t.id, x: t.x, y: t.y, w: t.w, h: 19 });
  }
}

/** A pipe from above, to scale (`pipePlan`): its sides, a light fill, its
 *  centreline, its ends closed, and a crossed circle where it rises or drops. */
function drawPipePlan(ctx: CanvasRenderingContext2D, vp: Viewport, plan: PipePlan, color: string): void {
  const S = (pts: Vec2[]): Vec2[] => pts.map((p) => vp.worldToScreen(p));
  const [left, right] = plan.sides.map(S);
  const centre = S(plan.centre);
  ctx.save();
  ctx.setLineDash([]);
  ctx.lineJoin = 'round';
  if (centre.length >= 2) {
    ctx.beginPath();
    left.forEach((p, i) => (i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)));
    for (let i = right.length - 1; i >= 0; i--) ctx.lineTo(right[i].x, right[i].y);
    ctx.closePath();
    ctx.globalAlpha = 0.18;
    ctx.fillStyle = color;
    ctx.fill();
    ctx.globalAlpha = 1;
    ctx.strokeStyle = color;
    ctx.lineWidth = 1.2;
    ctx.stroke();
    ctx.beginPath();
    ctx.setLineDash([10, 3, 2, 3]);
    ctx.lineWidth = 0.6;
    centre.forEach((p, i) => (i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)));
    ctx.stroke();
    ctx.setLineDash([]);
  }
  const rr = plan.r * vp.scale;
  for (const q of plan.risers) {
    const p = vp.worldToScreen(q);
    ctx.beginPath();
    ctx.arc(p.x, p.y, Math.max(2, rr), 0, Math.PI * 2);
    ctx.fillStyle = '#ffffff';
    ctx.fill();
    ctx.strokeStyle = color;
    ctx.lineWidth = 1.2;
    ctx.stroke();
    const k = Math.max(2, rr) * Math.SQRT1_2;
    ctx.beginPath();
    ctx.moveTo(p.x - k, p.y - k); ctx.lineTo(p.x + k, p.y + k);
    ctx.moveTo(p.x - k, p.y + k); ctx.lineTo(p.x + k, p.y - k);
    ctx.lineWidth = 0.8;
    ctx.stroke();
  }
  ctx.restore();
}


/** The connection whose schedule tag is under a screen point (+ `pad` px), topmost
 *  (last drawn) first, or null. */
export function connTagAt(hits: RenderHits, p: Vec2, pad = 2, skip?: (id: string) => boolean): string | null {
  for (let i = hits.connTags.length - 1; i >= 0; i--) {
    const b = hits.connTags[i];
    if (skip?.(b.id)) continue;
    if (Math.abs(p.x - b.x) <= b.w / 2 + pad && Math.abs(p.y - b.y) <= b.h / 2 + pad) return b.id;
  }
  return null;
}

/**
 * Nudge overlapping tag rectangles (each `w`×`h`, centred at x,y) apart along the
 * axis of least overlap, so they shift as little as possible from their lines.
 */
export function separateTags(tags: Array<{ x: number; y: number; w: number }>, h: number): void {
  const PAD = 3;
  for (let pass = 0; pass < 20; pass++) {
    let moved = false;
    for (let i = 0; i < tags.length; i++) {
      for (let j = i + 1; j < tags.length; j++) {
        const A = tags[i], B = tags[j];
        const dx = B.x - A.x, dy = B.y - A.y;
        const ox = (A.w + B.w) / 2 + PAD - Math.abs(dx);
        const oy = h + PAD - Math.abs(dy);
        if (ox <= 0 || oy <= 0) continue; // not overlapping
        if (ox < oy) { const s = ((dx >= 0 ? 1 : -1) * ox) / 2; A.x -= s; B.x += s; }
        else { const s = ((dy >= 0 ? 1 : -1) * oy) / 2; A.y -= s; B.y += s; }
        moved = true;
      }
    }
    if (!moved) break;
  }
}

/** A connection badge (a coloured pill: dark fill, coloured border + text). The
 *  text height is `textPx` (default 13 px, the model view's fixed on-screen size);
 *  a schematic sheet passes a paper-scaled height so the badge is a fixed 10 pt on
 *  the page. The pill, padding and radius scale with the text so it stays in
 *  proportion. */
export function drawTag(ctx: CanvasRenderingContext2D, x: number, y: number, text: string, color: string, textPx = 13): void {
  ctx.save();
  ctx.setLineDash([]);
  ctx.font = `bold ${textPx}px sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const w = ctx.measureText(text).width + textPx * (10 / 13);
  ctx.fillStyle = '#11141a';
  ctx.strokeStyle = color;
  ctx.lineWidth = Math.max(1, textPx * (1.5 / 13));
  ctx.beginPath();
  const h = textPx * (19 / 13);
  const rx = x - w / 2;
  const ry = y - h / 2;
  ctx.roundRect ? ctx.roundRect(rx, ry, w, h, textPx * (5 / 13)) : ctx.rect(rx, ry, w, h);
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = color;
  ctx.fillText(text, x, y + 0.5);
  ctx.restore();
}

function drawPolygon(ctx: CanvasRenderingContext2D, vp: Viewport, poly: Polygon, selected: boolean, showHandles: boolean, selectedEdge = -1): void {
  if (poly.points.length < 2) return;
  if (poly.ellipse) { drawEllipsePolygon(ctx, vp, poly, selected, showHandles); return; }
  const ring = (pts: Vec2[]) => {
    const sp = pts.map((p) => vp.worldToScreen(p));
    sp.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
    ctx.closePath();
  };
  ctx.save();
  // Outer ring plus any holes as one path, filled even-odd so the holes show.
  ctx.beginPath();
  ring(poly.points);
  for (const hole of poly.holes || []) if (hole.length >= 3) ring(hole);
  ctx.fillStyle = hexToRgba(poly.fill, poly.fillOpacity);
  ctx.fill('evenodd');
  const accent = '#ffd166';
  ctx.lineWidth = selected ? 2.5 : 1.5;
  ctx.strokeStyle = selected ? accent : hexToRgba(poly.stroke, poly.strokeOpacity);
  ctx.lineJoin = 'round';
  ctx.stroke();
  // Highlight a selected edge.
  if (selectedEdge >= 0 && selectedEdge < poly.points.length) {
    const a = vp.worldToScreen(poly.points[selectedEdge]);
    const b = vp.worldToScreen(poly.points[(selectedEdge + 1) % poly.points.length]);
    ctx.beginPath();
    ctx.moveTo(a.x, a.y);
    ctx.lineTo(b.x, b.y);
    ctx.strokeStyle = '#4aa3ff';
    ctx.lineWidth = 4;
    ctx.stroke();
  }
  // Resize handles + rotation knob only for a lone selection (not a multi-select).
  const locked = !!poly.edgeConstraints?.some(Boolean);
  if (showHandles) {
    // Temporary light-blue dimension lines on every face (double-click one to edit it).
    // Use the overlap-avoiding renderer so labels don't collide, like every other shape.
    if (poly.points.length >= 3) drawLineDimensions(ctx, vp, poly.points, true, poly.edgeLengths);
    drawShapeResizeHandles(ctx, vp, poly.points, accent);
    if (!locked) drawShapeRotateKnob(ctx, vp, poly.points, accent); // a locked polygon can't rotate
  }
  // Constraint badges on every constrained edge of a selected polygon: h/v locks
  // and parallel/perpendicular/equal/collinear relations (a length lock is shown
  // inline on its dimension figure, not as a badge).
  if (selected) {
    drawConstraintBadges(ctx, vp, poly.points, { locks: poly.edgeConstraints, relations: poly.relations });
  }
  ctx.restore();
}

// Flooring fills/hatches live in floorHatch.ts (shared with the paper sheet's
// flooring-schedule swatch and the DXF); the plan draws them below.

/** A flooring area: tinted fill + material hatch (clipped to the ring), outline,
 *  a fall arrow to the drain on a sloping floor, then the drain itself. Drawn on
 *  the layer below the rooms so walls and benches cover it. */
function drawFlooring(
  ctx: CanvasRenderingContext2D, vp: Viewport, floor: Flooring,
  selected: boolean, drainInst: ProductInstance | null, drainSel: boolean,
): void {
  const pts = floor.points;
  if (pts.length < 3) return;
  const style = FLOOR_STYLE[floor.kind];
  const sp = pts.map((p) => vp.worldToScreen(p));
  const ring = (): void => { ctx.beginPath(); sp.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y))); ctx.closePath(); };
  ctx.save();
  ring();
  ctx.fillStyle = style.fill;
  ctx.fill();
  // Hatch, clipped to the ring.
  ctx.save();
  ring();
  ctx.clip();
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const p of sp) { minX = Math.min(minX, p.x); minY = Math.min(minY, p.y); maxX = Math.max(maxX, p.x); maxY = Math.max(maxY, p.y); }
  const step = Math.max(6, FLOOR_HATCH_MM * vp.scale);
  if (style.hatch === 'grid') { // orthogonal tile grid
    ctx.strokeStyle = style.line;
    ctx.lineWidth = 1;
    ctx.beginPath();
    for (let x = Math.floor(minX / step) * step; x <= maxX; x += step) { ctx.moveTo(x, minY); ctx.lineTo(x, maxY); }
    for (let y = Math.floor(minY / step) * step; y <= maxY; y += step) { ctx.moveTo(minX, y); ctx.lineTo(maxX, y); }
    ctx.stroke();
  } else if (style.hatch === 'concrete') {
    hatchConcrete(ctx, minX, minY, maxX, maxY, step, style.line);
  } else {
    hatchGrout(ctx, minX, minY, maxX, maxY, step, style.line);
  }
  ctx.restore();
  // Outline.
  ring();
  ctx.strokeStyle = selected ? '#ffd166' : style.stroke;
  ctx.lineWidth = selected ? 2.5 : 1.5;
  ctx.lineJoin = 'round';
  ctx.stroke();
  // Fall arrows clustered around the drain (sloping floors only).
  if (floorIsSloping(floor.kind) && floor.drain) {
    const rPx = drainInst ? (Math.max(drainInst.width, drainInst.depth) / 2) * vp.scale : null;
    drawFallArrows(ctx, sp, vp.worldToScreen(floor.drain), rPx, style.stroke);
  }
  ctx.restore();
  // The drain itself, on top of the floor.
  if (drainInst) {
    if (drainInst.category === 'canalina') drawCanalina(ctx, vp, drainInst, drainSel);
    else drawDrain(ctx, vp, drainInst, drainSel);
    // While the floor is selected, a rotate knob off the drain's +Y edge (drag it
    // to spin the drain, 15° snap) — matches floorDrainRotateHandle's hit test.
    if (selected) {
      const off = drainInst.depth / 2 + 24 / vp.scale;
      const base = vp.worldToScreen(instanceToWorld(drainInst, { x: 0, y: drainInst.depth / 2 }));
      const knob = vp.worldToScreen(instanceToWorld(drainInst, { x: 0, y: off }));
      ctx.save();
      ctx.strokeStyle = '#ffd166'; ctx.fillStyle = '#ffd166'; ctx.lineWidth = 1.5;
      ctx.beginPath(); ctx.moveTo(base.x, base.y); ctx.lineTo(knob.x, knob.y); ctx.stroke();
      ctx.beginPath(); ctx.arc(knob.x, knob.y, 5, 0, Math.PI * 2); ctx.fill();
      ctx.restore();
    }
  }
}

/** A room's ceiling marks projected to the screen (`ceilingMarksProjected`):
 *  its lines, each arrow as a 28 px shaft centred on its anchor (tail → tip,
 *  downhill), and the crown figure's point. The ONE projection the overlay draws
 *  and a press reads. */
export function ceilingMarksScreen(vp: Viewport, inst: ProductInstance, marks: CeilingMarks): ReturnType<typeof ceilingMarksProjected> {
  return ceilingMarksProjected(marks, (p) => vp.worldToScreen(instanceToWorld(inst, p)));
}

/** A room's ceiling on the plan, in its own paint slot (over what stands in it):
 *  the underside's dashed lines, the fall arrows, and — for a selected room, not
 *  while its corners are being edited — the crown figure. Their ink is the walls'
 *  colour darkened, a PRINT colour: on the model canvas (`sampleUnder`, which
 *  samples what is drawn under a screen box) it is lifted like a dimension's
 *  wherever it runs over a dark backdrop — the canvas, a dark floor. */
function drawCeilingOverlay(
  ctx: CanvasRenderingContext2D, vp: Viewport, inst: ProductInstance,
  sampleUnder: ((box: { x0: number; y0: number; x1: number; y1: number }) => LumaProbe | null) | null,
): void {
  if (isFlatCeiling(inst)) return;
  const shown = ceilingMarksShown(inst, { all: pass.showCeilings, selected: pass.ceilingSelectedIds.has(inst.id), single: pass.ceilingRoomId === inst.id });
  if (!shown.lines.length && !shown.arrows.length && !shown.crown) return;
  const sc = ceilingMarksScreen(vp, inst, shown);
  const col = darkenHex(wallColorOf(inst), 0.45);
  let sample: LumaProbe | null = null;
  if (sampleUnder) {
    const pts = [...sc.lines.flatMap((l) => l.pts), ...sc.arrows.flatMap((a) => [a.tail, a.tip]), ...(sc.crown ? [sc.crown.at] : [])];
    const PAD = 120; // arrowheads, and the crown figure set off its point
    sample = sampleUnder({
      x0: Math.min(...pts.map((p) => p.x)) - PAD, y0: Math.min(...pts.map((p) => p.y)) - PAD,
      x1: Math.max(...pts.map((p) => p.x)) + PAD, y1: Math.max(...pts.map((p) => p.y)) + PAD,
    });
  }
  const ink = makeInkResolver(col, sample);
  /** A polyline in runs of one ink, its dashes carried on across them. */
  const stroke = (pts: Vec2[]): void => {
    if (!sample) {
      ctx.strokeStyle = col;
      ctx.beginPath(); pts.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y))); ctx.stroke();
      return;
    }
    let along = 0;
    for (let i = 1; i < pts.length; i++) {
      for (const run of inkRuns(pts[i - 1], pts[i], ink)) {
        ctx.strokeStyle = run.color;
        ctx.lineDashOffset = along + Math.hypot(run.a.x - pts[i - 1].x, run.a.y - pts[i - 1].y);
        ctx.beginPath(); ctx.moveTo(run.a.x, run.a.y); ctx.lineTo(run.b.x, run.b.y); ctx.stroke();
      }
      along += Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y);
    }
    ctx.lineDashOffset = 0;
  };
  ctx.save();
  ctx.lineWidth = 1.2;
  ctx.setLineDash([6, 4]);
  for (const l of sc.lines) stroke(l.closed && l.pts.length > 2 ? [...l.pts, l.pts[0]] : l.pts);
  ctx.setLineDash([]);
  ctx.lineWidth = 1.5;
  for (const a of sc.arrows) {
    stroke([a.tail, a.tip]);
    ctx.strokeStyle = ctx.fillStyle = ink.at(a.tip.x, a.tip.y);
    drawArrow(ctx, a.tip, a.tail);
  }
  if (sc.crown && pass.polyRoomVertexEditId !== inst.id) {
    const text = formatDimension(sc.crown.value, pass.dimStyle);
    ctx.font = '11px system-ui, sans-serif';
    let x: number, y: number;
    const d = sc.crown.side, t = sc.crown.toward;
    if (t) {
      // Where lines meet (a pyramid's or a dome's apex, under its grip): 14 px
      // out into the gap chosen on screen, the figure starting there — or
      // ending there when the gap opens to the left.
      x = sc.crown.at.x + t.x * 14; y = sc.crown.at.y + t.y * 14;
      if (t.x < 0) x -= 8 + ctx.measureText(text).width;
    } else if (d) {
      // On a line of its own (a gable's ridge): 14 px off it, square to it, on
      // the side the figure's text runs AWAY from the line.
      const k = d.x < -1e-9 || (Math.abs(d.x) <= 1e-9 && d.y > 0) ? -14 : 14;
      x = sc.crown.at.x + d.x * k; y = sc.crown.at.y + d.y * k;
    } else {
      // Just inside the room from the crown point (on the outline), clear of the
      // wall's own figures.
      const c = vp.worldToScreen({ x: inst.x, y: inst.y });
      const dx = c.x - sc.crown.at.x, dy = c.y - sc.crown.at.y, L = Math.hypot(dx, dy) || 1;
      x = sc.crown.at.x + (dx / L) * 22; y = sc.crown.at.y + (dy / L) * 22;
    }
    const w = ctx.measureText(text).width;
    ctx.fillStyle = ink.over([{ x, y }, { x: x + 8 + w / 2, y }, { x: x + 8 + w, y }]);
    ctx.beginPath(); ctx.moveTo(x, y - 5); ctx.lineTo(x + 5, y + 4); ctx.lineTo(x - 5, y + 4); ctx.closePath(); ctx.fill();
    ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
    ctx.fillText(text, x + 8, y);
  }
  ctx.restore();
}

/** Short fall arrows ringing the drain (`fallArrows`, the DXF's rule too, in
 *  screen px): one per floor edge, pointing INWARD at the drain from that edge's
 *  direction, their tips just outside the drain footprint (`rPx`) so they sit
 *  close to it without overlapping. */
function drawFallArrows(ctx: CanvasRenderingContext2D, ring: Vec2[], drain: Vec2, rPx: number | null, color: string): void {
  ctx.save();
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineWidth = 1.5;
  for (const { tail, tip } of fallArrows(ring, drain, rPx, FALL_ARROW_PX)) {
    ctx.beginPath(); ctx.moveTo(tail.x, tail.y); ctx.lineTo(tip.x, tip.y); ctx.stroke();
    drawArrow(ctx, tip, tail);
  }
  ctx.restore();
}

const REVCLOUD_SCALLOP_PX = 16; // target scallop width on screen (px), so the
                                // scallops stay visible at any zoom or cloud size
export const REVCLOUD_COLOR = '#e63946';

function drawRevisionCloud(ctx: CanvasRenderingContext2D, vp: Viewport, cloud: RevisionCloud, selected: boolean, showHandles: boolean): void {
  if (cloud.points.length < 2) return;
  // A stored scallop size (mm) overrides the screen-adaptive default (as px).
  const scallopPx = cloud.scallopMm && cloud.scallopMm > 0 ? Math.max(4, cloud.scallopMm * vp.scale) : REVCLOUD_SCALLOP_PX;
  ctx.save();
  ctx.beginPath();
  // Each scallop sampled in world space and projected, so it stays correct at any zoom.
  let started = false;
  for (const arc of cloudScallops(cloud.points, scallopPx / vp.scale)) {
    let sweep = arc.endDeg - arc.startDeg;
    while (sweep <= 0) sweep += 360;
    const rPx = arc.radius * vp.scale;
    const segCount = Math.max(4, Math.min(24, Math.ceil(((sweep / 360) * rPx) / 3)));
    for (let k = 0; k <= segCount; k++) {
      const ang = ((arc.startDeg + (sweep * k) / segCount) * Math.PI) / 180;
      const sp = vp.worldToScreen({ x: arc.center.x + arc.radius * Math.cos(ang), y: arc.center.y + arc.radius * Math.sin(ang) });
      if (!started) { ctx.moveTo(sp.x, sp.y); started = true; }
      else ctx.lineTo(sp.x, sp.y);
    }
  }
  ctx.closePath();
  ctx.lineJoin = 'round';
  ctx.lineWidth = selected ? 1.8 : 1;
  ctx.strokeStyle = selected ? '#ffd166' : (cloud.color || REVCLOUD_COLOR);
  ctx.stroke();
  // Same selection affordances as a polygon: bounding-box resize handles, a
  // rotation knob and per-face dimension lines. (Green per-vertex handles appear
  // only in vertex-edit mode, drawn by the shared editVerticesId pass.)
  if (showHandles) {
    const accent = '#ffd166';
    if (cloud.points.length >= 3) drawLineDimensions(ctx, vp, cloud.points, true);
    drawShapeResizeHandles(ctx, vp, cloud.points, accent);
    drawShapeRotateKnob(ctx, vp, cloud.points, accent);
  }
  ctx.restore();
}

/** Live dashed-outline preview of a revision cloud being drawn (the actual
 *  scalloped shape only appears once committed — a plain outline is enough to
 *  show the loop being traced). */
function drawPendingRevisionCloud(ctx: CanvasRenderingContext2D, vp: Viewport, draft: Vec2[], hover: Vec2 | null): void {
  const verts = hover ? [...draft, hover] : draft;
  if (!verts.length) return;
  ctx.save();
  if (verts.length >= 2) {
    const pts = verts.map((p) => vp.worldToScreen(p));
    ctx.beginPath();
    pts.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
    ctx.closePath();
    ctx.fillStyle = 'rgba(230, 57, 70, 0.12)';
    ctx.fill();
    ctx.setLineDash([6, 4]);
    ctx.strokeStyle = REVCLOUD_COLOR;
    ctx.lineWidth = 1.5;
    ctx.stroke();
    ctx.setLineDash([]);
  }
  ctx.fillStyle = REVCLOUD_COLOR;
  for (const q of draft) {
    const p = vp.worldToScreen(q);
    ctx.beginPath();
    ctx.arc(p.x, p.y, 3.5, 0, Math.PI * 2);
    ctx.fill();
  }
  if (hover) drawCursorReadout(ctx, (q) => vp.worldToScreen(q), hover, draft.length ? draft[draft.length - 1] : null);
  ctx.restore();
}

/** Draw a circle/ellipse polygon as a smooth ellipse fitted to its point bbox. */
function drawEllipsePolygon(ctx: CanvasRenderingContext2D, vp: Viewport, poly: Polygon, selected: boolean, showHandles: boolean): void {
  // Recover the ellipse from its point ring through the shared fit, so the drawn
  // centre and the one the object snap offers are the same point by construction.
  const pts = poly.points;
  const n = pts.length || 1;
  const fit = ellipseFromRing(pts);
  if (!fit) return;
  const cx = fit.center.x, cy = fit.center.y;
  const p0 = pts[0] ?? { x: cx, y: cy };
  const pq = pts[Math.round(n / 4) % n] ?? { x: cx, y: cy };
  const c = vp.worldToScreen({ x: cx, y: cy });
  const p0s = vp.worldToScreen(p0);
  const pqs = vp.worldToScreen(pq);
  const rxS = Math.hypot(p0s.x - c.x, p0s.y - c.y);
  const ryS = Math.hypot(pqs.x - c.x, pqs.y - c.y);
  const phi = Math.atan2(p0s.y - c.y, p0s.x - c.x); // screen-space tilt of the major axis
  ctx.save();
  ctx.beginPath();
  ctx.ellipse(c.x, c.y, rxS, ryS, phi, 0, Math.PI * 2);
  ctx.fillStyle = hexToRgba(poly.fill, poly.fillOpacity);
  ctx.fill();
  ctx.lineWidth = selected ? 2.5 : 1.5;
  ctx.strokeStyle = selected ? '#ffd166' : hexToRgba(poly.stroke, poly.strokeOpacity);
  ctx.stroke();
  if (showHandles) {
    const rx = Math.hypot(p0.x - cx, p0.y - cy), ry = Math.hypot(pq.x - cx, pq.y - cy);
    const circle = Math.abs(rx - ry) < 1;
    // Bbox resize handles map cleanly to the axes only when axis-aligned (or a
    // circle); a tilted ellipse is resized via its Radius X/Y fields instead.
    const rotW = Math.atan2(p0.y - cy, p0.x - cx);
    const m = ((rotW % (Math.PI / 2)) + Math.PI / 2) % (Math.PI / 2);
    if (circle || m < 0.02 || Math.PI / 2 - m < 0.02) {
      drawShapeResizeHandles(ctx, vp, poly.points);
      // Bounding-box width/height dimensions (only meaningful axis-aligned).
      let mnx = Infinity, mny = Infinity, mxx = -Infinity, mxy = -Infinity;
      for (const p of poly.points) { mnx = Math.min(mnx, p.x); mny = Math.min(mny, p.y); mxx = Math.max(mxx, p.x); mxy = Math.max(mxy, p.y); }
      drawLineDimensions(ctx, vp, [{ x: mnx, y: mny }, { x: mxx, y: mny }, { x: mxx, y: mxy }, { x: mnx, y: mxy }], true);
    }
    if (!circle) drawShapeRotateKnob(ctx, vp, poly.points); // an ellipse can be rotated
  }
  ctx.restore();
}

/** A small badge marking a locked edge: a horizontal or vertical bar with end caps. */
/** The constraint bundle a ring carries, for drawing per-edge badges. */
interface EdgeConsView {
  locks?: Array<'h' | 'v' | null>;
  relations?: ShapeRelation[];
}
type EdgeBadgeKind =
  | 'collinear'
  | 'equal'
  | 'h'
  | 'parallel'
  | 'perpendicular'
  | 'v';

/** The badge glyphs an edge should show: its orientation lock and any edge-pair
 *  relation it takes part in. A length lock is shown inline on the edge's
 *  dimension figure (a padlock beside the number), not as a badge here. */
function edgeBadgeKinds(cons: EdgeConsView, e: number): EdgeBadgeKind[] {
  const out: EdgeBadgeKind[] = [];
  const o = cons.locks?.[e];
  if (o === 'h' || o === 'v') out.push(o);
  for (const r of cons.relations ?? []) {
    if ('edges' in r && r.edges.includes(e)) out.push(r.type);
  }
  return out;
}

/** How much room the padlock takes beside a dimension label (glyph + its gap). */
const LOCK_SCALE = 1.35;
const LOCK_GLYPH_W = 6 * LOCK_SCALE + 5;
/** The equal-length badge takes the same room as the padlock. */
const EQUAL_GLYPH_W = LOCK_GLYPH_W;

/** The "=" that marks a pane held equal to its group — two short parallel strokes,
 *  centred at (x,y), matching the padlock's footprint. */
function drawEqualGlyph(ctx: CanvasRenderingContext2D, x: number, y: number, s = 1): void {
  ctx.save();
  ctx.lineWidth = 1.4;
  ctx.strokeStyle = ctx.fillStyle;
  ctx.beginPath();
  ctx.moveTo(x - 2.6 * s, y - 1.4 * s); ctx.lineTo(x + 2.6 * s, y - 1.4 * s);
  ctx.moveTo(x - 2.6 * s, y + 1.4 * s); ctx.lineTo(x + 2.6 * s, y + 1.4 * s);
  ctx.stroke();
  ctx.restore();
}

/** The padlock that marks a pinned figure: a body rectangle with a shackle arc
 *  above it. Shared by the locked-edge constraint badge and the locked wall
 *  dimensions, so one lock reads as one thing wherever it appears. */
function drawLockGlyph(ctx: CanvasRenderingContext2D, x: number, y: number, s = 1): void {
  ctx.save();
  ctx.lineWidth = 1.4;
  ctx.strokeStyle = ctx.fillStyle;
  ctx.strokeRect(x - 3 * s, y - 1.5 * s, 6 * s, 5 * s);
  ctx.beginPath();
  ctx.arc(x, y - 1.5 * s, 2.1 * s, Math.PI, 2 * Math.PI);
  ctx.stroke();
  ctx.restore();
}

/** One circular constraint badge with an intuitive glyph. */
function drawConstraintBadge(ctx: CanvasRenderingContext2D, x: number, y: number, kind: EdgeBadgeKind): void {
  const r = 9;
  ctx.save();
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(20, 22, 28, 0.9)';
  ctx.strokeStyle = '#4aa3ff';
  ctx.lineWidth = 1.5;
  ctx.fill();
  ctx.stroke();
  ctx.strokeStyle = '#9cd0ff';
  ctx.fillStyle = '#9cd0ff';
  if (kind === 'h' || kind === 'v') {
    ctx.lineWidth = 2;
    ctx.beginPath();
    if (kind === 'h') { ctx.moveTo(x - 5, y); ctx.lineTo(x + 5, y); }
    else { ctx.moveTo(x, y - 5); ctx.lineTo(x, y + 5); }
    ctx.stroke();
  } else {
    const glyph = kind === 'parallel' ? '∥' : kind === 'perpendicular' ? '⊥' : kind === 'equal' ? '=' : '≡';
    ctx.font = 'bold 12px Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(glyph, x, y + 0.5);
  }
  ctx.restore();
}

/** Draw the constraint badges for every constrained edge of a ring (world pts):
 *  lifted just off the segment (toward the ring interior, clear of the outward
 *  temporary dimensions) and spread along the edge so several badges — or the
 *  h/v tick sitting on the line — stay separated and readable. */
function drawConstraintBadges(ctx: CanvasRenderingContext2D, vp: Viewport, ringWorld: Vec2[], cons: EdgeConsView, closed = true): void {
  const n = ringWorld.length;
  const last = closed ? n : n - 1;
  const scr = ringWorld.map((p) => vp.worldToScreen(p));
  let cx = 0, cy = 0;
  for (const p of scr) { cx += p.x; cy += p.y; }
  cx /= n; cy /= n;
  const OFFSET = 13; // px off the segment line, so the badge doesn't merge with it
  const spacing = 22; // px between adjacent badges on the same edge
  for (let e = 0; e < last; e++) {
    const kinds = edgeBadgeKinds(cons, e);
    if (!kinds.length) continue;
    const a = scr[e], b = scr[(e + 1) % n];
    let tx = b.x - a.x, ty = b.y - a.y;
    const L = Math.hypot(tx, ty) || 1;
    tx /= L; ty /= L;
    // Perpendicular pointing toward the ring's interior (away from centroid → flip).
    let nx = -ty, ny = tx;
    const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2;
    if ((mx - cx) * nx + (my - cy) * ny > 0) { nx = -nx; ny = -ny; }
    const bx = mx + nx * OFFSET, by = my + ny * OFFSET;
    const start = -((kinds.length - 1) / 2) * spacing;
    kinds.forEach((k, i) => drawConstraintBadge(ctx, bx + tx * (start + i * spacing), by + ty * (start + i * spacing), k));
  }
}

// Salt-wall cross-section (mm), derived from the reference drawing.
const SALT_FRAME_W = 50;        // brown end-frame width
const SALT_FRAME_H = 191;       // brown end-frame height (the overall height)
const SALT_BODY_H = 71;         // pink body thickness
const SALT_BODY_BOTTOM = 24;    // gap from the body's bottom to the frame's bottom
const SALT_BODY_TOP = SALT_FRAME_H - SALT_BODY_BOTTOM - SALT_BODY_H; // body top offset below the top edge (= 96)
const SALT_FRAME_COLOR = '#7c5a32';
const SALT_BODY_COLOR = '#f6c4d2';

/** Unit direction and screen-downward normal of a salt wall's top edge. */
function saltAxes(a: Vec2, b: Vec2): { dx: number; dy: number; nx: number; ny: number; len: number } {
  const dxw = b.x - a.x, dyw = b.y - a.y;
  const len = Math.hypot(dxw, dyw) || 1;
  const dx = dxw / len, dy = dyw / len;
  return { dx, dy, nx: dy, ny: -dx, len }; // n points to the body side (screen-down)
}

/** The outer footprint of a salt wall (top edge extruded down by the frame
 *  height), in world coordinates — used for hit-testing and the selection outline. */
export function saltWallHull(a: Vec2, b: Vec2): Vec2[] {
  const { nx, ny } = saltAxes(a, b);
  return [a, b, { x: b.x + nx * SALT_FRAME_H, y: b.y + ny * SALT_FRAME_H }, { x: a.x + nx * SALT_FRAME_H, y: a.y + ny * SALT_FRAME_H }];
}

/** Draw a salt wall: a stretchable pink body between two fixed brown end frames.
 *  startCap/endCap control whether the end frames appear (false for interior
 *  joints of a multi-segment polyline). */
function drawSaltWall(ctx: CanvasRenderingContext2D, vp: Viewport, a: Vec2, b: Vec2, selected: boolean, startCap = true, endCap = true, prev: Vec2 | null = null, next: Vec2 | null = null): void {
  const { dx, dy, nx, ny, len } = saltAxes(a, b);
  if (len < 1) return;
  const off = (p: Vec2, sd: number, sn: number): Vec2 => ({ x: p.x + dx * sd + nx * sn, y: p.y + dy * sd + ny * sn });
  const fill = (poly: Vec2[], color: string): void => {
    const sp = poly.map((p) => vp.worldToScreen(p));
    ctx.beginPath();
    sp.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
    ctx.closePath();
    ctx.fillStyle = color;
    ctx.fill();
  };
  ctx.save();
  // Pink body first (its ends tuck under the frames), then the two brown frames.
  // The body's top/bottom edges mitre with the neighbouring segments at each
  // interior joint so a bent salt wall meets cleanly at its corners.
  const bTop = SALT_BODY_TOP, bBot = SALT_BODY_TOP + SALT_BODY_H;
  fill([ribbonJoint(prev, a, b, bTop), ribbonJoint(a, b, next, bTop), ribbonJoint(a, b, next, bBot), ribbonJoint(prev, a, b, bBot)], SALT_BODY_COLOR);
  if (startCap) fill([a, off(a, SALT_FRAME_W, 0), off(a, SALT_FRAME_W, SALT_FRAME_H), off(a, 0, SALT_FRAME_H)], SALT_FRAME_COLOR);
  if (endCap) fill([off(b, -SALT_FRAME_W, 0), b, off(b, 0, SALT_FRAME_H), off(b, -SALT_FRAME_W, SALT_FRAME_H)], SALT_FRAME_COLOR);
  if (selected) {
    const hull = saltWallHull(a, b).map((p) => vp.worldToScreen(p));
    ctx.beginPath();
    hull.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
    ctx.closePath();
    ctx.lineWidth = 2;
    ctx.strokeStyle = '#ffd166';
    ctx.stroke();
  }
  ctx.restore();
}

/** Draw a heated bench: a copper gradient body with 3 staggered red heating
 *  waves. `a`/`b` are the wall-side (back) corners; `depth` is the bench
 *  depth (mm). The waves are inset from the edges and offset from each other,
 *  matching the reference plan symbol. */
/**
 * The two long-edge lengths (mm) of a heated-bench ribbon: `back` is the node
 * line (a→b→…); `front` is the depth-offset edge, corner-mitred exactly as it is
 * drawn. They differ where the path bends; `Math.max` of the two is the bench's
 * running length for power. A straight (2-node) bench has back === front.
 */
export function heatedBenchSideLengths(nodes: Vec2[], depth: number): { back: number; front: number } {
  let back = 0;
  for (let i = 0; i + 1 < nodes.length; i++) back += distance(nodes[i], nodes[i + 1]);
  const front = nodes.map((p, i) => ribbonJoint(i > 0 ? nodes[i - 1] : null, p, i + 1 < nodes.length ? nodes[i + 1] : null, depth));
  let frontLen = 0;
  for (let i = 0; i + 1 < front.length; i++) frontLen += distance(front[i], front[i + 1]);
  return { back, front: frontLen };
}

function drawHeatedBench(ctx: CanvasRenderingContext2D, vp: Viewport, a: Vec2, b: Vec2, depth: number, selected: boolean, prev: Vec2 | null = null, next: Vec2 | null = null): void {
  const { dx, dy, nx, ny, len } = saltAxes(a, b);
  if (len < 1) return;
  const off = (p: Vec2, sd: number, sn: number): Vec2 => ({ x: p.x + dx * sd + nx * sn, y: p.y + dy * sd + ny * sn });

  ctx.save();

  // Back edge is the node line (a→b); the front edge (offset by depth) mitres
  // with the neighbouring segments so bends meet cleanly at each corner.
  const fa = ribbonJoint(prev, a, b, depth), fb = ribbonJoint(a, b, next, depth);
  const corners = [a, b, fb, fa].map(p => vp.worldToScreen(p));
  ctx.beginPath();
  corners.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
  ctx.closePath();
  // Body fill — vertical copper gradient, darker at the wall side (back),
  // lighter toward the front, like the reference render.
  if (selected) {
    ctx.fillStyle = '#ffd166';
  } else {
    const back = vp.worldToScreen(off(a, len / 2, 0));
    const front = vp.worldToScreen(off(a, len / 2, depth));
    const g = ctx.createLinearGradient(back.x, back.y, front.x, front.y);
    g.addColorStop(0, '#a9742f');
    g.addColorStop(1, '#dcb47e');
    ctx.fillStyle = g;
  }
  ctx.fill();

  // 3 staggered red sine waves at ~1/4, 1/2 and 3/4 depth. Each row is inset
  // from the ends and shifted horizontally so the crests don't line up.
  ctx.strokeStyle = selected ? '#7a2200' : '#9a2600';
  ctx.lineWidth = Math.max(1.2, vp.scale * 1.6);
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  const wavelength = 320;   // mm per full hump cycle (~5 humps at 2 m)
  const amp = 38;           // wave amplitude (mm)
  // Per-row [startFrac, endFrac, depthFrac] — staggered starts/ends.
  const rows: [number, number, number][] = [
    [0.10, 0.84, 0.27],
    [0.18, 0.93, 0.50],
    [0.12, 0.86, 0.73],
  ];
  for (const [sf, ef, df] of rows) {
    const start = len * sf, end = len * ef;
    const span = end - start;
    if (span < 1) continue;
    const wd = depth * df;
    const steps = Math.max(24, Math.round(span / 10));
    ctx.beginPath();
    for (let s = 0; s <= steps; s++) {
      const sd = start + (s / steps) * span;
      const sn = wd + amp * Math.sin(((sd - start) / wavelength) * Math.PI * 2);
      const p = vp.worldToScreen(off(a, sd, sn));
      if (s === 0) ctx.moveTo(p.x, p.y);
      else ctx.lineTo(p.x, p.y);
    }
    ctx.stroke();
  }

  // Outline
  ctx.beginPath();
  corners.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
  ctx.closePath();
  ctx.strokeStyle = selected ? '#ffd166' : '#7a5030';
  ctx.lineWidth = selected ? 2 : 1;
  ctx.stroke();

  ctx.restore();
}

/** A slatted sauna bench segment: the same mitred ribbon as a heated bench, but
 *  filled as a row of seat slats (doghe) running along the length, stacked across
 *  the depth from the node line (back) to the mitred front edge — matching how a
 *  sauna-room bench draws its slats. */
function drawSaunaBench(ctx: CanvasRenderingContext2D, toS: (p: Vec2) => Vec2, seg: SaunaBenchSegment, color: string, selected: boolean): void {
  ctx.save();
  ctx.lineJoin = 'round';
  const body = seg.body.map(toS);
  const trace = (): void => { ctx.beginPath(); body.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y))); ctx.closePath(); };
  // Base body fill (faint), so gaps between slats read as the bench, not the page.
  trace();
  ctx.fillStyle = selected ? hexToRgba('#ffd166', 0.30) : hexToRgba(color, 0.28);
  ctx.fill();
  // Slats: strips across the depth, spanning the full length.
  for (const slat of seg.slats) {
    const q = slat.map(toS);
    ctx.beginPath(); q.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y))); ctx.closePath();
    ctx.fillStyle = selected ? hexToRgba('#ffd166', 0.6) : hexToRgba(color, 0.72);
    ctx.fill();
    ctx.strokeStyle = darken(color, 0.28);
    ctx.lineWidth = 1;
    ctx.stroke();
  }
  // Outer body outline.
  trace();
  ctx.strokeStyle = selected ? '#4aa3ff' : darken(color, 0.35);
  ctx.lineWidth = selected ? 2 : 1.2;
  ctx.stroke();
  ctx.restore();
}

/** Flex4: a cladding panel with dense horizontal slats, a recessed fixture box
 *  near the wall side, and one thick accent stripe ~40 % of the way down. */
function drawFlex4(ctx: CanvasRenderingContext2D, vp: Viewport, inst: ProductInstance, selected: boolean): void {
  const hw = inst.width / 2;
  const hd = inst.depth / 2;
  const d  = inst.depth;

  const L = (lx: number, ly: number): Vec2 => vp.worldToScreen(instanceToWorld(inst, { x: lx, y: ly }));

  // Build a closed canvas path from local (x, y) pairs.
  const closedPath = (pts: Array<[number, number]>): void => {
    ctx.beginPath();
    pts.forEach(([x, y], i) => { const s = L(x, y); if (i === 0) ctx.moveTo(s.x, s.y); else ctx.lineTo(s.x, s.y); });
    ctx.closePath();
  };

  // Tessellate a rounded rect into local (x, y) pairs.
  // x0 < x1 and y0 < y1 in local (y-up) coordinates.
  const roundRectPts = (x0: number, y0: number, x1: number, y1: number, r: number): Array<[number, number]> => {
    const R = Math.min(r, (x1 - x0) / 2, (y1 - y0) / 2);
    const N = 6;
    const pts: Array<[number, number]> = [];
    const arc = (cx: number, cy: number, a0: number, a1: number): void => {
      for (let i = 0; i <= N; i++) { const a = a0 + (a1 - a0) * (i / N); pts.push([cx + R * Math.cos(a), cy + R * Math.sin(a)]); }
    };
    // CCW in local-y-up space: bottom-left → bottom-right → top-right → top-left
    arc(x0 + R, y0 + R, Math.PI,       Math.PI * 1.5);
    arc(x1 - R, y0 + R, Math.PI * 1.5, Math.PI * 2);
    arc(x1 - R, y1 - R, 0,             Math.PI * 0.5);
    arc(x0 + R, y1 - R, Math.PI * 0.5, Math.PI);
    return pts;
  };

  ctx.save();

  // 1. Background fill.
  closedPath([[-hw, -hd], [hw, -hd], [hw, hd], [-hw, hd]]);
  ctx.fillStyle = selected ? '#f5ecd2' : '#e8dcc8';
  ctx.fill();

  // 2. Dense horizontal slat lines at 25 mm pitch.
  ctx.strokeStyle = selected ? '#3a2e1e' : '#2a1e14';
  ctx.lineWidth = Math.max(0.5, vp.scale * 0.5);
  for (let y = -hd + 25; y < hd; y += 25) {
    const a = L(-hw, y), b = L(hw, y);
    ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();
  }

  // 3. Recessed fixture panel: 5.5 %–18 % of depth from the wall side (y = −hd),
  //    ±31.5 % of width, with 14 mm corner radius.
  const cutBot = -hd + d * 0.180; // upper local-y bound (farther from wall)
  const cutTop = -hd + d * 0.055; // lower local-y bound (closer to wall)
  const cutHw  = inst.width * 0.315;
  const rrPts  = roundRectPts(-cutHw, cutTop, cutHw, cutBot, 14);
  closedPath(rrPts);
  ctx.fillStyle = selected ? '#faf5e2' : '#f5edd8';
  ctx.fill();
  closedPath(rrPts);
  ctx.strokeStyle = selected ? '#c0a878' : '#a89060';
  ctx.lineWidth = Math.max(0.8, vp.scale * 0.8);
  ctx.stroke();

  // 4. Accent stripe: a noticeably thicker line ~40 % from the wall side.
  const accentY = -hd + d * 0.40;
  const acA = L(-hw, accentY), acB = L(hw, accentY);
  ctx.strokeStyle = selected ? '#3a2418' : '#1a1208';
  ctx.lineWidth = Math.max(1.8, vp.scale * 3.5);
  ctx.beginPath(); ctx.moveTo(acA.x, acA.y); ctx.lineTo(acB.x, acB.y); ctx.stroke();

  // 5. Outline.
  closedPath([[-hw, -hd], [hw, -hd], [hw, hd], [-hw, hd]]);
  ctx.strokeStyle = selected ? '#ffd166' : '#7a6444';
  ctx.lineWidth   = selected ? 2 : 1;
  ctx.stroke();

  ctx.restore();
}

// Cable channel (canalina) appearance.
const CANALINA_FILL = '#ffff00';
export const CANALINA_STROKE = '#19c819';
export const CANALINA_INSET = 6; // mm: wall thickness between the outer edge and the inner outline
// Floor drain grate appearance.
export const DRAIN_COLOR = '#ffff00';
const DRAIN_SPOKES = 10;

/** A cable channel: a yellow rectangle (full footprint) with a green outer edge
 *  and an inset green inner outline. Stretches with the footprint width. */
function drawCanalina(ctx: CanvasRenderingContext2D, vp: Viewport, inst: ProductInstance, selected: boolean): void {
  const hw = inst.width / 2, hd = inst.depth / 2;
  const m = Math.min(CANALINA_INSET, hw - 1, hd - 1);
  const rect = (x0: number, y0: number, x1: number, y1: number): void => {
    const pts = [{ x: x0, y: y0 }, { x: x1, y: y0 }, { x: x1, y: y1 }, { x: x0, y: y1 }]
      .map((p) => vp.worldToScreen(instanceToWorld(inst, p)));
    ctx.beginPath();
    pts.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
    ctx.closePath();
  };
  ctx.save();
  rect(-hw, -hd, hw, hd);
  ctx.fillStyle = CANALINA_FILL;
  ctx.fill();
  ctx.strokeStyle = selected ? '#ffd166' : CANALINA_STROKE;
  ctx.lineWidth = selected ? 2.5 : 1.5;
  ctx.stroke();
  rect(-hw + m, -hd + m, hw - m, hd - m);
  ctx.lineWidth = 1.5;
  ctx.strokeStyle = CANALINA_STROKE;
  ctx.stroke();
  ctx.restore();
}

/** A floor drain grate: a yellow ring with radial wedges (spokes) inside. Fits
 *  the instance footprint (circular by default). */
function drawDrain(ctx: CanvasRenderingContext2D, vp: Viewport, inst: ProductInstance, selected: boolean): void {
  const rx = inst.width / 2, ry = inst.depth / 2;
  if (rx < 1 || ry < 1) return;
  const L = (lx: number, ly: number): Vec2 => vp.worldToScreen(instanceToWorld(inst, { x: lx, y: ly }));
  const color = selected ? '#ffd166' : DRAIN_COLOR;
  ctx.save();
  // Radial wedges, each a sector with a gap to its neighbours.
  ctx.fillStyle = color;
  const gap = 0.34, rim = 0.84;
  for (let i = 0; i < DRAIN_SPOKES; i++) {
    const a0 = ((i + gap / 2) / DRAIN_SPOKES) * Math.PI * 2;
    const a1 = ((i + 1 - gap / 2) / DRAIN_SPOKES) * Math.PI * 2;
    const c = L(0, 0);
    ctx.beginPath();
    ctx.moveTo(c.x, c.y);
    const steps = 8;
    for (let s = 0; s <= steps; s++) {
      const a = a0 + (a1 - a0) * (s / steps);
      const p = L(Math.cos(a) * rx * rim, Math.sin(a) * ry * rim);
      ctx.lineTo(p.x, p.y);
    }
    ctx.closePath();
    ctx.fill();
  }
  // Outer ring.
  ctx.strokeStyle = color;
  ctx.lineWidth = Math.max(2, Math.min(rx, ry) * vp.scale * 0.16);
  ctx.beginPath();
  const ringSteps = 64;
  for (let s = 0; s <= ringSteps; s++) {
    const a = (s / ringSteps) * Math.PI * 2;
    const p = L(Math.cos(a) * rx, Math.sin(a) * ry);
    if (s === 0) ctx.moveTo(p.x, p.y);
    else ctx.lineTo(p.x, p.y);
  }
  ctx.closePath();
  ctx.stroke();
  ctx.restore();
}

/**
 * Render an infrared emitter in plan view (top-down), matching the reference
 * plan symbol. The emitter mounts against a sauna wall (y = -hd in local coords)
 * and radiates into the room (y+). Wall snap line = y = -hd (the magenta line).
 *
 * Layout (derived from reference: 102 mm inner gap at wall, 111 mm at room side,
 * 124 mm total depth):
 *   - Dark-red heating bar centred at the wall face, 48 % of product half-width
 *   - Two beige side frames: outer edges flush with footprint, inner edges splay
 *     slightly outward from 48 % to 50 % of half-width over the full depth
 *   - Three wavy red IR lines in the transparent centre gap
 *   - No outer bounding stroke — uncoloured areas are transparent
 */
function drawInfraredEmitter(ctx: CanvasRenderingContext2D, vp: Viewport, inst: ProductInstance, selected: boolean): void {
  const hw = inst.width / 2;
  const hd = inst.depth / 2;
  const S = (lx: number, ly: number): Vec2 => vp.worldToScreen(instanceToWorld(inst, { x: lx, y: ly }));
  const poly = (pts: Vec2[]): void => {
    ctx.beginPath();
    pts.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
    ctx.closePath();
  };

  ctx.save();

  // Geometry derived from reference symbol proportions.
  const barHW  = hw * 0.48;          // half-width of dark-red bar (≈ 102/212)
  const splay  = hw * 0.043;         // inner-edge outward shift at far end (≈ 4.5/106)
  const barTop = -hd - hd * 0.15;    // bar pokes slightly behind the wall face
  const barBot = -hd + hd * 0.12;    // and just barely into the room
  const panelY0 = -hd;               // panels start exactly at the wall snap line
  const panelY1 = hd;                // panels run the full product depth

  // --- side frames (beige): fill only, no outer stroke ---
  const panelColor = selected ? '#ffd166' : '#f3d3a0';
  ctx.fillStyle = panelColor;
  // Left frame: outer edge vertical, inner edge splays slightly outward (leftward).
  poly([S(-hw, panelY0), S(-barHW, panelY0), S(-barHW - splay, panelY1), S(-hw, panelY1)]);
  ctx.fill();
  // Right frame (mirror).
  poly([S(hw, panelY0), S(barHW, panelY0), S(barHW + splay, panelY1), S(hw, panelY1)]);
  ctx.fill();

  // --- IR radiation waves: 3 vertical sine curves in the centre gap ---
  const waveY0 = barBot;
  const waveY1 = panelY1 * 0.9;
  if (waveY1 > waveY0) {
    ctx.strokeStyle = selected ? '#ffd166' : '#c01818';
    ctx.lineWidth = Math.max(0.8, vp.scale * 0.7);
    const cols = [-barHW * 0.45, 0, barHW * 0.45];
    const amp   = barHW * 0.14;
    const steps = 60;
    for (const cx of cols) {
      ctx.beginPath();
      for (let s = 0; s <= steps; s++) {
        const t  = s / steps;
        const ly = waveY0 + t * (waveY1 - waveY0);
        const lx = cx + amp * Math.sin(t * Math.PI * 2 * 7);
        const p  = S(lx, ly);
        if (s === 0) ctx.moveTo(p.x, p.y); else ctx.lineTo(p.x, p.y);
      }
      ctx.stroke();
    }
  }

  // --- heating element bar (dark red) at the wall face, drawn on top ---
  poly([S(-barHW, barTop), S(barHW, barTop), S(barHW, barBot), S(-barHW, barBot)]);
  ctx.fillStyle = selected ? '#ffd166' : '#8b2222';
  ctx.fill();
  if (selected) {
    ctx.strokeStyle = '#ffd166';
    ctx.lineWidth = 2;
    ctx.stroke();
  }

  if (selected && !pass.multiSelect) { drawHandles(ctx, vp, inst); drawLineDimensions(ctx, vp, instanceCorners(inst), true); }
  ctx.restore();
}

function drawWall(ctx: CanvasRenderingContext2D, vp: Viewport, wall: Wall, selected: boolean): void {
  if (wall.style === 'line') { drawLine(ctx, vp, wall, selected); return; }
  const band = wallBand(wall.points, wall.thickness, wall.align);
  if (band.length < 3) return;
  // The band cut at its doors, panes and openings (`wallSolidRuns`): each solid
  // stretch is its own outline, so the cut ends read as jambs.
  ctx.save();
  ctx.beginPath();
  for (const loop of wallSolidRuns(wall).flat()) {
    loop.map((p) => vp.worldToScreen(p)).forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
    ctx.closePath();
  }
  ctx.fillStyle = hexToRgba(wall.fill, wall.fillOpacity);
  ctx.fill();
  ctx.lineWidth = selected ? 2.5 : 1;
  ctx.strokeStyle = selected
    ? '#ffd166'
    : hexToRgba(wall.stroke ?? darken(wall.fill, 0.5), wall.strokeOpacity ?? wall.fillOpacity);
  ctx.lineJoin = 'round';
  ctx.stroke();
  for (const sp of wallFeatureSpans(wall)) drawWallFeature(ctx, vp, wall, sp, sp.f.id === pass.selectedWallFeatureId && selected);
  // A part's pocket (its flush box), under it in the wall: its outline dashed,
  // the wall above the plan's cut.
  for (const pk of wallPockets(wall, pass.wallCutParts)) {
    const fr = wallSegFrame(wall, pk.seg);
    if (!fr) continue;
    ctx.beginPath();
    wallPocketQuad(fr, pk).map((p) => vp.worldToScreen(p)).forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
    ctx.closePath();
    ctx.setLineDash([4, 3]);
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.setLineDash([]);
  }
  if (pass.wallFeaturePreview?.wallId === wall.id) {
    const sp = wallFeatureSpans({ ...wall, features: [pass.wallFeaturePreview.feature] })[0];
    if (sp) drawWallFeature(ctx, vp, wall, sp, true);
  }
  if (selected) {
    drawShapeRotateKnob(ctx, vp, band);
    // Dimension both faces of the band (not the invisible centre-line), each on
    // its OWN outside. `wallBand` builds the band as [...leftFace,
    // ...rightFace.reversed()], one point per centre-line vertex, so walking it the
    // outside of every face is on the left (side +1) — a single "away from the
    // centroid" rule put both faces of a bent wall's leg on the same side, the
    // inner face's figure outside and the outer face's inside. Each label is
    // tagged with the band edge it measures (`DIM_TAG_FACE_EDGE`), which is what
    // the double-click edits.
    const n = wall.points.length;
    let cx = 0, cy = 0;
    for (const p of band) { cx += p.x; cy += p.y; }
    const centroid = { x: cx / band.length, y: cy / band.length };
    const placed: DimRect[] = [];
    for (const [start, face] of [[0, band.slice(0, n)], [n, band.slice(n)]] as const) {
      // One figure per segment: a closed wall's last point sits on its first, so
      // its closing segment is already the last one.
      const m = face.length - 1;
      // A pinned segment (`segLengths`) puts its padlock on both faces' figures.
      const segs: Array<{ a: Vec2; b: Vec2; tag: string; side: 1; locked: boolean }> = [];
      for (let i = 0; i < m; i++) {
        const s = start === 0 ? i : n - 2 - i;
        segs.push({ a: face[i], b: face[(i + 1) % face.length], tag: `${DIM_TAG_FACE_EDGE}${start + i}`, side: 1, locked: wallSegPin(wall, s) != null });
      }
      drawDimSegments(ctx, vp, segs, centroid, true, placed);
    }
  }
  ctx.restore();
}

/** One door, pane or opening of a drawn wall (`wallFeatures.ts`): a door's leaf
 *  and swing, a pane on the middle of the wall, an opening as the wall at half
 *  strength. A selected one gets the green halo a room's pane does. */
function drawWallFeature(ctx: CanvasRenderingContext2D, vp: Viewport, wall: Wall, sp: WallFeatureSpan, sel: boolean): void {
  const path = (pts: Vec2[]): void => {
    ctx.beginPath();
    pts.map((p) => vp.worldToScreen(p)).forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
    ctx.closePath();
  };
  ctx.save();
  if (sel) {
    path(wallFeatureQuad(sp));
    ctx.strokeStyle = '#22e650';
    ctx.lineWidth = 3;
    ctx.stroke();
  }
  if (sp.f.kind === 'opening') {
    path(wallFeatureQuad(sp));
    ctx.fillStyle = hexToRgba(wall.fill, wall.fillOpacity * 0.5);
    ctx.fill();
    ctx.strokeStyle = darken(wall.fill, 0.5);
    ctx.lineWidth = 1;
    ctx.stroke();
  } else if (sp.f.kind === 'glass') {
    path(wallPaneQuad(sp));
    ctx.fillStyle = glassFill(sp.f);
    ctx.strokeStyle = glassStroke(sp.f);
    ctx.lineWidth = GLASS_STROKE_WIDTH;
    ctx.fill();
    ctx.stroke();
  } else {
    const sw = wallDoorSwing(sp);
    const toS = (p: Vec2): Vec2 => vp.worldToScreen(p);
    ctx.strokeStyle = sel ? '#22e650' : '#e7b15a';
    ctx.lineWidth = 1.6;
    strokeSwing(ctx, sw, toS);
    const h = toS(sw.hinge);
    ctx.fillStyle = ctx.strokeStyle;
    ctx.beginPath();
    ctx.arc(h.x, h.y, 3, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

/** A `style: 'line'` wall: a single stroked polyline of `thickness` mm. When
 *  selected it also shows non-overlapping temporary dimensions for every segment. */
function drawLine(ctx: CanvasRenderingContext2D, vp: Viewport, wall: Wall, selected: boolean): void {
  const pts = wall.points;
  if (pts.length < 2) return;
  const sp = pts.map((p) => vp.worldToScreen(p));
  ctx.save();
  ctx.beginPath();
  sp.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
  if (polylineIsClosed(pts)) ctx.closePath(); // its joint is a corner, not two ends
  ctx.lineWidth = Math.max(1, wall.thickness * vp.scale);
  ctx.strokeStyle = selected ? '#ffd166' : hexToRgba(wall.fill, wall.fillOpacity ?? 1);
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.stroke();
  if (selected) {
    // (Its vertex grips come from main.ts selectionGrips, drawn with the others.)
    // The same band main.ts's hitShapeRotation reads — the knob was live but never drawn.
    drawShapeRotateKnob(ctx, vp, wallBand(pts, wall.thickness, wall.align));
    drawLineDimensions(ctx, vp, pts, isClosedPolyline(pts), wallSegPins(wall));
  }
  ctx.restore();
}

/**
 * Temporary dimensions for a selected line / wall / room outline: one per segment
 * (an orthogonal measurement for axis-aligned segments, the segment length for
 * diagonal ones). Each is offset perpendicular to its segment — for a closed
 * outline the preferred side is outward (away from the centroid) — trying the
 * other side and growing the offset until its label clears every dimension
 * already placed, so they never overlap.
 */
/** Temporary distance guides drawn while dragging an object: each is a short
 *  orthogonal dimension line (with arrows + a mm label) to a nearby wall/object. */
function drawDragGuides(ctx: CanvasRenderingContext2D, vp: Viewport, guides: Array<{ a: Vec2; b: Vec2; dist: number }>): void {
  ctx.save();
  ctx.font = '12px sans-serif';
  for (const g of guides) {
    const sa = vp.worldToScreen(g.a), sb = vp.worldToScreen(g.b);
    ctx.strokeStyle = '#ffd166';
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 3]);
    ctx.beginPath();
    ctx.moveTo(sa.x, sa.y);
    ctx.lineTo(sb.x, sb.y);
    ctx.stroke();
    ctx.setLineDash([]);
    drawArrow(ctx, sa, sb);
    drawArrow(ctx, sb, sa);
    const label = formatDim(g.dist);
    const mid = { x: (sa.x + sb.x) / 2, y: (sa.y + sb.y) / 2 };
    const lw = ctx.measureText(label).width;
    ctx.fillStyle = 'rgba(20, 21, 26, 0.85)';
    ctx.fillRect(mid.x - lw / 2 - 3, mid.y - 8, lw + 6, 15);
    ctx.fillStyle = '#ffe6a3';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(label, mid.x, mid.y);
  }
  ctx.restore();
}

type DimRect = { x0: number; y0: number; x1: number; y1: number };

/** Tag of the extra figure a chained wall draws for its FULL length. Its probe
 *  lands on the same edge as the chain's, so this is what tells a double-click to
 *  edit the wall length instead of whichever chain figure spans the midpoint. */
export const DIM_TAG_WALL_OVERALL = 'wall-overall';
/** Prefix of the tag a wall's face figure carries, followed by the band edge it
 *  measures. A wall's two faces lie a thickness apart — usually closer than a
 *  label's probe sits off its segment — so the probe alone can land on the OTHER
 *  face; the tag names the face outright. */
export const DIM_TAG_FACE_EDGE = 'face-edge:';

/** A drawn dimension label's footprint on screen: centre, rotation, half-size. */
// `probe` is a WORLD point just off the measured segment on the label's INTENDED
// side — the side the label prefers, even when it flipped to the other side to
// dodge an overlap. The double-click resolves which figure to edit from this
// probe, not from where the label actually landed, so an outer figure whose label
// spilled inside the room still edits that figure and not the wall behind it.
// `tag` names the KIND of figure the label belongs to when the probe alone can't
// say. A wall that hosts a door or pane draws its chain AND an overall figure for
// the whole wall; both probe to the same edge, so only the tag separates them.
type DimLabelBox = { cx: number; cy: number; ang: number; hw: number; hh: number; probe: Vec2; tag?: string };

/** The `probe` of the temporary dimension label under a screen point (+ `pad`
 *  px), or null. Callers resolve WHICH figure to edit from this, not the raw
 *  click, so the label's own drawn side can't send them to the wrong figure. */
function dimLabelBoxAt(hits: RenderHits, p: Vec2, pad: number): DimLabelBox | null {
  // Labels crowd near a corner — a wall's outer figure and the adjacent wall's
  // interior span can overlap. Return the NEAREST label (by centre distance), not
  // the first in draw order, or a click on one figure resolves to whichever
  // happens to be drawn first (which sent a corner figure's edit to the
  // neighbouring wall's interior dimension).
  let best: { box: DimLabelBox; d: number } | null = null;
  for (const b of hits.dimLabels) {
    const dx = p.x - b.cx, dy = p.y - b.cy;
    const c = Math.cos(b.ang), s = Math.sin(b.ang);
    if (Math.abs(dx * c + dy * s) <= b.hw + pad && Math.abs(-dx * s + dy * c) <= b.hh + pad) {
      const d = dx * dx + dy * dy;
      if (!best || d < best.d) best = { box: b, d };
    }
  }
  return best ? best.box : null;
}

export function dimLabelProbeAt(hits: RenderHits, p: Vec2, pad = 3): Vec2 | null {
  return dimLabelBoxAt(hits, p, pad)?.probe ?? null;
}

/** The `tag` of the temporary dimension label under a screen point — the SAME
 *  label `dimLabelProbeAt` resolves, so the two can never disagree about which
 *  figure was clicked. */
export function dimLabelTagAt(hits: RenderHits, p: Vec2, pad = 3): string | null {
  return dimLabelBoxAt(hits, p, pad)?.tag ?? null;
}

/** Whether a screen point lands on a temporary dimension's text (+ `pad` px). */
export function dimLabelAt(hits: RenderHits, p: Vec2, pad = 3): boolean {
  return dimLabelBoxAt(hits, p, pad) !== null;
}

/**
 * Draw a set of temporary dimension lines (one per segment), each stepped
 * outward to a free lane so labels never overlap each other (or any already in
 * `placed`). With `outward`, a segment prefers the side facing away from
 * `centroid` (so a closed outline's dimensions sit outside it); `opts.inward`
 * flips that so the labels sit toward the centroid (inside the outline), and
 * `opts.firstGapPx` overrides the first lane's distance from the edge.
 */
function drawDimSegments(
  ctx: CanvasRenderingContext2D,
  vp: Viewport,
  segs: Array<{ a: Vec2; b: Vec2; locked?: boolean; equal?: boolean; tag?: string; side?: 1 | -1 }>,
  centroid: Vec2,
  outward: boolean,
  placed: DimRect[] = [],
  opts: { inward?: boolean; firstGapPx?: number } = {}
): void {
  ctx.save();
  ctx.font = '12px sans-serif';
  // First lane sits clear of an object's rotation knob (a ~32 px stem+handle off
  // the back edge) and resize handles; further lanes stack outward from there.
  const edgeGap = (opts.firstGapPx ?? 44) / vp.scale;
  const laneGap = 20 / vp.scale;
  const gapForStep = (step: number): number => edgeGap + laneGap * (step - 1);
  const overlap = (a: DimRect, b: DimRect): boolean => a.x0 < b.x1 && a.x1 > b.x0 && a.y0 < b.y1 && a.y1 > b.y0;
  for (const { a, b, locked, equal, tag, side } of segs) {
    const len = distance(a, b);
    if (len < 1e-6) continue;
    const badgeW = locked ? LOCK_GLYPH_W : equal ? EQUAL_GLYPH_W : 0;
    const dx = (b.x - a.x) / len, dy = (b.y - a.y) / len;
    const nx = -dy, ny = dx; // perpendicular
    let order = [1, -1];
    if (side) order = [side, -side]; // the segment knows its own outside
    else if (outward || opts.inward) {
      const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2;
      let out = (mx - centroid.x) * nx + (my - centroid.y) * ny >= 0 ? 1 : -1;
      if (opts.inward) out = -out; // labels sit toward the centroid (inside)
      order = [out, -out];
    }
    // A world point a little off the segment on the INTENDED side (order[0]) —
    // recorded with the label so a double-click resolves the figure from here, not
    // from the label's actual (possibly flipped) position.
    const probeOff = 20 / vp.scale;
    const probe: Vec2 = { x: (a.x + b.x) / 2 + nx * order[0] * probeOff, y: (a.y + b.y) / 2 + ny * order[0] * probeOff };
    const label = formatDim(len);
    const lw = ctx.measureText(label).width + badgeW;
    let best: { sa: Vec2; sb: Vec2; rect: DimRect } | null = null;
    for (let step = 1; step <= 12 && !best; step++) {
      for (const side of order) {
        const off = gapForStep(step) * side;
        const sa = vp.worldToScreen({ x: a.x + nx * off, y: a.y + ny * off });
        const sb = vp.worldToScreen({ x: b.x + nx * off, y: b.y + ny * off });
        const mid = { x: (sa.x + sb.x) / 2, y: (sa.y + sb.y) / 2 };
        const rect = { x0: mid.x - lw / 2 - 5, y0: mid.y - 11, x1: mid.x + lw / 2 + 5, y1: mid.y + 11 };
        if (!placed.some((r) => overlap(r, rect))) { best = { sa, sb, rect }; break; }
      }
    }
    if (!best) {
      const off = gapForStep(12) * order[0];
      const sa = vp.worldToScreen({ x: a.x + nx * off, y: a.y + ny * off });
      const sb = vp.worldToScreen({ x: b.x + nx * off, y: b.y + ny * off });
      const mid = { x: (sa.x + sb.x) / 2, y: (sa.y + sb.y) / 2 };
      best = { sa, sb, rect: { x0: mid.x - lw / 2 - 5, y0: mid.y - 11, x1: mid.x + lw / 2 + 5, y1: mid.y + 11 } };
    }
    placed.push(best.rect);
    const { sa, sb } = best;
    const ea = vp.worldToScreen(a), eb = vp.worldToScreen(b);
    ctx.strokeStyle = '#7fb2ff';
    ctx.fillStyle = '#7fb2ff';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(ea.x, ea.y); ctx.lineTo(sa.x, sa.y); // extension lines
    ctx.moveTo(eb.x, eb.y); ctx.lineTo(sb.x, sb.y);
    ctx.moveTo(sa.x, sa.y); ctx.lineTo(sb.x, sb.y); // the dimension line
    ctx.stroke();
    drawArrow(ctx, sa, sb);
    drawArrow(ctx, sb, sa);
    const mid = { x: (sa.x + sb.x) / 2, y: (sa.y + sb.y) / 2 };
    const ang = Math.atan2(sb.y - sa.y, sb.x - sa.x);
    // Push the label onto the side of the dimension line facing AWAY from the
    // measured object — the extension-line direction (edge → line) is exactly
    // that. (A fixed baseline offset only reads right for horizontal lines.)
    let awx = sa.x - ea.x, awy = sa.y - ea.y;
    const al = Math.hypot(awx, awy) || 1; awx /= al; awy /= al;
    ctx.save();
    const lang = Math.abs(ang) > Math.PI / 2 ? ang + Math.PI : ang;
    pass.hits.dimLabels.push({ cx: mid.x + awx * 9, cy: mid.y + awy * 9, ang: lang, hw: lw / 2, hh: 7, probe, tag });
    ctx.translate(mid.x + awx * 9, mid.y + awy * 9);
    ctx.rotate(lang);
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#cfe2ff';
    // A pinned figure carries the same padlock the locked-edge constraint badge
    // draws, so "this number will not move on its own" reads the same everywhere.
    ctx.fillText(label, badgeW / 2, 0);
    if (locked) drawLockGlyph(ctx, -lw / 2 + 3 * LOCK_SCALE, 0, LOCK_SCALE);
    else if (equal) drawEqualGlyph(ctx, -lw / 2 + 3 * LOCK_SCALE, 0, LOCK_SCALE);
    ctx.restore();
  }
  ctx.restore();
}

/** A polyline whose ends meet reads as a closed loop for dimensioning, so its
 *  closing edge is measured and labels prefer the outward side (like a polygon). */
function isClosedPolyline(pts: Vec2[]): boolean {
  if (pts.length < 3) return false;
  const a = pts[0], b = pts[pts.length - 1];
  return Math.hypot(a.x - b.x, a.y - b.y) < 1; // within 1 mm
}

/**
 * A selected object's TEMPORARY ("face") dimensions are emitted from inside that
 * object's own draw pass, which happens long before the sections and annotations
 * — so a section painted over a selected room simply covered its figures. These
 * two entry points therefore QUEUE the draw instead of doing it, and `render()`
 * flushes the queue at the very end, beside the placed dimensions. Same rule,
 * same reason: a dimension is the measurement and nothing may bury it.
 *
 * Deferring is safe because both take WORLD points and project through `vp`, so
 * they do not depend on whatever transform their object had set — and
 * `pass.hits.dimLabels` is still filled within the same frame, before any input can
 * read it for the double-click hit test.
 */
function drawLineDimensions(ctx: CanvasRenderingContext2D, vp: Viewport, pts: Vec2[], closed = false, edgeLengths?: Array<number | null>): void {
  pass.deferredDims.push(() => drawLineDimensionsNow(ctx, vp, pts, closed, edgeLengths));
}

function drawRoomDimensions(ctx: CanvasRenderingContext2D, vp: Viewport, inst: ProductInstance): void {
  pass.deferredDims.push(() => drawRoomDimensionsNow(ctx, vp, inst));
}

function drawLineDimensionsNow(ctx: CanvasRenderingContext2D, vp: Viewport, pts: Vec2[], closed = false, edgeLengths?: Array<number | null>): void {
  if (pts.length < 2) return;
  let cx = 0, cy = 0;
  for (const p of pts) { cx += p.x; cy += p.y; }
  const centroid = { x: cx / pts.length, y: cy / pts.length };
  const segs: Array<{ a: Vec2; b: Vec2; locked?: boolean }> = [];
  const n = closed ? pts.length : pts.length - 1;
  // A length-locked edge draws the padlock beside its figure — the one inline
  // badge every pinned dimension uses, instead of a separate constraint badge.
  for (let i = 0; i < n; i++) segs.push({ a: pts[i], b: pts[(i + 1) % pts.length], locked: (edgeLengths?.[i] ?? null) != null });
  drawDimSegments(ctx, vp, segs, centroid, closed);
}

/** The inner (room-side) outline of a room in world coordinates: the drawn
 *  outline inset by the wall thickness. Empty when the walls would meet. */
function roomInnerWorld(inst: ProductInstance): Vec2[] {
  // The mitred inner ring, deliberately: this drives the interior clear-span
  // dimensions, which stay one per wall however the corners are rounded.
  if (isPolyRoom(inst)) return polyEdges(wallView(inst)).map((e) => instanceToWorld(inst, e.ia)); // one per WALL: a jamb adds no interior figure
  const t = wallThicknessOf(inst);
  const hw = inst.width / 2 - t, hd = inst.depth / 2 - t;
  if (hw <= 5 || hd <= 5) return [];
  return [
    instanceToWorld(inst, { x: -hw, y: -hd }), instanceToWorld(inst, { x: hw, y: -hd }),
    instanceToWorld(inst, { x: hw, y: hd }), instanceToWorld(inst, { x: -hw, y: hd }),
  ];
}

/** Interior clear dimensions along the inside of a room's walls: one per inner
 *  edge, drawn just inside the wall face (toward the room centre). */
function drawInnerRoomDimensions(ctx: CanvasRenderingContext2D, vp: Viewport, inst: ProductInstance): void {
  const inner = roomInnerWorld(inst);
  if (inner.length < 3) return;
  let cx = 0, cy = 0;
  for (const p of inner) { cx += p.x; cy += p.y; }
  const segs: Array<{ a: Vec2; b: Vec2 }> = [];
  for (let i = 0; i < inner.length; i++) {
    const a = inner[i], b = inner[(i + 1) % inner.length];
    // A wall hosting interior partitions reads as clear span | partition | clear
    // span, the same way the door wall's outer dimension splits — so each figure
    // is a real interior distance rather than one span straight through them.
    const splits = isPolyRoom(inst) ? innerWallDimSplits(inst, i) : [];
    if (!splits.length) { segs.push({ a, b }); continue; }
    let from = a;
    for (const sp of splits) {
      const fa = instanceToWorld(inst, sp.a), fb = instanceToWorld(inst, sp.b);
      segs.push({ a: from, b: fa }, { a: fa, b: fb });
      from = fb;
    }
    segs.push({ a: from, b });
  }
  drawDimSegments(ctx, vp, segs, { x: cx / inner.length, y: cy / inner.length }, false, [], { inward: true, firstGapPx: 16 });
}

/** World-space leader for a rounded corner's radius dimension: `on` sits at the
 *  middle of the outer arc, `far` is the label end, out along the bisector away
 *  from the room (`cornerRadiusLeaderLocal` — into the notch at a reflex corner).
 *  Shared by the drawing and by the double-click hit-test, so they can't disagree
 *  about where it is. */
export function cornerRadiusLeader(inst: ProductInstance, c: RoomCorner): { on: Vec2; far: Vec2 } {
  const { on, far } = cornerRadiusLeaderLocal(inst, c);
  return { on: instanceToWorld(inst, on), far: instanceToWorld(inst, far) };
}

/** Radius dimension for each rounded corner: a leader from the middle of the
 *  outer arc, pointing outward, labelled `R<radius>` — the one dimension a
 *  curved wall needs, since its length dimensions still measure the sharp
 *  corner. Drawn with the room's other automatic dimensions. */
function drawCornerRadiusDimensions(ctx: CanvasRenderingContext2D, vp: Viewport, inst: ProductInstance): void {
  const corners = roomCorners(inst);
  if (!corners.some(Boolean)) return;
  ctx.save();
  ctx.font = '12px sans-serif';
  ctx.textBaseline = 'middle';
  for (const c of corners) {
    if (!c) continue;
    const { on, far } = cornerRadiusLeader(inst, c);
    const a = vp.worldToScreen(on), b = vp.worldToScreen(far);
    if (Math.hypot(b.x - a.x, b.y - a.y) < 6) continue; // too small on screen to label
    ctx.strokeStyle = '#7fb2ff'; // same blue as the other automatic dimensions
    ctx.fillStyle = '#7fb2ff';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(b.x, b.y);
    ctx.lineTo(a.x, a.y);
    ctx.stroke();
    // Arrowhead on the arc, pointing at it.
    const ang = Math.atan2(a.y - b.y, a.x - b.x);
    ctx.beginPath();
    ctx.moveTo(a.x, a.y);
    ctx.lineTo(a.x - 8 * Math.cos(ang - 0.3), a.y - 8 * Math.sin(ang - 0.3));
    ctx.lineTo(a.x - 8 * Math.cos(ang + 0.3), a.y - 8 * Math.sin(ang + 0.3));
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = '#cfe2ff';
    ctx.textAlign = b.x >= a.x ? 'left' : 'right';
    ctx.fillText(`R${Math.round(c.radius)}`, b.x + (b.x >= a.x ? 4 : -4), b.y);
  }
  ctx.restore();
}

/** Temporary dimensions for a selected room: the outer wall outline (every wall
 *  broken into solid | door-or-pane | solid over whatever it hosts) plus the
 *  interior clear span along the inside of every wall. All are spaced out to stay
 *  readable. */
function drawRoomDimensionsNow(ctx: CanvasRenderingContext2D, vp: Viewport, inst: ProductInstance): void {
  if (isPolyRoom(inst)) {
    const outline = roomOutlineWorld(inst);
    // Every wall is split at its doors and glazed panes, so a wall with two panes
    // reads solid | glass | solid | glass | solid — the same treatment the door
    // wall always had, generalised to any combination.
    const segs: Array<{ a: Vec2; b: Vec2; locked?: boolean; equal?: boolean; tag?: string }> = [];
    // A wall broken into a chain also gets ONE figure for its whole length, drawn
    // further out so the chain stays readable — the chain says how the wall is
    // divided, this says how long it is. A wall with nothing on it already IS its
    // own overall figure, so it gets no second one.
    const overall: Array<{ a: Vec2; b: Vec2; locked?: boolean; tag?: string }> = [];
    for (let i = 0; i < outline.length; i++) {
      const a = outline[i], b = outline[(i + 1) % outline.length];
      const len = Math.hypot(b.x - a.x, b.y - a.y) || 1;
      const ux = (b.x - a.x) / len, uy = (b.y - a.y) / len;
      // The WALL's chain (corner to corner, across its panes' jambs).
      const spans = wallChain(inst, i);
      // A single-span wall (no door/pane) carries the wall's whole-length lock;
      // show it as the same inline padlock a pinned feature gets.
      const pinned = wallLengthPin(inst, i) != null;
      if (spans.length <= 1) { segs.push({ a, b, locked: pinned }); continue; }
      for (const sp of spans) {
        segs.push({
          a: { x: a.x + ux * sp.s, y: a.y + uy * sp.s },
          b: { x: a.x + ux * sp.t, y: a.y + uy * sp.t },
          locked: sp.locked,
          equal: sp.equal,
        });
      }
      overall.push({ a, b, locked: pinned, tag: DIM_TAG_WALL_OVERALL });
    }
    if (outline.length >= 3) {
      let cx = 0, cy = 0;
      for (const p of outline) { cx += p.x; cy += p.y; }
      const centroid = { x: cx / outline.length, y: cy / outline.length };
      // ONE `placed` list across both passes, so an overall figure never lands on
      // top of a chain figure; its own lane starts two steps further out.
      const placed: DimRect[] = [];
      drawDimSegments(ctx, vp, segs, centroid, true, placed);
      if (overall.length) drawDimSegments(ctx, vp, overall, centroid, true, placed, { firstGapPx: 44 + 20 * 2 });
    } else {
      drawLineDimensionsNow(ctx, vp, outline, true); // already inside the flush
    }
    drawInnerRoomDimensions(ctx, vp, inst);
    drawCornerRadiusDimensions(ctx, vp, inst);
    return;
  }
  const c = instanceCorners(inst); // [front-left, front-right, back-right, back-left]
  const hd = inst.depth / 2;
  const segs: Array<{ a: Vec2; b: Vec2 }> = [];
  const doorRect = roomDoors(inst).find((d) => d.innerWallId == null) ?? null;
  const dp = doorRect ? doorParts(inst, doorRect) : null;
  if (dp) {
    const loX = Math.min(dp.jambL.x, dp.jambR.x), hiX = Math.max(dp.jambL.x, dp.jambR.x);
    const jL = instanceToWorld(inst, { x: loX, y: -hd });
    const jR = instanceToWorld(inst, { x: hiX, y: -hd });
    segs.push({ a: c[0], b: jL }, { a: jL, b: jR }, { a: jR, b: c[1] }); // left wall | door | right wall
  } else {
    segs.push({ a: c[0], b: c[1] });
  }
  segs.push({ a: c[1], b: c[2] }, { a: c[2], b: c[3] }, { a: c[3], b: c[0] });
  drawDimSegments(ctx, vp, segs, { x: inst.x, y: inst.y }, true);
  drawInnerRoomDimensions(ctx, vp, inst);
}

/**
 * Live preview of the line being drawn: dashed polyline, vertex dots and a
 * cursor readout (pointer coordinates + distance from the last vertex).
 *
 * Takes a projector rather than a Viewport so the sheet line tool draws the
 * identical preview in paper space (which is Y-down, and so has no Viewport).
 */
export function drawPendingLine(ctx: CanvasRenderingContext2D, project: (p: Vec2) => Vec2, draft: Vec2[], hover: Vec2 | null): void {
  const line = hover ? [...draft, hover] : draft;
  if (!line.length) return;
  ctx.save();
  if (line.length >= 2) {
    const lp = line.map((p) => project(p));
    ctx.setLineDash([6, 4]);
    ctx.strokeStyle = '#ffd166';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    lp.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
    ctx.stroke();
    ctx.setLineDash([]);
  }
  ctx.fillStyle = '#ffd166';
  for (const q of draft) {
    const p = project(q);
    ctx.beginPath();
    ctx.arc(p.x, p.y, 3.5, 0, Math.PI * 2);
    ctx.fill();
  }
  if (hover) drawCursorReadout(ctx, project, hover, draft.length ? draft[draft.length - 1] : null);
  ctx.restore();
}

/** Live preview of the repeat tool: a dashed baseline polyline with vertex dots,
 *  segment dimensions, and each tiled product copy drawn translucently. */
function drawPendingRepeat(ctx: CanvasRenderingContext2D, vp: Viewport, pr: RepeatPreview): void {
  ctx.save();
  // Baseline polyline.
  const line = pr.pts.map((p) => vp.worldToScreen(p));
  if (line.length >= 2) {
    ctx.setLineDash([6, 4]);
    ctx.strokeStyle = '#ffd166';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    line.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
    ctx.stroke();
    ctx.setLineDash([]);
  }
  ctx.fillStyle = '#ffd166';
  for (const q of pr.pts) {
    const p = vp.worldToScreen(q);
    ctx.beginPath();
    ctx.arc(p.x, p.y, 3.5, 0, Math.PI * 2);
    ctx.fill();
  }
  // Each tiled copy, drawn translucently via the generic instance renderer.
  ctx.globalAlpha = 0.6;
  for (const c of pr.copies) {
    const inst = {
      id: '_repeat_preview',
      templateId: '',
      name: '',
      category: 'repeatable',
      x: c.x,
      y: c.y,
      rotation: c.rotation,
      width: pr.width,
      depth: pr.depth,
      color: pr.color,
      geometry: pr.geometry ?? undefined,
      preparations: [],
      properties: [],
    } as unknown as ProductInstance;
    drawInstance(ctx, vp, inst, false);
  }
  ctx.globalAlpha = 1;
  // Segment dimensions, like the other polyline tools.
  if (pr.pts.length >= 2) drawPendingDimensions(ctx, vp, pr.pts);
  ctx.restore();
}

function drawPendingWall(ctx: CanvasRenderingContext2D, vp: Viewport, draft: Vec2[], hover: Vec2 | null): void {
  const line = hover ? [...draft, hover] : draft;
  // Preview the band at the default thickness, centred.
  const band = wallBand(line, 100, 'center');
  ctx.save();
  if (band.length >= 3) {
    const pts = band.map((p) => vp.worldToScreen(p));
    ctx.beginPath();
    pts.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
    ctx.closePath();
    ctx.fillStyle = 'rgba(204, 204, 204, 0.30)';
    ctx.fill();
  }
  // Centre line + vertex dots.
  const lp = line.map((p) => vp.worldToScreen(p));
  ctx.setLineDash([6, 4]);
  ctx.strokeStyle = '#ffd166';
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  lp.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.fillStyle = '#ffd166';
  for (const p of draft.map((q) => vp.worldToScreen(q))) {
    ctx.beginPath();
    ctx.arc(p.x, p.y, 3.5, 0, Math.PI * 2);
    ctx.fill();
  }
  // Cursor readout: pointer coordinates and the live segment length.
  if (hover) drawCursorReadout(ctx, (q) => vp.worldToScreen(q), hover, draft.length ? draft[draft.length - 1] : null);
  ctx.restore();
}

/** A dimension figure / an x,y pair in the frame's style (dimFormat.ts). */
function formatDim(mm: number): string { return formatDimension(mm, pass.dimStyle); }

/** A ceiling plan's title: a reflected one's names its mirror's height in the
 *  drawing's style. */
export function rcpTitle(v: CeilingPlanView, room: ProductInstance): string {
  return ceilingViewTitle(v, room, formatDim);
}

/** A ceiling plan's title with its figures in `figure`'s style: the plan above
 *  the ceiling, or the reflected one at its mirror's height. The canvas, a sheet,
 *  the DXF and the Parts tab all name a view by it. */
export function ceilingViewTitle(v: CeilingPlanView, room: ProductInstance, figure: (mm: number) => string): string {
  return v.kind === 'above' ? tr('Plan above the ceiling') : tr('Reflected ceiling plan at {h}').replace('{h}', figure(rcpDrawnHeight(v, room)));
}

/** A room's floor grille, on its floor under the benches (floorGrille.ts):
 *  each top slat filled, the battens under them dashed over. */
function drawFloorGrille(ctx: CanvasRenderingContext2D, inst: ProductInstance, toS: (p: Vec2) => Vec2): void {
  const lay = grilleLayout(inst);
  if (!lay) return;
  const col = grilleColor(inst);
  const trace = (r: RingPoly): void => {
    ctx.beginPath();
    for (const ring of [r.outer, ...r.holes]) {
      ring.map(toS).forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
      ctx.closePath();
    }
  };
  ctx.save();
  ctx.lineWidth = 0.75;
  for (const s of lay.slats) {
    trace(s.ring);
    ctx.fillStyle = col;
    ctx.fill('evenodd');
    ctx.strokeStyle = darken(col, 0.5);
    ctx.stroke();
  }
  ctx.setLineDash([4, 3]);
  ctx.strokeStyle = darken(col, 0.35);
  for (const b of lay.battens) { trace(b.ring); ctx.stroke(); }
  ctx.restore();
}

/** The pulsing green highlight of a bench join a click would swap. */
function pulseJoint(ctx: CanvasRenderingContext2D, polys: Vec2[][], toS: (p: Vec2) => Vec2): void {
  if (!polys.length) return;
  const pulse = 0.4 + 0.35 * (1 + Math.sin(performance.now() / 180)) / 2;
  ctx.lineJoin = 'round';
  for (const poly of polys) {
    const pts = poly.map(toS);
    if (pts.length < 3) continue;
    ctx.beginPath();
    pts.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
    ctx.closePath();
    ctx.fillStyle = `rgba(34, 230, 80, ${(pulse * 0.55).toFixed(3)})`;
    ctx.fill();
    ctx.lineWidth = 2;
    ctx.strokeStyle = `rgba(34, 230, 80, ${pulse.toFixed(3)})`;
    ctx.stroke();
  }
}

/** A temporary figure on screen: a light line from `a` to `b`, arrowed at `b`
 *  (and at `a` too when `both`), its length boxed at the middle — that box
 *  returned (centre, half-size, screen px). */
function drawTempFigure(ctx: CanvasRenderingContext2D, a: Vec2, b: Vec2, len: number, both: boolean, style: DimStyle = pass.dimStyle): { cx: number; cy: number; hw: number; hh: number } {
  ctx.save();
  ctx.strokeStyle = '#7fb2ff';
  ctx.fillStyle = '#cfe2ff';
  ctx.lineWidth = 1;
  ctx.font = '12px sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();
  drawArrow(ctx, b, a);
  if (both) drawArrow(ctx, a, b);
  const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  const label = formatDimension(len, style);
  const w = ctx.measureText(label).width + 6;
  ctx.fillStyle = 'rgba(20,24,30,0.8)';
  ctx.fillRect(mid.x - w / 2, mid.y - 8, w, 16);
  ctx.fillStyle = '#cfe2ff';
  ctx.fillText(label, mid.x, mid.y);
  ctx.restore();
  return { cx: mid.x, cy: mid.y, hw: w / 2, hh: 8 };
}

/** The light levels' colour scale (lux → colour), by stops: a dim sauna's few
 *  lux blue, a well-lit seat yellow, a glare red. */
export const LUX_SCALE: ReadonlyArray<[number, [number, number, number]]> = [
  [0, [20, 30, 90]], [5, [40, 90, 200]], [20, [40, 190, 210]], [50, [60, 200, 90]], [100, [240, 220, 60]], [200, [245, 140, 40]], [500, [220, 40, 40]],
];
/** The colour of `lux` on the scale (log-like: between stops). */
export function luxColour(lux: number): [number, number, number] {
  const s = LUX_SCALE;
  if (lux <= s[0][0]) return s[0][1];
  for (let i = 1; i < s.length; i++) {
    if (lux <= s[i][0]) {
      const [a, ca] = s[i - 1], [b, cb] = s[i], t = (lux - a) / (b - a);
      return [0, 1, 2].map((k) => Math.round(ca[k] + (cb[k] - ca[k]) * t)) as [number, number, number];
    }
  }
  return s[s.length - 1][1];
}

/** Each room's light levels as a see-through heat map over its floor (each cell
 *  in the room's frame), and the scale's key at the canvas's bottom left. */
function drawLuxMaps(ctx: CanvasRenderingContext2D, vp: Viewport, lux: NonNullable<RenderOptions['lux']>): void {
  ctx.save();
  for (const m of lux.maps) {
    const r = (m.rotation * Math.PI) / 180, c = Math.cos(r), s = Math.sin(r);
    const W = (x: number, y: number): Vec2 => vp.worldToScreen({ x: m.x + x * c - y * s, y: m.y + x * s + y * c });
    const o = W(0, 0), ex = W(1, 0), ey = W(0, 1);
    ctx.save();
    ctx.setTransform(ctx.getTransform().multiply(new DOMMatrix([ex.x - o.x, ex.y - o.y, ey.x - o.x, ey.y - o.y, o.x, o.y])));
    for (const q of m.cells) {
      const [R, G, B] = luxColour(q.lux);
      ctx.fillStyle = `rgba(${R}, ${G}, ${B}, 0.5)`;
      ctx.fillRect(q.x - m.cell / 2, q.y - m.cell / 2, m.cell + 0.5, m.cell + 0.5);
    }
    ctx.restore();
  }
  // The key: the scale's stops, left to right.
  const h = ctx.canvas.clientHeight || ctx.canvas.height, x0 = 16, y0 = h - 56, w = 220;
  ctx.fillStyle = 'rgba(20, 24, 30, 0.85)';
  ctx.fillRect(x0 - 8, y0 - 22, w + 16, 50);
  const n = LUX_SCALE.length - 1;
  LUX_SCALE.forEach(([, [R, G, B]], i) => {
    if (i < n) { ctx.fillStyle = `rgb(${R}, ${G}, ${B})`; ctx.fillRect(x0 + (w * i) / n, y0, w / n + 0.5, 10); }
  });
  ctx.fillStyle = '#cfe2ff';
  ctx.font = '11px sans-serif';
  ctx.textAlign = 'center'; ctx.textBaseline = 'top';
  LUX_SCALE.forEach(([v], i) => ctx.fillText(String(v), x0 + (w * i) / n, y0 + 13));
  ctx.textAlign = 'left'; ctx.textBaseline = 'bottom';
  ctx.fillText(lux.title, x0, y0 - 4);
  ctx.restore();
}

/** A spot's temporary figures and guides (world, `RenderOptions.spotAid`): from
 *  its centre to the walls and to the spots it lines up with (`both` arrowed),
 *  `lane` 1 a lane beside its line; the guides (dashed) from the spots it is
 *  held in line with; the other lines it is held on. */
export interface SpotAidDraw { figures: Array<{ a: Vec2; b: Vec2; len: number; both: boolean; lane: number }>; guides: Array<[Vec2, Vec2]>; holds: Array<{ kind: SpotGuideKind; a: Vec2; b: Vec2 }> }

/** A spot's temporary figures and guides: a figure a lane out stands
 *  `SPOT_LANE` px beside its line, with its extension lines; a line held on
 *  besides a spot's projection is drawn across — a centre line dash-dot with its
 *  ℄, a halfway line dashed, a bench's front long-dashed. */
const SPOT_LANE = 18;
const SPOT_HOLD_DASH: Record<SpotGuideKind, number[]> = { centre: [14, 4, 2, 4], half: [4, 4], bench: [10, 4], channel: [10, 4], spacing: [4, 4], offset: [2, 3] };
/** THE spot aid's painter, over a projection: the plan's viewport, or the 3D
 *  view's at the level the spot hangs (ui/lightTools.ts `paintSpotAid3d`) — a
 *  point it cannot show is null, and what needs it is left out. Its figures read
 *  in `style`. Returns what it drew: each figure's label box (by its index in
 *  `aid.figures`, where a click retypes one), the guides' count, the holds' kinds. */
export function paintSpotAid(ctx: CanvasRenderingContext2D, project: (p: Vec2) => Vec2 | null, aid: SpotAidDraw, style: DimStyle): { figures: FigureBox[]; guides: number; holds: SpotGuideKind[] } {
  const drawn = { figures: [] as FigureBox[], guides: 0, holds: [] as SpotGuideKind[] };
  ctx.save();
  ctx.setLineDash([4, 4]);
  ctx.strokeStyle = ctx.fillStyle = 'rgba(120, 200, 255, 0.85)';
  ctx.lineWidth = 1;
  for (const [from, to] of aid.guides) {
    const a = project(from), b = project(to);
    if (!a || !b) continue;
    ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();
    ctx.fillRect(a.x - 2.5, a.y - 2.5, 5, 5);
    drawn.guides++;
  }
  ctx.font = 'bold 14px sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  for (const h of aid.holds) {
    const a = project(h.a), b = project(h.b);
    if (!a || !b) continue;
    ctx.setLineDash(SPOT_HOLD_DASH[h.kind]);
    ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();
    drawn.holds.push(h.kind);
    if (h.kind !== 'centre') continue;
    // The ℄ just inside the wall, beside the line.
    const l = Math.hypot(b.x - a.x, b.y - a.y) || 1, d = { x: (b.x - a.x) / l, y: (b.y - a.y) / l };
    ctx.fillText('℄', a.x + d.x * 16 - d.y * 10, a.y + d.y * 16 + d.x * 10);
  }
  ctx.setLineDash([]);
  ctx.strokeStyle = '#7fb2ff';
  aid.figures.forEach((f, i) => {
    let a = project(f.a), b = project(f.b);
    if (!a || !b) return;
    if (f.lane) {
      const l = Math.hypot(b.x - a.x, b.y - a.y) || 1, o = SPOT_LANE * f.lane;
      const n = { x: (-(b.y - a.y) / l) * o, y: ((b.x - a.x) / l) * o };
      const a1 = { x: a.x + n.x, y: a.y + n.y }, b1 = { x: b.x + n.x, y: b.y + n.y };
      ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(a1.x, a1.y); ctx.moveTo(b.x, b.y); ctx.lineTo(b1.x, b1.y); ctx.stroke();
      a = a1; b = b1;
    }
    drawn.figures.push({ ...drawTempFigure(ctx, a, b, f.len, f.both, style), i });
  });
  ctx.restore();
  return drawn;
}


/** The index (in `opts.spotAid.figures`) of the spot figure whose label lies
 *  under a screen point (+ `pad` px), the nearest when two overlap; else −1. */
export function spotFigureAt(hits: RenderHits, p: Vec2, pad = 3): number {
  let best = -1, bestD = Infinity;
  for (const b of hits.spotFigures) {
    const dx = p.x - b.cx, dy = p.y - b.cy;
    if (Math.abs(dx) > b.hw + pad || Math.abs(dy) > b.hh + pad) continue;
    const d = dx * dx + dy * dy;
    if (d < bestD) { bestD = d; best = b.i; }
  }
  return best;
}


/** The index (in `opts.supportSpacings`) of the support figure whose label lies
 *  under a screen point (+ `pad` px), the nearest when two overlap; else −1. */
export function supportFigureAt(hits: RenderHits, p: Vec2, pad = 3): number {
  let best = -1, bestD = Infinity;
  for (const b of hits.supportFigures) {
    const dx = p.x - b.cx, dy = p.y - b.cy;
    if (Math.abs(dx) > b.hw + pad || Math.abs(dy) > b.hh + pad) continue;
    const d = dx * dx + dy * dy;
    if (d < bestD) { bestD = d; best = b.i; }
  }
  return best;
}
function formatCoordPair(x: number, y: number): string { return formatCoordPairIn(x, y, pass.dimStyle); }

/** Dashed blue orthogonal alignment guides from every vertex the snapped cursor
 *  lines up with (same x → vertical, same y → horizontal). Shared by every draw
 *  tool so alignment feedback is identical everywhere. */
/** Guides to another draft of the same kind (a section line's endpoints, a
 *  dimension's endpoints) — purple, to read apart from the blue guides to
 *  ordinary geometry. */
const ALIGN_GUIDE_COLOR = 'rgba(186, 104, 255, 0.95)';

function drawOrthoGuides(ctx: CanvasRenderingContext2D, vp: Viewport, hover: Vec2 | null, verts: Vec2[], color = 'rgba(120, 200, 255, 0.85)', mark = false): void {
  if (!hover) return;
  const hs = vp.worldToScreen(hover);
  ctx.save();
  ctx.setLineDash([4, 4]);
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineWidth = 1;
  for (const q of verts) {
    const alignX = Math.abs(q.x - hover.x) < 1e-6, alignY = Math.abs(q.y - hover.y) < 1e-6;
    if (alignX !== alignY) { // exactly one axis aligned → draw that guide
      const qs = vp.worldToScreen(q);
      ctx.beginPath(); ctx.moveTo(qs.x, qs.y); ctx.lineTo(hs.x, hs.y); ctx.stroke();
      if (mark) ctx.fillRect(qs.x - 2.5, qs.y - 2.5, 5, 5); // the vertex the guide comes from
    }
  }
  ctx.restore();
}

function drawPendingPolygon(ctx: CanvasRenderingContext2D, vp: Viewport, draft: Vec2[], hover: Vec2 | null, snapVerts: Vec2[] = []): void {
  const verts = hover ? [...draft, hover] : draft;
  if (!verts.length) return;
  ctx.save();
  // Outline + translucent fill once there are at least two corners to span.
  if (verts.length >= 2) {
    const pts = verts.map((p) => vp.worldToScreen(p));
    ctx.beginPath();
    pts.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
    ctx.closePath();
    ctx.fillStyle = 'rgba(255, 255, 255, 0.18)';
    ctx.fill();
    ctx.setLineDash([6, 4]);
    ctx.strokeStyle = '#ffd166';
    ctx.lineWidth = 1.5;
    ctx.stroke();
    ctx.setLineDash([]);
  }
  drawOrthoGuides(ctx, vp, hover, [...draft, ...snapVerts]);

  // Committed vertex dots.
  ctx.fillStyle = '#ffd166';
  for (const q of draft) {
    const p = vp.worldToScreen(q);
    ctx.beginPath();
    ctx.arc(p.x, p.y, 3.5, 0, Math.PI * 2);
    ctx.fill();
  }
  // Temporary dimension lines for every drawn segment, offset outside the shape.
  if (verts.length >= 2) drawPendingDimensions(ctx, vp, verts);
  // Cursor readout: pointer coordinates and the live segment length.
  if (hover) drawCursorReadout(ctx, (q) => vp.worldToScreen(q), hover, draft.length ? draft[draft.length - 1] : null);
  ctx.restore();
}

/**
 * Dimension lines for each segment of the polygon being drawn. Each is pushed a
 * fixed screen gap to the side of its edge that faces away from the shape's
 * centroid, so the lines sit outside the drawing rather than across it.
 */
function drawPendingDimensions(ctx: CanvasRenderingContext2D, vp: Viewport, verts: Vec2[], closed = false): void {
  if (verts.length < 2) return;
  let cx = 0, cy = 0;
  for (const p of verts) { cx += p.x; cy += p.y; }
  const centroid = { x: cx / verts.length, y: cy / verts.length };
  const segs: Array<{ a: Vec2; b: Vec2 }> = [];
  const edges = closed ? verts.length : verts.length - 1;
  for (let i = 0; i < edges; i++) segs.push({ a: verts[i], b: verts[(i + 1) % verts.length] });
  // Same overlap-avoiding renderer the selected-object dimensions use, so a
  // draft's labels never collide (outward = away from the draft's centroid).
  drawDimSegments(ctx, vp, segs, centroid, true);
}

/** F12 (AutoCAD's dynamic input): whether the readout rides along at the cursor. */
let CURSOR_READOUT = true;
export function setCursorReadout(on: boolean): void { CURSOR_READOUT = on; }

/** A small box by the cursor with its coordinates and the distance from the last vertex. */
function drawCursorReadout(ctx: CanvasRenderingContext2D, project: (p: Vec2) => Vec2, hover: Vec2, last: Vec2 | null): void {
  if (!CURSOR_READOUT) return;
  const s = project(hover);
  const lines = [formatCoordPair(hover.x, hover.y)];
  if (last) lines.push(`Δ ${formatDim(distance(last, hover))}`);
  ctx.save();
  ctx.font = '12px sans-serif';
  ctx.textBaseline = 'top';
  ctx.textAlign = 'left';
  const padX = 6, padY = 4, lh = 15;
  let w = 0;
  for (const l of lines) w = Math.max(w, ctx.measureText(l).width);
  const bw = w + padX * 2, bh = lines.length * lh + padY * 2;
  // Default to lower-right of the cursor; flip if it would leave the canvas.
  // (Coordinates are CSS px — the context is pre-scaled by the device ratio.)
  const viewW = ctx.canvas.clientWidth || ctx.canvas.width;
  const viewH = ctx.canvas.clientHeight || ctx.canvas.height;
  let bx = s.x + 14, by = s.y + 14;
  if (bx + bw > viewW) bx = s.x - 14 - bw;
  if (by + bh > viewH) by = s.y - 14 - bh;
  ctx.fillStyle = 'rgba(20, 22, 28, 0.85)';
  ctx.strokeStyle = 'rgba(127, 178, 255, 0.6)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.rect(bx, by, bw, bh);
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = '#e6eefc';
  lines.forEach((l, i) => ctx.fillText(l, bx + padX, by + padY + i * lh));
  ctx.restore();
}

// Text-box comment style — matches the on-screen schedule tables.
export const COMMENT_FONT_PX = 13;
export const COMMENT_LINE_PX = 18;
export const COMMENT_PAD_PX = 8;
export const COMMENT_DEFAULT_FILL = '#ffffff';
export const COMMENT_DEFAULT_OPACITY = 1;
/** Default corner rounding radius (mm) for a text box — model comment and its
 *  schematic-box twin — when `cornerRadius` is absent: a gentle round. */
export const TEXTBOX_CORNER_DEFAULT = 2;

/** Greedy word-wrap of `text` to `maxWidth` px for the given context font. */
export function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const out: string[] = [];
  for (const para of text.split('\n')) {
    if (para === '') { out.push(''); continue; }
    let line = '';
    for (const word of para.split(/(\s+)/)) {
      const next = line + word;
      if (ctx.measureText(next).width > maxWidth && line.trim() !== '') {
        out.push(line.replace(/\s+$/, ''));
        line = word.replace(/^\s+/, '');
      } else {
        line = next;
      }
    }
    out.push(line.replace(/\s+$/, ''));
  }
  return out;
}

function drawTextComment(
  ctx: CanvasRenderingContext2D,
  vp: Viewport,
  t: TextComment,
  selected: boolean,
  editing: boolean,
  hops: Vec2[] = [],
  sample: LumaProbe | null = null
): void {
  const tl = vp.worldToScreen({ x: t.x, y: t.y });
  const wPx = t.w * vp.scale;
  const hPx = t.h * vp.scale;
  // The box is drawn in its own frame: origin at its top-left anchor, turned by
  // its rotation (CCW in the world is clockwise-negative on the Y-down screen).
  const rad = ((t.rotation ?? 0) * Math.PI) / 180;
  const toScreen = (lx: number, ly: number): Vec2 => ({ x: tl.x + lx * Math.cos(rad) + ly * Math.sin(rad), y: tl.y - lx * Math.sin(rad) + ly * Math.cos(rad) });
  ctx.save();
  ctx.translate(tl.x, tl.y);
  ctx.rotate(-rad);
  // Box fill + outline (schedule style by default). Corner rounding is a paper-mm
  // property, clamped to half the shorter side, so it prints and zooms with the box.
  const rPx = Math.max(0, Math.min((t.cornerRadius ?? TEXTBOX_CORNER_DEFAULT) * vp.scale, wPx / 2, hPx / 2));
  ctx.beginPath();
  if (ctx.roundRect) ctx.roundRect(0, 0, wPx, hPx, rPx); else ctx.rect(0, 0, wPx, hPx);
  ctx.fillStyle = hexToRgba(t.fill, t.fillOpacity);
  ctx.fill();
  const outline = selected ? '#ffd166' : hexToRgba(t.color ?? ANNOTATION_COLOR, t.strokeOpacity ?? 1);
  ctx.lineWidth = selected ? 2 : 1;
  ctx.strokeStyle = outline;
  ctx.stroke();

  // Text (skipped while the inline editor is showing it). Deliberately NOT
  // clipped to the box: the type size is independent of the frame, so text that
  // no longer fits wraps to the width and overflows rather than being cut off.
  if (!editing) {
    const fontPx = (t.heightMm ?? COMMENT_FONT_MM) * vp.scale;
    const pad = fontPx * (COMMENT_PAD_PX / COMMENT_FONT_PX);
    ctx.font = `${fontPx}px sans-serif`;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.fillStyle = t.textColor ?? ANNOTATION_COLOR;
    const lines = wrapText(ctx, t.text, wPx - pad * 2);
    let ly = pad;
    for (const line of lines) {
      ctx.fillText(line, pad, ly);
      ly += fontPx * (COMMENT_LINE_PX / COMMENT_FONT_PX);
    }
  }
  ctx.restore();
  // Leader arrow (dimension style) from the box edge to the target. The box
  // itself sits on its own fill, but the arrow reaches out across the plan — so
  // it gets the dimensions' adaptive ink, lifted only over the dark stretches.
  if (t.leader) {
    const target = vp.worldToScreen(t.leader);
    // The nearest point of the (turned) box outline, found in the box's frame.
    const lt = textBoxLocal(t, t.leader);
    const ls = nearestRectPoint(0, 0, wPx, hPx, { x: lt.x * vp.scale, y: lt.y * vp.scale });
    const start = toScreen(ls.x, ls.y);
    const ink = makeInkResolver(selected ? '#ffd166' : t.color ?? ANNOTATION_COLOR, sample);
    ctx.save();
    ctx.globalAlpha = t.strokeOpacity ?? 1; // opacity rides here, so the ink stays a plain colour
    ctx.lineWidth = 1.25;
    // Hop over any crossing leader so the two read as not connected; each run of
    // constant ink is humped separately, so the hops are preserved.
    for (const run of inkRuns(start, target, ink)) {
      ctx.strokeStyle = run.color;
      strokeWithHumps(ctx, run.a, run.b, hops, 5);
    }
    ctx.fillStyle = ink.at(target.x, target.y);
    drawArrow(ctx, target, start);
    ctx.restore();
  }

  // Resize handles when selected (and not mid-typing), plus a handle on the
  // leader arrow tip so it can be dragged like a resize handle.
  if (selected && !editing) {
    drawGrips(ctx, textCommentHandles(t).map((h) => vp.worldToScreen(h.world)));
    if (t.leader) drawGrips(ctx, [vp.worldToScreen(t.leader)]);
    const k = textRotationKnob(vp, t);
    drawKnob(ctx, k.anchor, k.knob);
  }
}

/** The eight resize handles (corners + edge midpoints) of a text comment box. */
export function textCommentHandles(t: TextComment): Handle[] {
  const dirs: Array<[-1 | 0 | 1, -1 | 0 | 1]> = [
    [-1, -1], [1, -1], [1, 1], [-1, 1], [0, -1], [1, 0], [0, 1], [-1, 0],
  ];
  return dirs.map(([sx, sy]) => ({
    sx,
    sy,
    world: textBoxToWorld(t, ((sx + 1) / 2) * t.w, ((1 - sy) / 2) * t.h),
  }));
}

/** Closest point on the (screen) rectangle's border to `p`. */
export function nearestRectPoint(x: number, y: number, w: number, h: number, p: Vec2): Vec2 {
  const cx = Math.max(x, Math.min(x + w, p.x));
  const cy = Math.max(y, Math.min(y + h, p.y));
  // If p is outside, (cx,cy) is already on the border; if inside, clamp to the
  // nearest edge so the leader starts at the box outline.
  if (p.x > x && p.x < x + w && p.y > y && p.y < y + h) {
    const dl = p.x - x, dr = x + w - p.x, dt = p.y - y, db = y + h - p.y;
    const m = Math.min(dl, dr, dt, db);
    if (m === dl) return { x, y: p.y };
    if (m === dr) return { x: x + w, y: p.y };
    if (m === dt) return { x: p.x, y };
    return { x: p.x, y: y + h };
  }
  return { x: cx, y: cy };
}

/**
 * Live preview of the box being placed with a text tool: its dashed outline and,
 * once both corners are down, the leader arrow tracking the cursor.
 *
 * Takes a projector and a scale rather than a Viewport, so a sheet's text tool
 * draws the identical preview in paper space (Y-down, no Viewport). `box.y` is
 * the TOP edge in both spaces, so the projected corner is always the top-left.
 */
export function drawPendingTextBox(
  ctx: CanvasRenderingContext2D,
  project: (p: Vec2) => Vec2,
  scale: number,
  box: { x: number; y: number; w: number; h: number; leader?: Vec2 }
): void {
  const tl = project({ x: box.x, y: box.y });
  const wPx = box.w * scale;
  const hPx = box.h * scale;
  ctx.save();
  ctx.setLineDash([6, 4]);
  ctx.strokeStyle = '#ffd166';
  ctx.lineWidth = 1.5;
  ctx.strokeRect(tl.x, tl.y, wPx, hPx);
  if (box.leader) {
    const target = project(box.leader);
    const start = nearestRectPoint(tl.x, tl.y, wPx, hPx, target);
    ctx.beginPath();
    ctx.moveTo(start.x, start.y);
    ctx.lineTo(target.x, target.y);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = '#ffd166';
    drawArrow(ctx, target, start);
  }
  ctx.restore();
}

// ---- Adaptive annotation ink (model space only) ----------------------------
// A dimension is drawn in its own colour, but the default annotation red is a
// PRINT colour: on the dark model canvas it is two dark tones on top of each
// other. Rather than ringing the strokes (a halo/outline reads as glow), the ink
// ITSELF is lifted in luminance — hue preserved — and only over the stretches
// that actually sit on a dark backdrop. A dimension crossing a white underlay
// keeps its true colour there and lifts only where it leaves it.
//
// The backdrop is the composite of two canvases: the static one (page fill,
// imported PDF/DXF underlays, grid) and whatever the main canvas has drawn so
// far this frame (geometry always precedes the dimensions). Both are scaled into
// one small offscreen buffer — a GPU blit, then a single small readback — so the
// per-frame cost stays flat regardless of zoom.

const LUMA_STEP = 4; // CSS px per sampled cell — the resolution of the light/dark edge
const LUMA_MAX_CELLS = 120_000; // cap on the readback; a larger box is sampled coarser
let lumaCanvas: HTMLCanvasElement | null = null;

/** A luminance probe (0..255) at a CSS px point, with the canvas it reads
 *  (`clip`, CSS px): past it a line is off screen. */
type LumaProbe = ((x: number, y: number) => number) & { clip?: { x0: number; y0: number; x1: number; y1: number } };

/** Sample the composited backdrop over `box` (CSS px). Returns a luminance probe
 *  (0..255) in CSS px, or null when there is nothing worth sampling. */
function backdropSampler(
  ctx: CanvasRenderingContext2D,
  bg: HTMLCanvasElement,
  dpr: number,
  box: { x0: number; y0: number; x1: number; y1: number }
): LumaProbe | null {
  const cssW = ctx.canvas.width / dpr, cssH = ctx.canvas.height / dpr;
  const x0 = Math.max(0, Math.floor(box.x0)), y0 = Math.max(0, Math.floor(box.y0));
  const x1 = Math.min(cssW, Math.ceil(box.x1)), y1 = Math.min(cssH, Math.ceil(box.y1));
  const w = x1 - x0, h = y1 - y0;
  if (w <= 0 || h <= 0) return null;
  // Past the cap the cells grow instead of the ink being dropped: a room zoomed
  // to fill a large screen kept its dark print ink (the box was the canvas).
  const step = Math.max(LUMA_STEP, Math.sqrt((w * h) / LUMA_MAX_CELLS));
  const cw = Math.max(1, Math.round(w / step)), ch = Math.max(1, Math.round(h / step));
  if (!lumaCanvas) lumaCanvas = document.createElement('canvas');
  lumaCanvas.width = cw; lumaCanvas.height = ch;
  const lc = lumaCanvas.getContext('2d', { willReadFrequently: true });
  if (!lc) return null;
  lc.clearRect(0, 0, cw, ch);
  const sx = x0 * dpr, sy = y0 * dpr, sw = w * dpr, sh = h * dpr;
  try {
    if (bg.width > 0 && bg.height > 0) lc.drawImage(bg, sx, sy, sw, sh, 0, 0, cw, ch);
    lc.drawImage(ctx.canvas, sx, sy, sw, sh, 0, 0, cw, ch); // transparent where nothing is drawn
  } catch { return null; }
  const data = lc.getImageData(0, 0, cw, ch).data;
  const probe = (x: number, y: number): number => {
    const ix = Math.min(cw - 1, Math.max(0, Math.floor(((x - x0) * cw) / w)));
    const iy = Math.min(ch - 1, Math.max(0, Math.floor(((y - y0) * ch) / h)));
    const i = (iy * cw + ix) * 4;
    return 0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2];
  };
  return Object.assign(probe, { clip: { x0: 0, y0: 0, x1: cssW, y1: cssH } });
}

function rgbOf(hex: string): [number, number, number] | null {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex.trim());
  return m ? [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)] : null;
}

/** Hue- and saturation-preserving luminance lift: the same colour, bright enough
 *  to read on a dark backdrop. A colour already this light is returned unchanged. */
const LIFT_TARGET_L = 0.6;
function liftInk(hex: string): string {
  const rgb = rgbOf(hex);
  if (!rgb) return hex;
  const [r, g, b] = rgb.map((v) => v / 255) as [number, number, number];
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (l >= LIFT_TARGET_L) return hex;
  const d = max - min;
  const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
  let hDeg = 0;
  if (d !== 0) {
    if (max === r) hDeg = 60 * (((g - b) / d) % 6);
    else if (max === g) hDeg = 60 * ((b - r) / d + 2);
    else hDeg = 60 * ((r - g) / d + 4);
  }
  const c = (1 - Math.abs(2 * LIFT_TARGET_L - 1)) * s;
  const x = c * (1 - Math.abs(((hDeg / 60) % 2) - 1));
  const m = LIFT_TARGET_L - c / 2;
  const seg = Math.floor(((hDeg % 360) + 360) % 360 / 60);
  const rgbP: [number, number, number] =
    seg === 0 ? [c, x, 0] : seg === 1 ? [x, c, 0] : seg === 2 ? [0, c, x]
      : seg === 3 ? [0, x, c] : seg === 4 ? [x, 0, c] : [c, 0, x];
  const hx = (v: number): string => Math.round(Math.min(255, Math.max(0, (v + m) * 255))).toString(16).padStart(2, '0');
  return `#${hx(rgbP[0])}${hx(rgbP[1])}${hx(rgbP[2])}`;
}

// Backdrop luminance below which the ink is fully lifted, and above which it is
// left alone. The band between them ramps, so a soft edge doesn't step harshly.
const LUMA_DARK = 95;
const LUMA_LIGHT = 150;

/** Resolve the ink over a backdrop: the stored colour over a light one, the
 *  lifted colour over a dark one, blended across the band between. `at` decides
 *  per point (strokes, arrowheads); `over` averages several points, for a run of
 *  glyphs that must take ONE decision together. */
interface InkResolver {
  at(x: number, y: number): string;
  over(pts: Vec2[]): string;
  /** The canvas the backdrop was read from (`LumaProbe.clip`). */
  clip?: { x0: number; y0: number; x1: number; y1: number };
  /** No backdrop: every point takes the stored ink. */
  plain?: boolean;
}
function makeInkResolver(
  color: string,
  sample: LumaProbe | null
): InkResolver {
  const plain = { at: () => color, over: () => color, plain: true };
  if (!sample) return plain;
  const base = rgbOf(color), lift = rgbOf(liftInk(color));
  if (!base || !lift) return plain;
  const cache = new Map<number, string>();
  const forLuma = (luma: number): string => {
    const raw = (LUMA_LIGHT - luma) / (LUMA_LIGHT - LUMA_DARK);
    const t = Math.round(Math.min(1, Math.max(0, raw)) * 8) / 8; // quantised, so runs merge
    if (t <= 0) return color;
    let hit = cache.get(t);
    if (!hit) {
      const mix = (i: number): number => Math.round(base[i] + (lift[i] - base[i]) * t);
      hit = `rgb(${mix(0)}, ${mix(1)}, ${mix(2)})`;
      cache.set(t, hit);
    }
    return hit;
  };
  return {
    clip: sample.clip,
    at: (x, y) => forLuma(sample(x, y)),
    over: (pts) => {
      if (!pts.length) return color;
      let sum = 0;
      for (const p of pts) sum += sample(p.x, p.y);
      return forLuma(sum / pts.length);
    },
  };
}

/** Stroke a screen-space polyline, split where the backdrop changes so each
 *  stretch carries its own ink. Consecutive stretches of the same ink merge into
 *  one path, so the usual whole-line case costs a single stroke. */
const INK_SEG = 9; // CSS px per adaptive sub-segment
/** Split a→b into consecutive runs of constant ink. Neighbouring sub-segments
 *  that resolve to the same ink merge, so the usual whole-line case is one run.
 *  Only the stretch on the canvas is split: past it the line is off screen and
 *  runs on in one piece, so a line zoomed to millions of px long costs no more
 *  than one across the screen (and a stored ink is one run whatever its length). */
function inkRuns(a: Vec2, b: Vec2, ink: InkResolver): Array<{ color: string; a: Vec2; b: Vec2 }> {
  if (ink.plain) return [{ color: ink.at(a.x, a.y), a, b }];
  const len = Math.hypot(b.x - a.x, b.y - a.y);
  const pt = (u: number): Vec2 => ({ x: a.x + (b.x - a.x) * u, y: a.y + (b.y - a.y) * u });
  const span = ink.clip ? clipToBox(a, b, ink.clip) : [0, 1] as [number, number];
  if (!span) { const m = pt(0.5); return [{ color: ink.at(m.x, m.y), a, b }]; }
  const [u0, u1] = span;
  const n = Math.max(1, Math.ceil(((u1 - u0) * len) / INK_SEG));
  const out: Array<{ color: string; a: Vec2; b: Vec2 }> = [];
  const push = (color: string, p0: Vec2, p1: Vec2): void => {
    const last = out[out.length - 1];
    if (last && last.color === color) last.b = p1;
    else out.push({ color, a: p0, b: p1 });
  };
  if (u0 > 0) { const e = pt(u0); push(ink.at(e.x, e.y), a, e); }
  for (let k = 1; k <= n; k++) {
    const p0 = pt(u0 + ((u1 - u0) * (k - 1)) / n), p1 = pt(u0 + ((u1 - u0) * k) / n);
    push(ink.at((p0.x + p1.x) / 2, (p0.y + p1.y) / 2), p0, p1);
  }
  if (u1 < 1) { const e = pt(u1); push(ink.at(e.x, e.y), e, b); }
  return out;
}

/** The stretch [u0, u1] of a→b inside `box` (Liang–Barsky), or null. */
function clipToBox(a: Vec2, b: Vec2, box: { x0: number; y0: number; x1: number; y1: number }): [number, number] | null {
  const dx = b.x - a.x, dy = b.y - a.y;
  let u0 = 0, u1 = 1;
  for (const [p, q] of [[-dx, a.x - box.x0], [dx, box.x1 - a.x], [-dy, a.y - box.y0], [dy, box.y1 - a.y]]) {
    if (p === 0) { if (q < 0) return null; continue; }
    const r = q / p;
    if (p < 0) { if (r > u1) return null; u0 = Math.max(u0, r); } else { if (r < u0) return null; u1 = Math.min(u1, r); }
  }
  return u1 > u0 ? [u0, u1] : null;
}

function strokeAdaptive(
  ctx: CanvasRenderingContext2D,
  pts: Vec2[],
  ink: InkResolver
): void {
  for (let i = 1; i < pts.length; i++) {
    for (const run of inkRuns(pts[i - 1], pts[i], ink)) {
      ctx.strokeStyle = run.color;
      ctx.beginPath();
      ctx.moveTo(run.a.x, run.a.y);
      ctx.lineTo(run.b.x, run.b.y);
      ctx.stroke();
    }
  }
}

/** Screen-space bounding box of a dimension's drawn extent (line, extension
 *  lines, arrowheads and label), for sizing the backdrop sample. */
function dimScreenBox(vp: Viewport, dim: DimensionLine): { x0: number; y0: number; x1: number; y1: number } | null {
  const g = dimensionGeometry(dim.a, dim.b, dim.offset, dim.kind);
  if (!g) return null;
  const pts = [vp.worldToScreen(dim.a), vp.worldToScreen(dim.b), vp.worldToScreen(g.a), vp.worldToScreen(g.b)];
  const PAD = 40; // arrowheads + the label sitting off the line
  return {
    x0: Math.min(...pts.map((p) => p.x)) - PAD, y0: Math.min(...pts.map((p) => p.y)) - PAD,
    x1: Math.max(...pts.map((p) => p.x)) + PAD, y1: Math.max(...pts.map((p) => p.y)) + PAD,
  };
}

function drawDimension(
  ctx: CanvasRenderingContext2D,
  vp: Viewport,
  dim: DimensionLine,
  selected: boolean,
  sample: LumaProbe | null = null
): void {
  // The ONE resolver: where the line sits and what it reads. A horizontal or
  // vertical dimension runs along its own axis with the measured points projected
  // onto it, so the extension lines close the gap and the figure is that axis's
  // distance, not the full one.
  const g = dimensionGeometry(dim.a, dim.b, dim.offset, dim.kind);
  if (!g) return;
  const dx = g.u.x, dy = g.u.y;
  const off = dim.offset;
  const sa = vp.worldToScreen(g.a);
  const sb = vp.worldToScreen(g.b);
  const ea = vp.worldToScreen(dim.a);
  const eb = vp.worldToScreen(dim.b);
  const color = selected ? '#ffd166' : dim.color ?? ANNOTATION_COLOR;
  const label = formatDim(g.length);
  const ang = Math.atan2(sb.y - sa.y, sb.x - sa.x);
  // Offset the label to the side of the line facing away from the measured
  // baseline: the extension direction (baseline → line, = sa−ea). With no offset
  // there is no far side, so fall back to the line's perpendicular (screen-up).
  let awx = sa.x - ea.x, awy = sa.y - ea.y;
  const al = Math.hypot(awx, awy);
  if (al > 1e-3) { awx /= al; awy /= al; }
  else { awx = dy; awy = -dx; if (awy > 0) { awx = -awx; awy = -awy; } } // perpendicular, prefer up
  const mid = { x: (sa.x + sb.x) / 2, y: (sa.y + sb.y) / 2 };

  // With no sampler (sheets, print) this resolves to the stored colour for every
  // point, so those spaces render byte-for-byte as before.
  const ink = makeInkResolver(color, sample);
  ctx.save();
  ctx.lineWidth = 1.25;
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  if (Math.abs(off) > 1e-6) { // extension lines (only when offset is non-zero)
    strokeAdaptive(ctx, [ea, sa], ink);
    strokeAdaptive(ctx, [eb, sb], ink);
  }
  strokeAdaptive(ctx, [sa, sb], ink); // dimension line
  ctx.fillStyle = ink.at(sa.x, sa.y); drawArrow(ctx, sa, sb);
  ctx.fillStyle = ink.at(sb.x, sb.y); drawArrow(ctx, sb, sa);
  ctx.font = '13px sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const lx = mid.x + awx * 9, ly = mid.y + awy * 9;
  // The label takes ONE decision — never half a glyph lifted — but weighed over
  // its whole width, so a label straddling an edge follows the majority side.
  const half = ctx.measureText(label).width / 2;
  const ux = Math.cos(ang), uy = Math.sin(ang);
  const lPts: Vec2[] = [];
  for (let k = -2; k <= 2; k++) lPts.push({ x: lx + ux * half * (k / 2), y: ly + uy * half * (k / 2) });
  ctx.fillStyle = ink.over(lPts);
  ctx.translate(lx, ly);
  ctx.rotate(Math.abs(ang) > Math.PI / 2 ? ang + Math.PI : ang);
  ctx.fillText(label, 0, 0);
  ctx.restore();
}

export function drawArrow(ctx: CanvasRenderingContext2D, tip: Vec2, from: Vec2): void {
  const ang = Math.atan2(tip.y - from.y, tip.x - from.x);
  const size = 8;
  ctx.beginPath();
  ctx.moveTo(tip.x, tip.y);
  ctx.lineTo(tip.x - size * Math.cos(ang - 0.4), tip.y - size * Math.sin(ang - 0.4));
  ctx.lineTo(tip.x - size * Math.cos(ang + 0.4), tip.y - size * Math.sin(ang + 0.4));
  ctx.closePath();
  ctx.fill();
}

/** A leader: an arrowhead at points[0], the bend polyline, and the label text at
 *  the last point, running away from the last bend, its last line just over the
 *  end (no shelf line under it; the DXF writes it the same way). */
function drawLeader(ctx: CanvasRenderingContext2D, vp: Viewport, leader: Leader, selected: boolean, _lang: string): void {
  const pts = leader.points;
  if (pts.length < 2) return;
  const col = selected ? '#ffd166' : leader.color ?? ANNOTATION_COLOR;
  const sp = pts.map((p) => vp.worldToScreen(p));
  ctx.save();
  ctx.strokeStyle = col;
  ctx.fillStyle = col;
  ctx.lineWidth = selected ? 2 : 1.25;
  ctx.beginPath();
  sp.forEach((s, i) => (i ? ctx.lineTo(s.x, s.y) : ctx.moveTo(s.x, s.y)));
  ctx.stroke();
  drawArrow(ctx, sp[0], sp[1]);
  const anchor = sp[sp.length - 1];
  const prev = sp[sp.length - 2];
  const rightward = anchor.x >= prev.x; // text runs away from the last bend
  const lines = (leader.text || '').split('\n');
  ctx.font = '13px sans-serif';
  ctx.textBaseline = 'bottom';
  ctx.textAlign = rightward ? 'left' : 'right';
  const pad = 4;
  let ly = anchor.y - 2;
  const tx = anchor.x + (rightward ? pad : -pad);
  for (let i = lines.length - 1; i >= 0; i--) { ctx.fillText(lines[i], tx, ly); ly -= 16; }
  ctx.restore();
}

/** AutoCAD's AutoSnap marker colour — the chartreuse it marks an acquired snap
 *  with. Deliberately unlike anything else drawn over the plan: the purple
 *  alignment guides and the gold selection ink both have to stay tellable apart
 *  from it at a glance. */
export const SNAP_MARKER_COLOR = '#aaff00';

/** The AutoSnap tooltip's wording per snap kind — ONE map, so the marker's label
 *  and the Snap button's OSNAP menu can never name the same snap differently.
 *  English keys; both readers pass them through `tr()`. */
export const SNAP_KIND_LABEL: Record<string, string> = {
  endpoint: 'Endpoint',
  midpoint: 'Midpoint',
  center: 'Centre',
  intersection: 'Intersection',
  quadrant: 'Quadrant',
  perpendicular: 'Perpendicular',
  nearest: 'Nearest',
  node: 'Node',
};

/**
 * The object-snap indicator, drawn AutoCAD's way: a HOLLOW per-kind glyph centred
 * on the acquired point — □ endpoint, △ midpoint, ○ centre, ⊗ node, ✕
 * intersection, ◇ quadrant, ⊾ perpendicular, ⧖ nearest — with the AutoSnap tooltip naming the
 * snap just off it.
 *
 * Hollow, and with no dot at the point, is the whole idea: the glyph has to sit
 * ON the geometry it marks without covering it, and its own centre IS the point.
 * (The square used to carry a crosshair through it, which AutoCAD's does not — it
 * reads as a drawn cross on the plan at a glance.)
 *
 * One adaptation: every glyph is laid down twice, a dark wider pass under the
 * chartreuse one. AutoCAD only ever draws on its own dark model space, while here
 * a rasterised white plan is often the backdrop, and a bare bright outline
 * vanishes on it.
 */
/** The Constrain tool's feedback: the first pick in green, the piece a click
 *  would take in blue — a ring on a vertex, a broad stroke along an edge. */
/** A constraint piece marked: a ring round a vertex, a broad stroke along an edge. */
function drawConstraintPieceMark(ctx: CanvasRenderingContext2D, vp: Viewport, pts: Vec2[], color: string): void {
  const s = pts.map((p) => vp.worldToScreen(p));
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineCap = 'round';
  if (s.length === 1) {
    ctx.lineWidth = 2.5;
    ctx.beginPath(); ctx.arc(s[0].x, s[0].y, 7, 0, Math.PI * 2); ctx.stroke();
  } else {
    ctx.globalAlpha = 0.75;
    ctx.lineWidth = 6;
    ctx.beginPath(); ctx.moveTo(s[0].x, s[0].y);
    for (const q of s.slice(1)) ctx.lineTo(q.x, q.y); // an edge, or a curve's ring / arc
    ctx.stroke();
  }
  ctx.restore();
}

function drawConstraintPick(ctx: CanvasRenderingContext2D, vp: Viewport, pick: { hover: Vec2[] | null; first: Vec2[] | null }): void {
  if (pick.first) drawConstraintPieceMark(ctx, vp, pick.first, '#3ecf6e');
  if (pick.hover) drawConstraintPieceMark(ctx, vp, pick.hover, '#4aa3ff');
}

const CONSTRAINT_ON_COLOR = '#ff9f1a';
const CONSTRAINT_BROKEN_COLOR = '#e5484d';

/** The constraint badges: a rounded tag with the relation's glyph (and figure),
 *  a hairline back to its piece; orange while selected / hovered, red when broken. */
function drawObjConstraintBadges(ctx: CanvasRenderingContext2D, badges: NonNullable<RenderOptions['constraintBadges']>): void {
  ctx.save();
  ctx.font = '600 11px sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  for (const b of badges) {
    const x0 = b.x - b.w / 2, y0 = b.y - b.h / 2;
    const edge = b.broken ? CONSTRAINT_BROKEN_COLOR : b.on ? CONSTRAINT_ON_COLOR : '#8fa3b8';
    ctx.strokeStyle = edge;
    if (b.line) {
      // A driving dimension: its line between what it measures, arrows both ends.
      const [p, q] = b.line;
      ctx.lineWidth = 1.25;
      ctx.beginPath(); ctx.moveTo(p.x, p.y); ctx.lineTo(q.x, q.y); ctx.stroke();
      ctx.fillStyle = edge;
      if (Math.hypot(q.x - p.x, q.y - p.y) > 20) { drawArrow(ctx, p, q); drawArrow(ctx, q, p); }
    } else {
      ctx.globalAlpha = 0.6;
      ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(b.anchor.x, b.anchor.y); ctx.lineTo(b.x, b.y); ctx.stroke();
      ctx.globalAlpha = 1;
    }
    ctx.fillStyle = b.broken ? CONSTRAINT_BROKEN_COLOR : b.on ? CONSTRAINT_ON_COLOR : '#26313d';
    ctx.beginPath();
    ctx.roundRect(x0, y0, b.w, b.h, 4);
    ctx.fill();
    ctx.lineWidth = 1.2;
    ctx.stroke();
    ctx.fillStyle = b.on && !b.broken ? '#1b1b1b' : '#ffffff';
    ctx.fillText(b.text, b.x, b.y + 0.5);
  }
  ctx.restore();
}

export function drawSnapMarker(ctx: CanvasRenderingContext2D, s: Vec2, kind?: string, cssW = 0, cssH = 0, tooltip = true): void {
  ctx.save();
  const r = 6;
  ctx.lineJoin = 'miter';
  ctx.beginPath();
  switch (kind) {
    case 'midpoint': // triangle
      ctx.moveTo(s.x, s.y - r); ctx.lineTo(s.x + r, s.y + r); ctx.lineTo(s.x - r, s.y + r); ctx.closePath();
      break;
    case 'center': // circle
      ctx.arc(s.x, s.y, r, 0, Math.PI * 2);
      break;
    case 'node': { // circle + an inscribed ✕ (AutoCAD's node, not a + crosshair)
      ctx.arc(s.x, s.y, r, 0, Math.PI * 2);
      const d = r * Math.SQRT1_2;
      ctx.moveTo(s.x - d, s.y - d); ctx.lineTo(s.x + d, s.y + d);
      ctx.moveTo(s.x + d, s.y - d); ctx.lineTo(s.x - d, s.y + d);
      break;
    }
    case 'intersection': // ✕
      ctx.moveTo(s.x - r, s.y - r); ctx.lineTo(s.x + r, s.y + r);
      ctx.moveTo(s.x + r, s.y - r); ctx.lineTo(s.x - r, s.y + r);
      break;
    case 'quadrant': // ◇ — AutoCAD's mark for a curve's own axis tips
      ctx.moveTo(s.x, s.y - r); ctx.lineTo(s.x + r, s.y);
      ctx.lineTo(s.x, s.y + r); ctx.lineTo(s.x - r, s.y); ctx.closePath();
      break;
    case 'perpendicular': // ⊾ — the right angle, with the square tick in its corner
      ctx.moveTo(s.x - r, s.y - r); ctx.lineTo(s.x - r, s.y + r); ctx.lineTo(s.x + r, s.y + r);
      ctx.moveTo(s.x - r, s.y + 1); ctx.lineTo(s.x - 1, s.y + 1); ctx.lineTo(s.x - 1, s.y + r);
      break;
    case 'nearest': // hourglass ⧖
      ctx.moveTo(s.x - r, s.y - r); ctx.lineTo(s.x + r, s.y - r); ctx.lineTo(s.x - r, s.y + r); ctx.lineTo(s.x + r, s.y + r); ctx.closePath();
      break;
    default: // endpoint — a plain square
      ctx.rect(s.x - r, s.y - r, r * 2, r * 2);
  }
  // Same path, twice: the dark pass is the backing, the bright one the marker.
  ctx.strokeStyle = 'rgba(18,20,24,0.8)';
  ctx.lineWidth = 4;
  ctx.stroke();
  ctx.strokeStyle = SNAP_MARKER_COLOR;
  ctx.lineWidth = 1.8;
  ctx.stroke();

  // The AutoSnap tooltip: what makes the glyph readable without knowing it.
  const label = tooltip ? SNAP_KIND_LABEL[kind ?? 'endpoint'] : undefined;
  if (label) {
    const text = tr(label);
    ctx.font = '11px system-ui, sans-serif';
    const bw = Math.ceil(ctx.measureText(text).width) + 12, bh = 17;
    // Down-right of the marker like AutoCAD, flipped at the viewport's edge.
    const bx = cssW && s.x + r + 6 + bw > cssW - 4 ? s.x - r - 6 - bw : s.x + r + 6;
    const by = cssH && s.y + r + 4 + bh > cssH - 4 ? s.y - r - 4 - bh : s.y + r + 4;
    ctx.fillStyle = 'rgba(18,20,24,0.9)';
    ctx.fillRect(bx, by, bw, bh);
    ctx.strokeStyle = hexToRgba(SNAP_MARKER_COLOR, 0.5);
    ctx.lineWidth = 1;
    ctx.strokeRect(bx + 0.5, by + 0.5, bw - 1, bh - 1);
    ctx.fillStyle = SNAP_MARKER_COLOR;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, bx + 6, by + bh / 2);
  }
  ctx.restore();
}

function hexToRgba(hex: string, alpha: number): string {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!m) return `rgba(120,160,200,${alpha})`;
  const r = parseInt(m[1], 16);
  const g = parseInt(m[2], 16);
  const b = parseInt(m[3], 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

/** Whether a hex colour is near black (its brightest channel under 64). */
function darkHex(hex: string): boolean {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return !!m && Math.max(parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)) < 64;
}

/** Lighten a hex colour towards white by `amount` (0..1). */
function lighten(hex: string, amount: number): string {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!m) return hex;
  const f = (i: number) => {
    const c = parseInt(m[i], 16);
    return Math.round(c + (255 - c) * Math.max(0, Math.min(1, amount)));
  };
  return `rgb(${f(1)},${f(2)},${f(3)})`;
}

/** Darken a hex colour towards black by `amount` (0..1). */
function darken(hex: string, amount: number): string {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!m) return hex;
  const f = (i: number) => {
    const c = parseInt(m[i], 16);
    return Math.round(c * (1 - Math.max(0, Math.min(1, amount))));
  };
  return `rgb(${f(1)},${f(2)},${f(3)})`;
}

/** A room's sculpted benches in the plan: each filled in its colour, outlined,
 *  its seats' and steps' edges drawn thin; the selected one outlined and its
 *  active cage drawn over it (its columns, the plan's grips), its other cages
 *  as faint boxes. */
function drawSculptedBenches(ctx: CanvasRenderingContext2D, inst: ProductInstance, toS: (p: Vec2) => Vec2, selected: ReadonlySet<string>): void {
  const path = (pts: readonly Vec2[], close: boolean): void => {
    ctx.beginPath();
    pts.map(toS).forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
    if (close) ctx.closePath();
  };
  for (const sb of sculptedBenchesOf(inst)) {
    const lay = sculptLayout(inst, sb);
    if (!lay) continue;
    const col = sculptColor(inst, sb), sel = selected.has(sb.id);
    path(lay.outline, true);
    ctx.fillStyle = col;
    ctx.fill();
    ctx.lineWidth = 1;
    ctx.strokeStyle = darken(col, 0.5);
    for (const line of lay.seatLines) { path(line, false); ctx.stroke(); }
    path(lay.outline, true);
    ctx.lineWidth = sel ? 2.5 : 1;
    ctx.strokeStyle = sel ? '#ffd166' : darken(col, 0.5);
    ctx.stroke();
    // Its trestles, under it: outlined dashed (sculptTrestles.ts).
    const trestles = sculptTrestles(inst, sb);
    if (trestles.length) {
      ctx.save();
      ctx.setLineDash([5, 3]);
      ctx.lineWidth = 1;
      ctx.strokeStyle = darken(col, TRESTLE_LINE_SHADE);
      for (const t of trestles) { path(sculptTrestleFootprint(t), true); ctx.stroke(); }
      ctx.restore();
    }
    const cage = sel ? activeCageOf(sb) : null;
    if (!cage) continue;
    const plan = cagePlan(lay, cage);
    ctx.save();
    ctx.setLineDash([4, 3]);
    ctx.lineWidth = 1;
    ctx.strokeStyle = CAGE_COLOR;
    ctx.globalAlpha = 0.35;
    for (const c of sb.lattices ?? []) {
      if (c === cage) continue;
      for (const line of cageEdges(c)) { path(line.map((f) => framePoint(lay, f)), false); ctx.stroke(); }
    }
    ctx.globalAlpha = 1;
    for (const line of plan.lines) { path(line, false); ctx.stroke(); }
    ctx.restore();
  }
}
